import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Edit2, Sparkles, X, CalendarDays, Eye, Images, Bold, Italic, Heading2, Link as LinkIcon, List } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import WalkoutReveal from "@/components/WalkoutReveal";
import MediaLibrary from "@/components/MediaLibrary";
import Markdown from "@/components/Markdown";
import DriverCard from "@/components/DriverCard";
import { invalidateTiers } from "@/lib/tierConfig";
import { invalidateSplits } from "@/lib/splitsConfig";
import { useSplits } from "@/lib/useSplits";
import { F1_2026_TRACKS } from "@/lib/f1Tracks";
import { COUNTRIES, COUNTRY_NAME_BY_CODE, flagFromCode } from "@/lib/countries";

const TXT = "w-full bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm focus:border-[#00E5FF] outline-none";

export default function AdminPage() {
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="admin-page">
      <div className="h-1 w-12 bg-[#00E5FF] mb-3" />
      <div className="eyebrow text-[#00E5FF]">Control Room</div>
      <h1 className="font-display font-black italic uppercase tracking-normal text-5xl sm:text-6xl leading-none mt-1">Admin</h1>

      <Tabs defaultValue="races" className="mt-10">
        <TabsList className="bg-transparent border-0 p-0 rounded-none flex flex-col items-stretch h-auto gap-3" data-testid="admin-tab-groups">
          {[
            { label: "Inhalte", keys: ["races", "news", "news-categories", "roadmap"] },
            { label: "Liga · Setup", keys: ["splits", "seasons", "season-lock", "teams", "drivers", "stats-update", "card-reveal"] },
            { label: "Renn-Daten", keys: ["calendar", "results", "standings-edit"] },
            { label: "Konfiguration", keys: ["tiers", "footer", "misc", "backup"] },
          ].map(group => (
            <div key={group.label} className="flex flex-wrap items-center gap-2 w-full">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 w-32 shrink-0 text-left">{group.label}</div>
              <div className="flex flex-wrap gap-0 border border-white/10">
                {group.keys.map(t => (
                  <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`}
                    className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black uppercase tracking-widest font-bold rounded-none px-5 py-2 text-xs border-r border-white/10 last:border-r-0">
                    {t === "races" ? "Rennberichte" : t === "news" ? "News" : t === "news-categories" ? "News-Kategorien" : t === "roadmap" ? "Roadmap" : t === "drivers" ? "Fahrer" : t === "teams" ? "Teams" : t === "calendar" ? "Kalender" : t === "seasons" ? "Saisons" : t === "season-lock" ? "Saisons abschließen" : t === "splits" ? "Splits" : t === "tiers" ? "Wertungs-Stufen" : t === "results" ? "Rennergebnisse" : t === "stats-update" ? "Werte-Update" : t === "standings-edit" ? "Wertungs-Override" : t === "footer" ? "Footer" : t === "backup" ? "Backup" : t === "card-reveal" ? "Karten-Reveal" : "Sonstiges"}
                  </TabsTrigger>
                ))}
              </div>
            </div>
          ))}
        </TabsList>

        <TabsContent value="races" className="mt-6"><RaceReportsAdmin /></TabsContent>
        <TabsContent value="news" className="mt-6"><NewsAdmin /></TabsContent>
        <TabsContent value="news-categories" className="mt-6"><NewsCategoriesAdmin /></TabsContent>
        <TabsContent value="roadmap" className="mt-6"><RoadmapAdmin /></TabsContent>
        <TabsContent value="drivers" className="mt-6"><DriversAdmin /></TabsContent>
        <TabsContent value="stats-update" className="mt-6"><StatsUpdaterAdmin /></TabsContent>
        <TabsContent value="card-reveal" className="mt-6"><CardRevealAdminBlock /></TabsContent>
        <TabsContent value="teams" className="mt-6"><TeamsAdmin /></TabsContent>
        <TabsContent value="calendar" className="mt-6"><CalendarAdmin /></TabsContent>
        <TabsContent value="seasons" className="mt-6"><SeasonsAdmin /></TabsContent>
        <TabsContent value="season-lock" className="mt-6"><SeasonLockAdmin /></TabsContent>
        <TabsContent value="splits" className="mt-6"><SplitsAdmin /></TabsContent>
        <TabsContent value="tiers" className="mt-6"><TiersAdmin /></TabsContent>
        <TabsContent value="results" className="mt-6"><RaceResultsAdmin /></TabsContent>
        <TabsContent value="standings-edit" className="mt-6"><StandingsOverrideAdmin /></TabsContent>
        <TabsContent value="misc" className="mt-6"><SettingsAdmin /></TabsContent>
        <TabsContent value="footer" className="mt-6"><FooterAdmin /></TabsContent>
        <TabsContent value="backup" className="mt-6"><BackupAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============= TEAMS ============= */
const emptyTeam = { name: "", short_name: "", color: "#00E5FF", badge_url: "", points: 0, is_active: true };
function TeamsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyTeam);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/teams").then(x => setItems(x.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await api.put(`/teams/${editingId}`, form); toast.success("Team aktualisiert"); }
      else { await api.post("/teams", form); toast.success("Team erstellt"); }
      setForm(emptyTeam); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => { if (!confirm("Wirklich löschen?")) return; await api.delete(`/teams/${id}`); toast.success("Gelöscht"); load(); };
  const toggleActive = async (t) => {
    try {
      await api.put(`/teams/${t.id}`, { ...t, is_active: !(t.is_active !== false) });
      toast.success((t.is_active !== false) ? "Team auf inaktiv gesetzt" : "Team aktiviert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <form onSubmit={save} className="card-surface p-5 md:col-span-1" data-testid="team-form">
        <div className="font-display font-black italic uppercase text-lg">{editingId ? "Team bearbeiten" : "Neues Team"}</div>
        <Field label="Name"><input className={TXT} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="team-name" /></Field>
        <Field label="Kürzel"><input className={TXT} required value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})} data-testid="team-short" /></Field>
        <Field label="Farbe">
          <div className="flex items-center gap-2">
            <input type="color" value={form.color || "#00E5FF"} onChange={e => setForm({...form, color: e.target.value})}
              className="w-12 h-10 bg-transparent border border-white/10 cursor-pointer p-0" data-testid="team-color-picker" />
            <input className={TXT + " flex-1"} value={form.color} onChange={e => setForm({...form, color: e.target.value})} data-testid="team-color" />
          </div>
        </Field>
        <Field label="Badge / Logo">
          <ImageUploader
            value={form.badge_url || ""}
            onChange={(v) => setForm({...form, badge_url: v})}
            testIdPrefix="team-badge"
            targetW={256} targetH={256} cropFrom="center"
            folder="teams"
            publicId={editingId || undefined}
            hint={editingId ? `Wird als teams/${editingId} gespeichert (überschreibt vorhandenes)` : "Erst Team speichern, danach Bild hochladen"}
          />
        </Field>
        <Field label="Punkte (manuell)"><input type="number" className={TXT} value={form.points} onChange={e => setForm({...form, points: parseInt(e.target.value)||0})} /></Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="team-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Erstellen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptyTeam); }} className="btn-ghost text-xs"><X className="w-4 h-4 inline" /> Abbrechen</button>}
        </div>
      </form>
      <div className="md:col-span-2 space-y-2">
        {items.map((t) => {
          const active = t.is_active !== false;
          return (
          <div key={t.id} className={`border border-white/10 p-4 flex items-center gap-4 ${active ? "" : "opacity-60"}`}>
            <div className="w-2 h-10" style={{ background: t.color }} />
            {t.badge_url ? (
              <img src={t.badge_url} alt={t.name} className="w-10 h-10 object-contain border border-white/10 bg-[#0B0F19]" />
            ) : (
              <div className="w-10 h-10 flex items-center justify-center font-display font-black italic text-xs" style={{ background: t.color, color: "#000" }}>{t.short_name}</div>
            )}
            <div className="flex-1">
              <div className="font-bold flex items-center gap-2">
                {t.name} <span className="text-neutral-500 text-xs">{t.short_name}</span>
                {!active && <span className="text-[10px] px-1.5 py-0.5 bg-neutral-700 text-neutral-200 font-bold tracking-widest uppercase">Inaktiv</span>}
              </div>
              <div className="text-xs text-neutral-500">{t.color} · {t.points} Pkt.</div>
              <div className="text-[10px] font-mono text-neutral-600 mt-0.5">
                <span title="Klick zum Kopieren · Verwende diese ID als Dateinamen" className="cursor-pointer hover:text-cyan-300" onClick={() => { navigator.clipboard?.writeText(t.id); toast.success("ID kopiert"); }}>
                  teams/{t.id}
                </span>
              </div>
            </div>
            <button onClick={() => toggleActive(t)}
              className={`btn-ghost text-[10px] uppercase tracking-widest font-bold px-2 ${active ? "text-emerald-300" : "text-neutral-400"}`}
              data-testid={`team-toggle-active-${t.id}`}
              title={active ? "Team auf inaktiv setzen (verschwindet aus aktuellen Splits, bleibt in Ewiger Tabelle)" : "Team wieder aktivieren"}>
              {active ? "Aktiv" : "Inaktiv"}
            </button>
            <button onClick={() => { setEditingId(t.id); setForm({ ...emptyTeam, ...t }); }} className="btn-ghost text-xs" data-testid={`team-edit-${t.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
            <button onClick={() => del(t.id)} className="btn-ghost text-xs text-red-400" data-testid={`team-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============= DRIVERS ============= */
const emptyDriver = {
  name: "", team_id: "", split: "champions", number: 0, country: "Deutschland", country_code: "DE",
  photo_url: "", ovr: 80, bio: "", ingame_name: "", ingame_aliases: [], is_rookie: false, is_champion: false, champion_season: null, champion_season_id: null, is_reserve_driver: false,
  stats: { pac: 80, rac: 80, dfn: 80, agg: 80, exp: 80, pot: 80 },
  points: 0, wins: 0, podiums: 0, poles: 0, fastest_laps: 0, dnfs: 0,
};
function DriversAdmin() {
  const splits = useSplits();
  const splitMeta = (k) => splits.find(s => s.key === k) || splits[0] || { key: "champions", name: "Champions", color: "#00E5FF" };
  const [items, setItems] = useState([]); const [teams, setTeams] = useState([]);
  const [form, setForm] = useState(emptyDriver); const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkSplit, setBulkSplit] = useState(splits[0]?.key || "champions");
  const [filterSplit, setFilterSplit] = useState("all"); // 'all' | split.key | 'reserve'
  const [seasons, setSeasons] = useState([]);
  const load = () => Promise.all([api.get("/drivers"), api.get("/teams"), api.get("/seasons")]).then(([d, t, s]) => {
    setItems(d.data); setTeams(t.data); setSeasons(s.data || []);
  });
  useEffect(() => { load(); }, []);

  const currentYearForSplit = (split) => {
    const cur = seasons.find(x => x.is_current && (x.split || "champions") === split);
    if (cur?.year) return cur.year;
    const latest = [...seasons].filter(x => (x.split || "champions") === split).sort((a,b) => (b.year || 0) - (a.year || 0))[0];
    return latest?.year || new Date().getFullYear();
  };
  const currentYear = currentYearForSplit(form.split || "champions");

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        number: parseInt(form.number)||0,
        ovr: parseInt(form.ovr)||80,
        champion_season: form.is_champion ? (String(form.champion_season ?? "").trim() || "S ") : null,
      };
      if (editingId) { await api.put(`/drivers/${editingId}`, payload); toast.success("Fahrer aktualisiert"); }
      else { await api.post("/drivers", payload); toast.success("Fahrer erstellt"); }
      setForm(emptyDriver); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => { if (!confirm("Wirklich löschen?")) return; await api.delete(`/drivers/${id}`); toast.success("Gelöscht"); load(); };

  const toggleActive = async (d) => {
    const nextActive = d.is_active === false ? true : false;
    if (!nextActive && !confirm(`Fahrer „${d.name}" deaktivieren?\n\nEr wird in offenen Saisons und in der Fahrerwertung ausgeblendet — bleibt aber in der Ewigen Tabelle mit dem Label „Inaktiv" sichtbar.\nDas aktuelle Team wird entfernt.`)) return;
    try {
      const payload = { ...d, is_active: nextActive };
      if (!nextActive) payload.team_id = null;
      await api.put(`/drivers/${d.id}`, payload);
      toast.success(nextActive ? "Fahrer aktiviert" : "Fahrer deaktiviert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(d => d.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const applyBulkSplit = async () => {
    if (selected.size === 0) { toast.error("Bitte mindestens einen Fahrer auswählen."); return; }
    try {
      const { data } = await api.post("/drivers/bulk-split", { driver_ids: Array.from(selected), split: bulkSplit });
      const label = splitMeta(bulkSplit).name;
      if (data.updated > 0) toast.success(`${data.updated} Fahrer nach ${label} verschoben`);
      else if (data.matched > 0) toast.info(`${data.matched} Fahrer waren bereits im Split ${label}`);
      else toast.error("Keine Fahrer gefunden");
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={save} className="card-surface p-5 lg:col-span-2" data-testid="driver-form">
        <div className="font-display font-black italic uppercase text-lg mb-2">{editingId ? "Fahrer bearbeiten" : "Neuer Fahrer"}</div>
        <Field label="Name"><input className={TXT} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="driver-name" /></Field>
        <Field label="Ingame-Name (für CSV-Import)"><input className={TXT} placeholder="z. B. SoeMaster99" value={form.ingame_name || ""} onChange={e => setForm({...form, ingame_name: e.target.value})} data-testid="driver-ingame-name" /></Field>
        <Field label="Weitere Ingame-Aliases (kommagetrennt · für Auto-Match bei Gamertag-Varianten)">
          <input
            className={TXT}
            placeholder="z. B. SoeMaster, SoeMaster_99, F1SoeMaster"
            value={Array.isArray(form.ingame_aliases) ? form.ingame_aliases.join(", ") : ""}
            onChange={e => setForm({
              ...form,
              ingame_aliases: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
            })}
            data-testid="driver-ingame-aliases"
          />
        </Field>
        <label className={`flex items-center gap-2 border px-3 py-2 mb-3 cursor-pointer text-xs uppercase tracking-widest ${form.is_reserve_driver ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : "border-white/10 text-neutral-300"}`}>
          <input type="checkbox" checked={!!form.is_reserve_driver} onChange={e => setForm({...form, is_reserve_driver: e.target.checked, is_rookie: e.target.checked ? false : form.is_rookie, is_champion: e.target.checked ? false : form.is_champion, split: e.target.checked ? "" : (form.split || "champions")})} data-testid="driver-is-reserve" />
          Ersatzfahrer · in beiden Splits einsetzbar · keine Karte
        </label>
        {!form.is_reserve_driver && (
        <>
        <label className={`flex items-center gap-2 border px-3 py-2 mb-3 cursor-pointer text-xs uppercase tracking-widest ${form.is_rookie ? "border-[#00E5FF]/60 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-neutral-300"}`}>
          <input type="checkbox" checked={!!form.is_rookie} onChange={e => setForm({...form, is_rookie: e.target.checked, is_champion: e.target.checked ? false : form.is_champion})} data-testid="driver-rookie" />
          Neuer Fahrer · noch keine Wertung (Karte zeigt „??“)
        </label>
        <label className={`flex items-center gap-2 border px-3 py-2 mb-3 cursor-pointer text-xs uppercase tracking-widest ${form.is_champion ? "border-[#A855F7]/60 bg-[#A855F7]/10 text-[#E9D5FF]" : "border-white/10 text-neutral-300"} ${form.is_rookie ? "opacity-50 pointer-events-none" : ""}`}>
          <input type="checkbox" disabled={!!form.is_rookie} checked={!!form.is_champion} onChange={e => setForm({...form, is_champion: e.target.checked, champion_season: e.target.checked ? (form.champion_season || "S ") : null})} data-testid="driver-champion" />
          Champion · Special-Edition Karte (Krone-Badge)
        </label>
        {form.is_champion && (
          <div className="grid grid-cols-2 gap-2">
            <Field label='Saison-Badge Text (z. B. "S 4")'>
              <input
                type="text"
                className={TXT}
                placeholder="S 4"
                value={form.champion_season ?? ""}
                onChange={e => setForm({...form, champion_season: e.target.value})}
                data-testid="driver-champion-season"
              />
            </Field>
            <Field label="Champion-Saison (für Stats-Auslesen)">
              <select
                className={TXT}
                value={form.champion_season_id || ""}
                onChange={e => {
                  const sid = e.target.value || null;
                  const s = seasons.find(x => x.id === sid);
                  let badge = form.champion_season;
                  if (s) {
                    const m = (s.name || "").match(/(\d+)/);
                    badge = m ? `S ${m[1]}` : s.name;
                  }
                  setForm({...form, champion_season_id: sid, champion_season: sid ? badge : form.champion_season});
                }}
                data-testid="driver-champion-season-id"
              >
                <option value="">— keine —</option>
                {[...seasons]
                  .filter(s => (s.split || "champions") === (form.split || "champions"))
                  .sort((a, b) => (b.year || 0) - (a.year || 0))
                  .map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.year ? ` · ${s.year}` : ""}{s.game ? ` · ${s.game}` : ""}{s.closed ? " · geschlossen" : ""}
                    </option>
                  ))}
              </select>
            </Field>
          </div>
        )}
        <Field label="Split">
          <select className={TXT} value={form.split || splits[0]?.key || "champions"} onChange={e => setForm({...form, split: e.target.value})} data-testid="driver-split">
            {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </Field>
        </>
        )}
        <Field label="Team">
          <select className={TXT} value={form.team_id || ""} onChange={e => setForm({...form, team_id: e.target.value})} data-testid="driver-team">
            <option value="">— kein Team —</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Nummer"><input type="number" className={TXT} value={form.number} onChange={e => setForm({...form, number: e.target.value})} /></Field>
          <Field label="Land">
            <select className={TXT} value={form.country_code} onChange={e => {
              const code = e.target.value;
              setForm({...form, country_code: code, country: COUNTRY_NAME_BY_CODE[code] || form.country});
            }} data-testid="driver-country">
              <option value="">— wählen —</option>
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{flagFromCode(c.code)} {c.name} ({c.code})</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Foto / Portrait">
          <ImageUploader
            value={form.photo_url || ""}
            onChange={(v) => setForm({...form, photo_url: v})}
            testIdPrefix="driver-photo"
            targetW={480} targetH={600} cropFrom="top"
            folder="drivers"
            publicId={editingId || undefined}
            hint={editingId ? `Wird als drivers/${editingId} gespeichert (überschreibt vorhandenes)` : "Erst Fahrer speichern, danach Bild hochladen"}
            previewSize="w-16 h-20"
          />
        </Field>
        {!form.is_reserve_driver && (
        <Field label="OVR"><input type="number" className={TXT} value={form.ovr} onChange={e => setForm({...form, ovr: e.target.value})} /></Field>
        )}
        {!form.is_reserve_driver && (
        <div className="grid grid-cols-4 gap-2 mt-2">
          {[["pac","PAC"],["kon","KON"],["rac","RAC"],["cle","CLE"],["qua","QUA"],["erg","ERG"],["zuv","ZUV"]].map(([k, label]) => (
            <Field key={k} label={label}>
              <input type="number" className={TXT} value={form.stats[k] ?? 80}
                onChange={e => setForm({...form, stats: {...form.stats, [k]: parseInt(e.target.value)||0}})}
                data-testid={`driver-stat-${k}`} />
            </Field>
          ))}
        </div>
        )}

        {!form.is_reserve_driver && (
        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="eyebrow">Karriere-Statistik (Karten-Rückseite)</div>
          <div className="text-[10px] text-neutral-500 mt-1">
            Leer lassen = automatisch aus den Rennergebnissen berechnen. Eintragen = manueller Override.
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            <Field label="Rennen"><input type="number" className={TXT} value={form.races || ""} placeholder={`auto: ${form.career_auto?.races ?? 0}`} onChange={e => setForm({...form, races: parseInt(e.target.value)||0})} data-testid="driver-races" /></Field>
            <Field label="Podien"><input type="number" className={TXT} value={form.podiums || ""} placeholder={`auto: ${form.career_auto?.podiums ?? 0}`} onChange={e => setForm({...form, podiums: parseInt(e.target.value)||0})} /></Field>
            <Field label="Siege"><input type="number" className={TXT} value={form.wins || ""} placeholder={`auto: ${form.career_auto?.wins ?? 0}`} onChange={e => setForm({...form, wins: parseInt(e.target.value)||0})} /></Field>
            <Field label="Titel"><input type="number" className={TXT} value={form.titles || ""} placeholder={`auto: ${form.career_auto?.titles ?? 0}`} onChange={e => setForm({...form, titles: parseInt(e.target.value)||0})} data-testid="driver-titles" /></Field>
            <Field label="Punkte"><input type="number" className={TXT} value={form.points || ""} placeholder={`auto: ${form.career_auto?.points ?? 0}`} onChange={e => setForm({...form, points: parseInt(e.target.value)||0})} /></Field>
            <Field label="Poles"><input type="number" className={TXT} value={form.poles || ""} placeholder={`auto: ${form.career_auto?.poles ?? 0}`} onChange={e => setForm({...form, poles: parseInt(e.target.value)||0})} /></Field>
            <Field label="DNFs"><input type="number" className={TXT} value={form.dnfs || ""} placeholder={`auto: ${form.career_auto?.dnfs ?? 0}`} onChange={e => setForm({...form, dnfs: parseInt(e.target.value)||0})} /></Field>
            <Field label="Strafpkt."><input type="number" className={TXT} value={form.penalty_points || ""} placeholder="0" onChange={e => setForm({...form, penalty_points: parseInt(e.target.value)||0})} data-testid="driver-penalty" /></Field>
          </div>
          <Field label="Marktwert (€)">
            <input type="number" className={TXT} value={form.market_value} onChange={e => setForm({...form, market_value: parseInt(e.target.value)||0})} placeholder="z. B. 1500000" data-testid="driver-market-value" />
          </Field>
        </div>
        )}

        {!form.is_reserve_driver && (
        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="eyebrow">Vergangene Teams</div>
          <div className="grid grid-cols-2 gap-2 mt-2" data-testid="past-teams-grid">
            {teams.filter(t => t.id !== form.team_id).map(t => {
              const checked = (form.past_team_ids || []).includes(t.id);
              return (
                <label key={t.id} className={`flex items-center gap-2 border px-2 py-1.5 text-xs cursor-pointer ${checked ? "border-[#00E5FF]/50 bg-[#00E5FF]/5" : "border-white/10"}`}>
                  <input type="checkbox" checked={checked}
                    onChange={() => {
                      const cur = new Set(form.past_team_ids || []);
                      if (cur.has(t.id)) cur.delete(t.id); else cur.add(t.id);
                      setForm({ ...form, past_team_ids: Array.from(cur) });
                    }}
                    data-testid={`past-team-${t.id}`} />
                  <span className="inline-block w-2 h-4" style={{ background: t.color }} />
                  <span>{t.short_name}</span>
                </label>
              );
            })}
          </div>
        </div>
        )}

        <Field label="Bio (nur intern, nicht auf der Karte)"><textarea className={TXT} rows={2} value={form.bio || ""} onChange={e => setForm({...form, bio: e.target.value})} /></Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="driver-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Erstellen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptyDriver); }} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
        {editingId && <SeasonStatsEditor driverId={editingId} defaultSplit={form.split || "champions"} />}
      </form>
      <div className="lg:col-span-3 space-y-2">
        <div className="flex flex-wrap gap-1.5" data-testid="driver-filter-bar">
          {[{ k: "all", label: "Alle", color: "#FFFFFF" }, ...splits.map(s => ({ k: s.key, label: s.name, color: s.color })), { k: "reserve", label: "Ersatzfahrer", color: "#F59E0B" }].map(({ k, label, color }) => {
            const count = k === "all"
              ? items.length
              : k === "reserve"
              ? items.filter(d => d.is_reserve_driver).length
              : items.filter(d => !d.is_reserve_driver && (d.split || "champions") === k).length;
            const active = filterSplit === k;
            return (
              <button key={k} onClick={() => setFilterSplit(k)}
                className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 border transition-colors ${active ? "" : "border-white/10 text-neutral-300 hover:border-white/30"}`}
                style={active ? { borderColor: color, color, background: `${color}1A` } : undefined}
                data-testid={`driver-filter-${k}`}>
                {label} <span className="text-[10px] opacity-70 ml-1">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2 border border-white/10 bg-white/[0.02] px-3 py-2" data-testid="bulk-bar">
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-300">
            <input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} data-testid="bulk-toggle-all" />
            {selected.size > 0 ? `${selected.size} ausgewählt` : "Alle"}
          </label>
          <div className="flex-1" />
          <span className="text-xs uppercase tracking-widest text-neutral-400">Split zuweisen:</span>
          <select className="bg-[#0B0F19] border border-white/10 px-2 py-1 text-xs" value={bulkSplit} onChange={e => setBulkSplit(e.target.value)} data-testid="bulk-split-select">
            {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <button onClick={applyBulkSplit} disabled={selected.size === 0} className="btn-primary chamfer-sm text-xs disabled:opacity-40" data-testid="bulk-apply">
            Anwenden
          </button>
        </div>
        {items
          .filter(d => filterSplit === "all"
            ? true
            : filterSplit === "reserve"
            ? !!d.is_reserve_driver
            : !d.is_reserve_driver && (d.split || "champions") === filterSplit)
          .map(d => {
          const team = teams.find(t => t.id === d.team_id);
          const checked = selected.has(d.id);
          return (
            <div key={d.id} className={`border p-4 flex items-center gap-4 ${checked ? "border-[#00E5FF]/50 bg-[#00E5FF]/5" : d.is_active === false ? "border-white/5 bg-white/[0.02] opacity-60" : d.is_reserve_driver ? "border-amber-500/20 bg-amber-500/[0.03]" : "border-white/10"}`}>
              <input type="checkbox" checked={checked} onChange={() => toggleOne(d.id)} data-testid={`driver-select-${d.id}`} />
              <div className="w-12 h-12 bg-[#0B0F19] flex items-center justify-center font-display font-black italic text-[#00E5FF] text-xl">{d.is_reserve_driver ? "—" : d.ovr}</div>
              <div className="flex-1">
                <div className="font-bold">
                  {d.name}
                  <span className="text-xs text-neutral-500 ml-2">#{d.number}</span>
                  {d.is_reserve_driver ? (
                    <span className="text-[10px] uppercase tracking-widest ml-2 px-1.5 py-0.5 border border-amber-500/40 text-amber-400">Ersatzfahrer</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest ml-2" style={{ color: splitMeta(d.split).color }}>{splitMeta(d.split).name}</span>
                  )}
                  {d.is_active === false && (
                    <span className="text-[10px] uppercase tracking-widest ml-2 px-1.5 py-0.5 border border-neutral-500/40 text-neutral-400" data-testid={`driver-inactive-badge-${d.id}`}>Inaktiv</span>
                  )}
                </div>
                <div className="text-xs text-neutral-500" style={{ color: d.is_active === false ? undefined : team?.color }}>{d.is_active === false ? "— kein aktives Team" : (team?.name || "—")}</div>
                <div className="text-[10px] font-mono text-neutral-600 mt-0.5 flex items-center gap-1">
                  <span title="Klick zum Kopieren · Verwende diese ID als Dateinamen für Bulk-Uploads" className="cursor-pointer hover:text-cyan-300" onClick={() => { navigator.clipboard?.writeText(d.id); toast.success("ID kopiert"); }}>
                    drivers/{d.id}
                  </span>
                </div>
              </div>
              <button
                onClick={() => toggleActive(d)}
                title={d.is_active === false ? "Fahrer aktivieren" : "Fahrer deaktivieren (kein Team, nur in Ewiger Tabelle)"}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${d.is_active === false ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}
                data-testid={`driver-toggle-active-${d.id}`}
              >
                {d.is_active === false ? "Aktivieren" : "Deaktivieren"}
              </button>
              <button onClick={() => { setEditingId(d.id); setForm({ ...emptyDriver, ...d, stats: { ...emptyDriver.stats, ...(d.stats || {}) }, past_team_ids: d.past_team_ids || [], ingame_aliases: Array.isArray(d.ingame_aliases) ? d.ingame_aliases : [] }); }} className="btn-ghost text-xs" data-testid={`driver-edit-${d.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(d.id)} className="btn-ghost text-xs text-red-400" data-testid={`driver-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============= RACE REPORTS ============= */
const emptyRace = { title: "", race_name: "", track: "", date: "", weather: "Trocken", hero_image: "", summary: "", body: "", author: "Redaktion", published: false, split: null, season_id: null, calendar_event_id: null };
function RaceReportsAdmin() {
  const [items, setItems] = useState([]);
  const [splits, setSplits] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [form, setForm] = useState(emptyRace);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([
    api.get("/race-reports"),
    api.get("/splits"),
    api.get("/seasons"),
  ]).then(([r, sp, se]) => { setItems(r.data || []); setSplits(sp.data || []); setSeasons(se.data || []); });
  useEffect(() => { load(); }, []);

  // Default-Banner für neu angelegte / vorbefüllte Berichte
  const [defaultHero, setDefaultHero] = useState("");
  const [savedHero, setSavedHero] = useState("");
  const [savingHero, setSavingHero] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setDefaultHero(data?.default_race_hero || "");
      setSavedHero(data?.default_race_hero || "");
    }).catch(() => {});
  }, []);
  const heroChanged = (defaultHero || "") !== (savedHero || "");
  const saveDefaultHero = async () => {
    setSavingHero(true);
    try {
      const { data: cur } = await api.get("/settings");
      const { data: res } = await api.put("/settings", {
        watch_live_url: cur?.watch_live_url || "",
        delta_big_threshold: parseInt(cur?.delta_big_threshold ?? 5) || 5,
        default_race_hero: defaultHero || "",
      });
      setSavedHero(defaultHero || "");
      const updated = res?.race_reports_updated || 0;
      const baseMsg = defaultHero ? "Banner gespeichert" : "Banner zurückgesetzt";
      toast.success(updated > 0 ? `${baseMsg} · ${updated} bestehende${updated === 1 ? "r Bericht" : " Berichte"} aktualisiert` : baseMsg);
      if (updated > 0) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSavingHero(false); }
  };

  const splitColor = (key) => splits.find(s => s.key === key)?.color || "#00E5FF";
  const splitName = (key) => splits.find(s => s.key === key)?.name || key;
  const seasonLabel = (sid) => {
    const s = seasons.find(x => x.id === sid);
    return s ? `${s.name}${s.year ? ` · ${s.year}` : ""}` : "—";
  };

  // Filter
  const [filterSplit, setFilterSplit] = useState("all");
  const [filterSeason, setFilterSeason] = useState("all");
  const seasonOptions = useMemo(() => {
    const relevant = filterSplit === "all" ? items : items.filter(r => r.split === filterSplit);
    const ids = new Set(relevant.map(r => r.season_id).filter(Boolean));
    return seasons
      .filter(s => ids.has(s.id))
      .filter(s => filterSplit === "all" || (s.split || "champions") === filterSplit)
      .sort((a, b) => (b.year || 0) - (a.year || 0));
  }, [items, seasons, filterSplit]);
  useEffect(() => {
    if (filterSeason !== "all" && !seasonOptions.some(s => s.id === filterSeason)) {
      setFilterSeason("all");
    }
  }, [seasonOptions, filterSeason]);
  const visible = items.filter(r =>
    (filterSplit === "all" || r.split === filterSplit) &&
    (filterSeason === "all" || r.season_id === filterSeason)
  );

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await api.put(`/race-reports/${editingId}`, form); toast.success("Bericht aktualisiert"); }
      else { await api.post("/race-reports", form); toast.success("Bericht erstellt"); }
      setForm(emptyRace); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => { if (!confirm("Wirklich löschen?")) return; await api.delete(`/race-reports/${id}`); toast.success("Gelöscht"); load(); };

  return (
    <div>
      <div className="card-surface p-4 mb-4" data-testid="race-default-banner-card">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="font-display font-black italic uppercase text-sm">Rennberichts-Banner (Default)</div>
            <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed">
              Dieses Bild wird automatisch als Hero verwendet, wenn ein neuer Rennbericht angelegt oder per
              &bdquo;Aus Kalender vorbefüllen&ldquo; erzeugt wird. <strong className="text-neutral-300">Beim Speichern</strong> wird der Banner zusätzlich auf alle
              bestehenden Berichte angewendet, die <em>noch das alte Default-Banner</em> oder kein Hero-Bild haben.
              Individuell hochgeladene Hero-Bilder bleiben unverändert.
            </p>
          </div>
          <div className="w-full sm:w-[280px]">
            <ImageUploader
              value={defaultHero}
              onChange={(url) => setDefaultHero(url)}
              folder="race_reports"
              targetW={1600}
              targetH={900}
              cropFrom="center"
              hint="Empfohlen: 16:9 · wird auf 1600×900 zugeschnitten"
              previewSize="w-40 h-[90px]"
              testIdPrefix="race-default-banner"
            />
          </div>
          <div className="flex flex-col gap-2 self-center">
            <button
              type="button"
              onClick={saveDefaultHero}
              disabled={savingHero}
              className="btn-primary chamfer-sm text-xs disabled:opacity-40"
              data-testid="race-default-banner-save"
            >{savingHero ? "…" : heroChanged ? "Speichern" : "Speichern & anwenden"}</button>
            {defaultHero && (
              <button
                type="button"
                onClick={() => setDefaultHero("")}
                className="text-[10px] uppercase tracking-widest px-2 py-1 border border-white/10 text-neutral-400 hover:bg-white/5"
                data-testid="race-default-banner-clear"
              >Auf Standard zurücksetzen</button>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-neutral-500">
          {items.length} Bericht{items.length === 1 ? "" : "e"} · davon {items.filter(r => !r.published).length} Entwurf{items.filter(r => !r.published).length === 1 ? "" : "e"}
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            if (!window.confirm("Für jedes Kalender-Event einen unveröffentlichten Draft-Rennbericht anlegen?\n\nBestehende Berichte bleiben unverändert.")) return;
            setBusy(true);
            try {
              const { data } = await api.post("/race-reports/prefill-from-calendar");
              toast.success(`${data.created} neue Drafts erstellt · ${data.skipped} bereits vorhanden`);
              load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Fehler");
            } finally { setBusy(false); }
          }}
          className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-40"
          data-testid="race-prefill-calendar"
        >{busy ? "…" : "Aus Kalender vorbefüllen"}</button>
      </div>
      <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={save} className="card-surface p-5 lg:col-span-3" data-testid="race-form">
        <div className="font-display font-black italic uppercase text-lg mb-2">{editingId ? "Bericht bearbeiten" : "Neuer Rennbericht"}</div>
        <Field label="Titel"><input className={TXT} required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="race-title" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Renn-Name"><input className={TXT} required value={form.race_name} onChange={e => setForm({...form, race_name: e.target.value})} /></Field>
          <Field label="Strecke"><input className={TXT} required value={form.track} onChange={e => setForm({...form, track: e.target.value})} /></Field>
          <Field label="Datum"><input type="date" className={TXT} required value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Wetter"><input className={TXT} value={form.weather} onChange={e => setForm({...form, weather: e.target.value})} /></Field>
        </div>
        <Field label="Hero Bild URL"><input className={TXT} value={form.hero_image} onChange={e => setForm({...form, hero_image: e.target.value})} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Split">
            <select className={TXT} value={form.split || ""} onChange={e => setForm({...form, split: e.target.value || null})} data-testid="race-split">
              <option value="">— keiner —</option>
              {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Saison">
            <select className={TXT} value={form.season_id || ""} onChange={e => setForm({...form, season_id: e.target.value || null})} data-testid="race-season">
              <option value="">— keine —</option>
              {[...seasons]
                .filter(s => !form.split || (s.split || "champions") === form.split)
                .sort((a, b) => (b.year || 0) - (a.year || 0))
                .map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.year ? ` · ${s.year}` : ""}{s.game ? ` · ${s.game}` : ""}
                  </option>
                ))}
            </select>
          </Field>
        </div>
        <Field label="Zusammenfassung"><textarea className={TXT} rows={2} required value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} /></Field>
        <MarkdownField
          label="Inhalt (Markdown)"
          value={form.body}
          onChange={(v) => setForm({...form, body: v})}
          rows={10}
          required
          testId="race-body"
          placeholder={"# Erste Runden\n\nDas Rennen begann mit einem **dramatischen Start** ...\n\n## Boxenstopps\n\n- Runde 18: Top-3 wechseln auf Hard\n- Runde 35: VSC nutzt der Gegner aus"}
        />
        <Field label="Autor"><input className={TXT} value={form.author} onChange={e => setForm({...form, author: e.target.value})} /></Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="race-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Veröffentlichen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptyRace); }} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
      </form>
      <div className="lg:col-span-2 space-y-2">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">Filter:</span>
          <select
            className="bg-[#0B0F19] border border-white/10 px-2 py-1 text-xs"
            value={filterSplit}
            onChange={e => setFilterSplit(e.target.value)}
            data-testid="race-filter-split"
          >
            <option value="all">Alle Splits</option>
            {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <select
            className="bg-[#0B0F19] border border-white/10 px-2 py-1 text-xs"
            value={filterSeason}
            onChange={e => setFilterSeason(e.target.value)}
            data-testid="race-filter-season"
          >
            <option value="all">Alle Saisons</option>
            {seasonOptions.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{s.year ? ` · ${s.year}` : ""}
              </option>
            ))}
          </select>
          {(filterSplit !== "all" || filterSeason !== "all") && (
            <button
              type="button"
              onClick={() => { setFilterSplit("all"); setFilterSeason("all"); }}
              className="text-[10px] uppercase tracking-widest px-2 py-1 border border-white/10 text-neutral-400 hover:bg-white/5"
              data-testid="race-filter-reset"
            >Zurücksetzen</button>
          )}
          <span className="text-[10px] text-neutral-500 ml-auto">{visible.length} / {items.length}</span>
        </div>
        {visible.length === 0 && (
          <div className="text-xs text-neutral-500 italic border border-white/5 px-3 py-4 text-center">
            Keine Berichte für diese Auswahl.
          </div>
        )}
        {visible.map(r => (
          <div key={r.id} className={`border p-4 ${r.published ? "border-white/10" : "border-amber-500/30 bg-amber-500/[0.03]"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="font-bold leading-tight">{r.title}</div>
                <div className="text-xs text-neutral-500 mt-1">{r.race_name} · {r.date}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${r.published ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`} data-testid={`race-status-${r.id}`}>
                  {r.published ? "Veröffentlicht" : "Entwurf"}
                </span>
                {r.pinned && (
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-cyan-500/40 text-cyan-300" data-testid={`race-pinned-${r.id}`}>Angepinnt</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button
                onClick={async () => { await api.put(`/race-reports/${r.id}`, { ...r, published: !r.published }); toast.success(r.published ? "Versteckt" : "Veröffentlicht"); load(); }}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${r.published ? "border-white/15 text-neutral-300 hover:bg-white/5" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}
                data-testid={`race-toggle-publish-${r.id}`}
              >{r.published ? "Verstecken" : "Veröffentlichen"}</button>
              <button
                onClick={async () => { await api.put(`/race-reports/${r.id}`, { ...r, pinned: !r.pinned }); toast.success(r.pinned ? "Pin entfernt" : "Auf Startseite angepinnt"); load(); }}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${r.pinned ? "border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}
                data-testid={`race-toggle-pin-${r.id}`}
                title="Auf der Startseite als Haupt-Hero anpinnen"
              >{r.pinned ? "Unpin" : "Pin"}</button>
              <a href={`/rennberichte/${r.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs" title="Vorschau in neuem Tab" data-testid={`race-preview-${r.id}`}><Eye className="w-3.5 h-3.5" /></a>
              <button onClick={() => { setEditingId(r.id); setForm({ ...r }); }} className="btn-ghost text-xs" data-testid={`race-edit-${r.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(r.id)} className="btn-ghost text-xs text-red-400" data-testid={`race-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {(r.split || r.season_id) && (
              <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-widest">
                {r.split && <span style={{ color: splitColor(r.split) }}>{splitName(r.split)}</span>}
                {r.season_id && <span className="text-neutral-500">· {seasonLabel(r.season_id)}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

/* ============= NEWS ============= */
const emptyNews = { title: "", category: "", hero_image: "", summary: "", body: "", author: "Redaktion", published: false };
function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("all"); // "all" | category name
  const [form, setForm] = useState(emptyNews); const [editingId, setEditingId] = useState(null);
  const load = () => Promise.all([
    api.get("/news"),
    api.get("/news-categories"),
  ]).then(([n, c]) => {
    setItems(n.data);
    const cats = c.data || [];
    setCategories(cats);
    // Default-Kategorie sicherstellen, wenn Form leer/im Create-Modus ist
    setForm(f => {
      if (editingId) return f; // beim Bearbeiten nichts überschreiben
      const exists = cats.some(x => x.name === f.category);
      if (!exists && cats.length > 0) return { ...f, category: cats[0].name };
      return f;
    });
  });
  useEffect(() => { load(); }, []);
  const resetForm = () => {
    setForm({ ...emptyNews, category: categories[0]?.name || "" });
    setEditingId(null);
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await api.put(`/news/${editingId}`, form); toast.success("Artikel aktualisiert"); }
      else { await api.post("/news", form); toast.success("Artikel erstellt"); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => { if (!confirm("Wirklich löschen?")) return; await api.delete(`/news/${id}`); toast.success("Gelöscht"); load(); };

  const selectedCatHero = categories.find(c => c.name === form.category)?.hero_image;
  const visible = filterCat === "all" ? items : items.filter(n => n.category === filterCat);

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={save} className="card-surface p-5 lg:col-span-3" data-testid="news-form">
        <div className="font-display font-black italic uppercase text-lg mb-2">{editingId ? "Artikel bearbeiten" : "Neuer Artikel"}</div>
        <Field label="Titel"><input className={TXT} required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="news-title" /></Field>
        <Field label="Kategorie">
          <select
            className={TXT}
            value={form.category}
            onChange={e => setForm({...form, category: e.target.value})}
            data-testid="news-category"
          >
            {categories.length === 0 && <option value="News">News (Standard)</option>}
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          {categories.length === 0 && (
            <div className="text-[10px] text-amber-300/80 mt-1">
              Keine Kategorien angelegt. Lege sie im Tab „News-Kategorien" an.
            </div>
          )}
        </Field>
        <Field label="Hero Bild URL (leer = aus Kategorie übernehmen)">
          <input className={TXT} value={form.hero_image} placeholder={selectedCatHero ? `auto: ${selectedCatHero.slice(0, 50)}…` : ""} onChange={e => setForm({...form, hero_image: e.target.value})} />
        </Field>
        <Field label="Zusammenfassung"><textarea className={TXT} rows={2} required value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} /></Field>
        <MarkdownField
          label="Inhalt (Markdown)"
          value={form.body}
          onChange={(v) => setForm({...form, body: v})}
          rows={10}
          required
          testId="news-body"
          placeholder={"# Überschrift\n\nDer Artikel-Inhalt — mit **Markdown** Unterstützung.\n\n- Bullet 1\n- Bullet 2\n\n[Mehr lesen](https://...)"}
        />
        <Field label="Autor"><input className={TXT} value={form.author} onChange={e => setForm({...form, author: e.target.value})} /></Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="news-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Veröffentlichen"}</button>
          {editingId && <button type="button" onClick={resetForm} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
      </form>
      <div className="lg:col-span-2 space-y-2">
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="news-filter-bar">
            <button
              type="button"
              onClick={() => setFilterCat("all")}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${filterCat === "all" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/15 text-neutral-400 hover:border-white/40"}`}
              data-testid="news-filter-all"
            >Alle ({items.length})</button>
            {categories.map(c => {
              const count = items.filter(n => n.category === c.name).length;
              const active = filterCat === c.name;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setFilterCat(c.name)}
                  className="text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors"
                  style={active ? { borderColor: c.color, background: `${c.color}1A`, color: c.color } : { borderColor: "rgba(255,255,255,0.15)", color: "#a3a3a3" }}
                  data-testid={`news-filter-${c.id}`}
                >{c.name} ({count})</button>
              );
            })}
          </div>
        )}
        {visible.map(n => {
          const catColor = categories.find(c => c.name === n.category)?.color || "#00E5FF";
          return (
          <div key={n.id} className={`border p-4 ${n.published ? "border-white/10" : "border-amber-500/30 bg-amber-500/[0.03]"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest" style={{ color: catColor }}>{n.category}</div>
                <div className="font-bold leading-tight mt-1">{n.title}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${n.published ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`} data-testid={`news-status-${n.id}`}>
                  {n.published ? "Veröffentlicht" : "Entwurf"}
                </span>
                {n.pinned && (
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-cyan-500/40 text-cyan-300" data-testid={`news-pinned-${n.id}`}>Angepinnt</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button
                onClick={async () => { await api.put(`/news/${n.id}`, { ...n, published: !n.published }); toast.success(n.published ? "Versteckt" : "Veröffentlicht"); load(); }}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${n.published ? "border-white/15 text-neutral-300 hover:bg-white/5" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}
                data-testid={`news-toggle-publish-${n.id}`}
              >{n.published ? "Verstecken" : "Veröffentlichen"}</button>
              <button
                onClick={async () => { await api.put(`/news/${n.id}`, { ...n, pinned: !n.pinned }); toast.success(n.pinned ? "Pin entfernt" : "Auf Startseite angepinnt"); load(); }}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${n.pinned ? "border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}
                data-testid={`news-toggle-pin-${n.id}`}
                title="Auf der Startseite als Haupt-Hero anpinnen"
              >{n.pinned ? "Unpin" : "Pin"}</button>
              <a href={`/news/${n.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs" title="Vorschau in neuem Tab" data-testid={`news-preview-${n.id}`}><Eye className="w-3.5 h-3.5" /></a>
              <button onClick={() => { setEditingId(n.id); setForm({ ...n }); }} className="btn-ghost text-xs" data-testid={`news-edit-${n.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(n.id)} className="btn-ghost text-xs text-red-400" data-testid={`news-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          );
        })}
        {visible.length === 0 && (
          <div className="text-xs text-neutral-500 px-4 py-6 text-center border border-white/5">Keine Artikel.</div>
        )}
      </div>
    </div>
  );
}

/* ============= NEWS KATEGORIEN ============= */
const emptyNewsCat = { name: "", hero_image: "", color: "#00E5FF", sort_order: 0 };
function NewsCategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyNewsCat);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/news-categories").then(x => setItems(x.data || []));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const { data } = await api.put(`/news-categories/${editingId}`, form);
        const updated = data?.news_updated || 0;
        toast.success(updated > 0 ? `Kategorie aktualisiert · ${updated} News-Hero${updated === 1 ? "" : "s"} migriert` : "Kategorie aktualisiert");
      } else {
        await api.post("/news-categories", form);
        toast.success("Kategorie angelegt");
      }
      setForm(emptyNewsCat); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => {
    if (!confirm("Kategorie löschen? News behalten den Kategorie-Namen, verlieren aber die automatische Hero-Bild-Verknüpfung.")) return;
    await api.delete(`/news-categories/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={save} className="card-surface p-5 lg:col-span-2" data-testid="news-cat-form">
        <div className="font-display font-black italic uppercase text-lg mb-2">{editingId ? "Kategorie bearbeiten" : "Neue Kategorie"}</div>
        <Field label="Name"><input className={TXT} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="news-cat-name" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Sortierung"><input type="number" className={TXT} value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} /></Field>
          <Field label="Akzent-Farbe">
            <div className="flex items-center gap-2">
              <input type="color" value={form.color || "#00E5FF"} onChange={e => setForm({...form, color: e.target.value})} className="w-12 h-10 bg-transparent border border-white/10 cursor-pointer" data-testid="news-cat-color" />
              <input type="text" className={TXT} value={form.color || "#00E5FF"} onChange={e => setForm({...form, color: e.target.value})} />
            </div>
          </Field>
        </div>
        <Field label="Hero-Bild (wird auf alle News dieser Kategorie ohne eigenes Bild angewendet)">
          <ImageUploader
            value={form.hero_image}
            onChange={url => setForm({...form, hero_image: url})}
            folder="news_categories"
            targetW={1600}
            targetH={900}
            cropFrom="center"
            hint="Empfohlen: 16:9 · wird auf 1600×900 zugeschnitten"
            previewSize="w-40 h-[90px]"
            testIdPrefix="news-cat-hero"
          />
        </Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="news-cat-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Anlegen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptyNewsCat); }} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
      </form>
      <div className="lg:col-span-3 space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-neutral-500 px-4 py-6 text-center border border-white/5">Noch keine Kategorien angelegt.</div>
        )}
        {items.map(c => (
          <div key={c.id} className="border border-white/10 p-3 flex items-center gap-4" data-testid={`news-cat-row-${c.id}`}>
            <div className="w-20 h-14 bg-[#0B0F19] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
              {c.hero_image ? <img src={c.hero_image} alt={c.name} className="w-full h-full object-cover" draggable={false} /> : <span className="text-[10px] text-neutral-600 uppercase">kein Bild</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c.color || "#00E5FF" }} />
                <span className="font-bold" style={{ color: c.color || undefined }}>{c.name}</span>
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">Sortierung: {c.sort_order} · {c.color}</div>
            </div>
            <button onClick={() => { setEditingId(c.id); setForm({ name: c.name, hero_image: c.hero_image || "", color: c.color || "#00E5FF", sort_order: c.sort_order || 0 }); }} className="btn-ghost text-xs" data-testid={`news-cat-edit-${c.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
            <button onClick={() => del(c.id)} className="btn-ghost text-xs text-red-400" data-testid={`news-cat-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============= ROADMAP ============= */
const emptyRoadmap = { title: "", description: "", status: "planned", category: "feature", target: "", hero_image: "", sort_order: 0 };
const ROADMAP_STATUS_OPTS = [
  { v: "planned", label: "Geplant" },
  { v: "in_progress", label: "In Arbeit" },
  { v: "launched", label: "Veröffentlicht" },
];
const ROADMAP_CAT_OPTS = [
  { v: "feature", label: "Feature" },
  { v: "content", label: "Content" },
  { v: "other", label: "Sonstiges" },
];
function RoadmapAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyRoadmap);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/roadmap").then(x => setItems(x.data || []));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, sort_order: parseInt(form.sort_order) || 0 };
      if (editingId) { await api.put(`/roadmap/${editingId}`, payload); toast.success("Eintrag aktualisiert"); }
      else { await api.post("/roadmap", payload); toast.success("Eintrag erstellt"); }
      setForm(emptyRoadmap); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => { if (!confirm("Eintrag wirklich löschen?")) return; await api.delete(`/roadmap/${id}`); toast.success("Gelöscht"); load(); };

  const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={save} className="card-surface p-5 lg:col-span-2" data-testid="roadmap-form">
        <div className="font-display font-black italic uppercase text-lg mb-2">{editingId ? "Eintrag bearbeiten" : "Neuer Eintrag"}</div>
        <Field label="Titel"><input className={TXT} required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="roadmap-title" /></Field>
        <Field label="Kurzbeschreibung">
          <textarea className={TXT} rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="roadmap-description" />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Status">
            <select className={TXT} value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="roadmap-status">
              {ROADMAP_STATUS_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Kategorie">
            <select className={TXT} value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="roadmap-category">
              {ROADMAP_CAT_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label='Zielzeitraum (frei, z. B. "Q2 2026" oder "Saison 6")'>
            <input className={TXT} value={form.target} onChange={e => setForm({...form, target: e.target.value})} placeholder="Q2 2026" data-testid="roadmap-target" />
          </Field>
          <Field label="Sortierung"><input type="number" className={TXT} value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} data-testid="roadmap-sort" /></Field>
        </div>
        <Field label="Hero-Bild (optional)">
          <ImageUploader
            value={form.hero_image || ""}
            onChange={url => setForm({...form, hero_image: url})}
            folder="roadmap"
            targetW={1600} targetH={900} cropFrom="center"
            hint="Empfohlen: 16:9 · wird auf 1600×900 zugeschnitten"
            previewSize="w-40 h-[90px]"
            testIdPrefix="roadmap-hero"
          />
        </Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="roadmap-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Anlegen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptyRoadmap); }} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
      </form>
      <div className="lg:col-span-3 space-y-2">
        {sorted.length === 0 && (
          <div className="text-xs text-neutral-500 px-4 py-6 text-center border border-white/5">Noch keine Roadmap-Einträge angelegt.</div>
        )}
        {sorted.map(it => {
          const statLabel = ROADMAP_STATUS_OPTS.find(o => o.v === it.status)?.label || it.status;
          const catLabel = ROADMAP_CAT_OPTS.find(o => o.v === it.category)?.label || it.category;
          const statColor = it.status === "launched" ? "#10B981" : it.status === "in_progress" ? "#F59E0B" : "#A3A3A3";
          const catColor = it.category === "feature" ? "#00E5FF" : it.category === "content" ? "#A855F7" : "#737373";
          return (
            <div key={it.id} className="border border-white/10 p-3 flex items-start gap-4" data-testid={`roadmap-row-${it.id}`}>
              <div className="w-20 h-14 bg-[#0B0F19] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                {it.hero_image ? <img src={it.hero_image} alt={it.title} className="w-full h-full object-cover" /> : <span className="text-[10px] text-neutral-600 uppercase">kein Bild</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border" style={{ borderColor: `${catColor}66`, color: catColor }}>{catLabel}</span>
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border" style={{ borderColor: `${statColor}66`, color: statColor }}>{statLabel}</span>
                  {it.target && <span className="text-[10px] text-neutral-500">Ziel: {it.target}</span>}
                  <span className="text-[10px] text-neutral-600 ml-auto">#{it.sort_order || 0}</span>
                </div>
                <div className="font-bold leading-tight mt-1">{it.title}</div>
                {it.description && <div className="text-xs text-neutral-400 mt-1 line-clamp-2">{it.description}</div>}
              </div>
              <button onClick={() => { setEditingId(it.id); setForm({ ...emptyRoadmap, ...it }); }} className="btn-ghost text-xs" data-testid={`roadmap-edit-${it.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(it.id)} className="btn-ghost text-xs text-red-400" data-testid={`roadmap-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ============= AI GENERATOR ============= */
const emptyAI = { race_name: "", track: "", date: "", weather: "Trocken", laps: 50, pole: "", fastest_lap: "", podium_text: "", dnf_text: "", key_events: "", tone: "spannend, sportlich" };
function AIGeneratorPanel() {
  const [form, setForm] = useState(emptyAI);
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true); setOut(null);
    try {
      const payload = {
        race_name: form.race_name, track: form.track, date: form.date, weather: form.weather,
        laps: parseInt(form.laps)||50, pole: form.pole || null, fastest_lap: form.fastest_lap || null,
        podium: form.podium_text.split("\n").map(s => s.trim()).filter(Boolean),
        dnfs: form.dnf_text.split("\n").map(s => s.trim()).filter(Boolean),
        key_events: form.key_events, tone: form.tone,
      };
      const { data } = await api.post("/ai/generate-race-report", payload);
      setOut(data);
      toast.success("Bericht generiert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const publish = async () => {
    if (!out) return;
    try {
      const payload = {
        title: out.title, race_name: form.race_name, track: form.track, date: form.date,
        weather: form.weather, hero_image: "", summary: out.summary || out.body.slice(0, 220),
        body: out.body, author: "Redaktion (AI)",
      };
      await api.post("/race-reports", payload);
      toast.success("Als Rennbericht veröffentlicht");
      setOut(null); setForm(emptyAI);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={generate} className="card-surface p-5" data-testid="ai-form">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#00E5FF]" />
          <div className="font-display font-black italic uppercase text-lg">AI Rennbericht Generator</div>
        </div>
        <p className="text-sm text-neutral-400">Trage die Renndaten ein – Claude verfasst einen vollständigen Bericht.</p>
        <Field label="Renn-Name"><input className={TXT} required value={form.race_name} onChange={e => setForm({...form, race_name: e.target.value})} data-testid="ai-race-name" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Strecke"><input className={TXT} required value={form.track} onChange={e => setForm({...form, track: e.target.value})} /></Field>
          <Field label="Datum"><input type="date" className={TXT} required value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Wetter"><input className={TXT} value={form.weather} onChange={e => setForm({...form, weather: e.target.value})} /></Field>
          <Field label="Runden"><input type="number" className={TXT} value={form.laps} onChange={e => setForm({...form, laps: e.target.value})} /></Field>
        </div>
        <Field label="Pole Position"><input className={TXT} value={form.pole} onChange={e => setForm({...form, pole: e.target.value})} placeholder="Fahrer (Team)" /></Field>
        <Field label="Schnellste Runde"><input className={TXT} value={form.fastest_lap} onChange={e => setForm({...form, fastest_lap: e.target.value})} placeholder="Fahrer (Team)" /></Field>
        <Field label="Podium (1.–3., jeweils eine Zeile)">
          <textarea className={TXT} rows={3} value={form.podium_text} onChange={e => setForm({...form, podium_text: e.target.value})}
            placeholder={"Max Brinkmann (Soester Sturm)\nTim Hoffmann (Westfalen Velocity)\nJonas Lütke (Soester Sturm)"} />
        </Field>
        <Field label="DNFs (eine pro Zeile)">
          <textarea className={TXT} rows={2} value={form.dnf_text} onChange={e => setForm({...form, dnf_text: e.target.value})} />
        </Field>
        <Field label="Wichtige Ereignisse"><textarea className={TXT} rows={3} value={form.key_events} onChange={e => setForm({...form, key_events: e.target.value})} /></Field>
        <Field label="Tonalität"><input className={TXT} value={form.tone} onChange={e => setForm({...form, tone: e.target.value})} /></Field>
        <button type="submit" disabled={loading} className="btn-primary chamfer-sm mt-4 w-full justify-center" data-testid="ai-generate-btn">
          <Sparkles className="w-4 h-4" /> {loading ? "Generiere..." : "Mit AI generieren"}
        </button>
      </form>

      <div className="card-surface p-5 min-h-[300px]" data-testid="ai-output">
        {!out && <div className="text-neutral-500 text-sm">Noch keine Ausgabe.</div>}
        {out && (
          <>
            <div className="eyebrow text-[#00E5FF]">Generierter Bericht</div>
            <h3 className="font-display font-black italic uppercase text-2xl mt-1 leading-tight">{out.title}</h3>
            <p className="text-sm text-neutral-300 mt-3">{out.summary}</p>
            <div className="mt-4 text-sm text-neutral-200 leading-relaxed whitespace-pre-line max-h-96 overflow-auto border-t border-white/10 pt-4">{out.body}</div>
            <button onClick={publish} className="btn-primary chamfer-sm mt-4" data-testid="ai-publish-btn"><Plus className="w-4 h-4" /> Als Rennbericht veröffentlichen</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ============= CALENDAR ============= */
const emptyEvent = { split: "champions", season_id: "", title: "", track: "", country_code: "DE", short_code: "", date: "", time: "20:00", round: 1, completed: false, is_sprint: false, notes: "", hero_image: "" };
function CalendarAdmin() {
  const splits = useSplits();
  const splitMeta = (k) => splits.find(s => s.key === k) || splits[0] || { name: k, color: "#00E5FF" };
  const [items, setItems] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [form, setForm] = useState(emptyEvent);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [seasonFilter, setSeasonFilter] = useState("all");
  const load = () => Promise.all([
    api.get("/calendar").then(x => setItems(x.data)),
    api.get("/seasons").then(x => setSeasons(x.data)),
  ]);
  useEffect(() => {
    (async () => {
      await load();
      const cur = await api.get("/seasons/current").then(x => x.data).catch(() => null);
      if (cur) setForm(f => ({ ...f, season_id: cur.id }));
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, round: parseInt(form.round)||1, season_id: form.season_id || null };
      if (editingId) { await api.put(`/calendar/${editingId}`, payload); toast.success("Event aktualisiert"); }
      else { await api.post("/calendar", payload); toast.success("Event erstellt"); }
      setForm({ ...emptyEvent, season_id: form.season_id }); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => { if (!confirm("Event wirklich löschen?")) return; await api.delete(`/calendar/${id}`); toast.success("Gelöscht"); load(); };

  const seasonsById = Object.fromEntries(seasons.map(s => [s.id, s]));
  const filtered = items
    .filter(i => filter === "all" || i.split === filter)
    .filter(i => seasonFilter === "all" || i.season_id === seasonFilter)
    .slice()
    .sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={save} className="card-surface p-5 lg:col-span-2" data-testid="calendar-form">
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays className="w-5 h-5 text-[#00E5FF]" />
          <div className="font-display font-black italic uppercase text-lg">{editingId ? "Event bearbeiten" : "Neues Event"}</div>
        </div>
        <Field label="Split">
          <select className={TXT} value={form.split} onChange={e => {
            const newSplit = e.target.value;
            // Auto-Zeit: Champions 19:00, Elite 20:00, andere bleiben
            const defaultTime = newSplit === "champions" ? "19:00" : newSplit === "elite" ? "20:00" : form.time;
            setForm({ ...form, split: newSplit, time: defaultTime });
          }} data-testid="event-split">
            {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Saison">
          <select className={TXT} required value={form.season_id || ""} onChange={e => setForm({...form, season_id: e.target.value})} data-testid="event-season">
            <option value="" disabled>— Saison wählen —</option>
            {seasons.filter(s => (s.split || "champions") === form.split).map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.is_current ? " · aktuell" : ""}</option>
            ))}
          </select>
        </Field>
        <Field label="Titel"><input className={TXT} required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title" /></Field>
        <Field label="F1 2026 Strecken-Quickpick">
          <select
            className={TXT}
            value=""
            onChange={e => {
              const idx = parseInt(e.target.value);
              if (isNaN(idx)) return;
              const t = F1_2026_TRACKS[idx];
              const countryName = COUNTRY_NAME_BY_CODE[t.country_code] || "";
              setForm(f => ({
                ...f,
                track: t.track,
                country_code: t.country_code,
                short_code: t.short_code,
                round: f.round || (idx + 1),
                title: countryName ? `Großer Preis von ${countryName}` : f.title,
              }));
              toast.success(`Strecke übernommen: ${t.track}`);
            }}
            data-testid="event-track-quickpick"
          >
            <option value="">— Strecke wählen (füllt Strecke, Land & Kürzel) —</option>
            {F1_2026_TRACKS.map((t, i) => (
              <option key={`${t.country_code}-${t.track}`} value={i}>R{String(i + 1).padStart(2, "0")} · {t.track} · {t.country_code}</option>
            ))}
          </select>
        </Field>
        <Field label="Strecke"><input className={TXT} required value={form.track} onChange={e => setForm({...form, track: e.target.value})} data-testid="event-track" /></Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Runde"><input type="number" className={TXT} value={form.round} onChange={e => setForm({...form, round: e.target.value})} data-testid="event-round" /></Field>
          <Field label="Datum"><input type="date" className={TXT} required value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="event-date" /></Field>
          <Field label="Zeit"><input className={TXT} value={form.time} onChange={e => setForm({...form, time: e.target.value})} placeholder="20:00" data-testid="event-time" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Land (ISO-2)">
            <select className={TXT} value={form.country_code} onChange={e => {
              const code = e.target.value;
              const countryName = COUNTRY_NAME_BY_CODE[code] || "";
              // Title automatisch füllen mit "Großer Preis von <Land>"
              const newTitle = countryName ? `Großer Preis von ${countryName}` : form.title;
              setForm({ ...form, country_code: code, title: newTitle });
            }} data-testid="event-country">
              <option value="">— wählen —</option>
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{flagFromCode(c.code)} {c.name} ({c.code})</option>
              ))}
            </select>
          </Field>
          <Field label="Kürzel (3 Buchstaben, Tabelle)"><input className={TXT} maxLength={4} value={form.short_code || ""} onChange={e => setForm({...form, short_code: e.target.value.toUpperCase()})} data-testid="event-shortcode" placeholder="SST" /></Field>
        </div>
        <Field label="Hero-Bild URL (Strecke · wird beim Prefill in den Rennbericht übernommen)">
          <input className={TXT} value={form.hero_image || ""} placeholder="https://… oder leer = Default-F1-Bild" onChange={e => setForm({...form, hero_image: e.target.value})} data-testid="event-hero-image" />
        </Field>
        <Field label="Notizen"><textarea className={TXT} rows={2} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} data-testid="event-notes" /></Field>
        <label className={`flex items-center gap-2 border px-3 py-2 mt-3 cursor-pointer text-xs uppercase tracking-widest ${form.is_sprint ? "border-[#FFCC00]/60 bg-[#FFCC00]/10 text-[#FFCC00]" : "border-white/10 text-neutral-300"}`}>
          <input type="checkbox" checked={!!form.is_sprint} onChange={e => setForm({...form, is_sprint: e.target.checked})} data-testid="event-sprint" />
          Sprint-Wochenende (Quali + Sprintrennen + Rennen)
        </label>
        <label className="flex items-center gap-2 mt-3 text-sm text-neutral-300">
          <input type="checkbox" checked={form.completed} onChange={e => setForm({...form, completed: e.target.checked})} data-testid="event-completed" />
          Bereits gefahren
        </label>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="event-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Erstellen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptyEvent); }} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
      </form>

      <div className="lg:col-span-3">
        <div className="flex gap-2 mb-3 flex-wrap items-center">
          {[{ k: "all", label: "Alle Splits", color: "#00E5FF" }, ...splits.map(s => ({ k: s.key, label: s.name, color: s.color }))].map(({ k, label, color }) => (
            <button key={k} onClick={() => { setFilter(k); setSeasonFilter("all"); }}
              className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 border ${filter === k ? "" : "border-white/10 text-neutral-300"}`}
              style={filter === k ? { borderColor: color, color: color } : undefined}
              data-testid={`event-filter-${k}`}>{label}</button>
          ))}
          <select value={seasonFilter} onChange={e => setSeasonFilter(e.target.value)}
            className="bg-[#0B0F19] border border-white/10 px-2 py-1.5 text-xs ml-auto" data-testid="event-season-filter">
            <option value="all">Alle Saisons</option>
            {seasons
              .filter(s => filter === "all" || (s.split || "champions") === filter)
              .map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.game ? ` · ${s.game}` : ""}{filter === "all" ? ` · ${splitMeta(s.split).name}` : ""}{s.is_current ? " · aktuell" : ""}
                </option>
              ))}
          </select>
        </div>
        <div className="space-y-2">
          {filtered.map(ev => (
            <div key={ev.id} className="border border-white/10 p-4 flex items-center gap-4">
              <div className="w-1.5 h-12" style={{ background: splitMeta(ev.split).color }} />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center flex-wrap gap-2">
                  <span>{splitMeta(ev.split).name} · R{String(ev.round).padStart(2,"0")} · {seasonsById[ev.season_id]?.name || "?"}</span>
                  {ev.is_sprint && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold tracking-widest" style={{ background: "#FFCC00", color: "#000" }}>SPRINT</span>
                  )}
                </div>
                <div className="font-bold leading-tight mt-0.5">{ev.title}</div>
                <div className="text-xs text-neutral-500 mt-1">{ev.track} · {ev.date} · {ev.time} {ev.completed ? "· gefahren" : ""}</div>
              </div>
              <button onClick={() => { setEditingId(ev.id); setForm({ ...emptyEvent, ...ev }); }} className="btn-ghost text-xs" data-testid={`event-edit-${ev.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(ev.id)} className="btn-ghost text-xs text-red-400" data-testid={`event-delete-${ev.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-neutral-500 text-sm p-4">Keine Events.</div>}
        </div>
      </div>
    </div>
  );
}

/* ============= SEASON STATS EDITOR (in Driver edit) ============= */
function SeasonStatsEditor({ driverId, defaultSplit }) {
  const splits = useSplits();
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [stats, setStats] = useState({ points: 0, wins: 0, podiums: 0, poles: 0, fastest_laps: 0, dnfs: 0, split: defaultSplit });

  useEffect(() => {
    (async () => {
      const [s, cur] = await Promise.all([
        api.get("/seasons").then(x => x.data).catch(() => []),
        api.get("/seasons/current").then(x => x.data).catch(() => null),
      ]);
      setSeasons(s);
      if (cur) setSeasonId(cur.id);
    })();
  }, []);

  useEffect(() => {
    if (!seasonId || !driverId) return;
    api.get(`/season-stats?driver_id=${driverId}&season_id=${seasonId}`).then(({ data }) => {
      const row = data?.[0];
      setStats(row ? {
        points: row.points || 0, wins: row.wins || 0, podiums: row.podiums || 0,
        poles: row.poles || 0, fastest_laps: row.fastest_laps || 0, dnfs: row.dnfs || 0,
        split: row.split || defaultSplit,
      } : { points: 0, wins: 0, podiums: 0, poles: 0, fastest_laps: 0, dnfs: 0, split: defaultSplit });
    }).catch(() => {});
  }, [driverId, seasonId, defaultSplit]);

  const save = async (e) => {
    e.preventDefault();
    if (!seasonId) { toast.error("Bitte Saison wählen"); return; }
    try {
      await api.put("/season-stats", { driver_id: driverId, season_id: seasonId, ...stats });
      toast.success("Saison-Statistik gespeichert");
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="mt-6 border-t border-white/10 pt-5" data-testid="season-stats-editor">
      <div className="font-display font-black italic uppercase text-base">Saison-Statistik</div>
      <p className="text-xs text-neutral-500 mt-1">Hier werden Punkte & Erfolge pro Saison gepflegt – diese fließen direkt in die Wertung.</p>
      <Field label="Saison">
        <select className={TXT} value={seasonId} onChange={e => setSeasonId(e.target.value)} data-testid="stats-season">
          {seasons.filter(s => (s.split || "champions") === stats.split).map(s => (
            <option key={s.id} value={s.id}>{s.name}{s.is_current ? " · aktuell" : ""}</option>
          ))}
        </select>
      </Field>
      <Field label="Split (in dieser Saison)">
        <select className={TXT} value={stats.split} onChange={e => setStats({ ...stats, split: e.target.value })} data-testid="stats-split">
          {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-3 gap-2 mt-2">
        <Field label="Punkte"><input type="number" className={TXT} value={stats.points} onChange={e => setStats({ ...stats, points: parseInt(e.target.value)||0 })} data-testid="stats-points" /></Field>
        <Field label="Siege"><input type="number" className={TXT} value={stats.wins} onChange={e => setStats({ ...stats, wins: parseInt(e.target.value)||0 })} /></Field>
        <Field label="Podien"><input type="number" className={TXT} value={stats.podiums} onChange={e => setStats({ ...stats, podiums: parseInt(e.target.value)||0 })} /></Field>
        <Field label="Polen"><input type="number" className={TXT} value={stats.poles} onChange={e => setStats({ ...stats, poles: parseInt(e.target.value)||0 })} /></Field>
        <Field label="Fast. R."><input type="number" className={TXT} value={stats.fastest_laps} onChange={e => setStats({ ...stats, fastest_laps: parseInt(e.target.value)||0 })} /></Field>
        <Field label="DNFs"><input type="number" className={TXT} value={stats.dnfs} onChange={e => setStats({ ...stats, dnfs: parseInt(e.target.value)||0 })} /></Field>
      </div>
      <button onClick={save} type="button" className="btn-primary chamfer-sm mt-3" data-testid="stats-save"><Plus className="w-4 h-4" /> Statistik speichern</button>
    </div>
  );
}

/* ============= SEASONS ============= */
const emptySeason = { name: "", year: new Date().getFullYear(), is_current: false, split: "champions", game: "F1 25" };

function SeasonsAdmin() {
  const SPLIT_OPTIONS = useSplits().map(s => ({ key: s.key, label: s.name, color: s.color }));
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptySeason);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/seasons").then(x => setItems(x.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        year: parseInt(form.year) || new Date().getFullYear(),
        split: form.split || "champions",
        game: (form.game || "").trim(),
      };
      if (editingId) { await api.put(`/seasons/${editingId}`, payload); toast.success("Saison aktualisiert"); }
      else { await api.post("/seasons", payload); toast.success("Saison erstellt"); }
      setForm(emptySeason); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => {
    if (!confirm("Saison inkl. aller zugehörigen Statistiken & Kalender-Events löschen?")) return;
    await api.delete(`/seasons/${id}`); toast.success("Gelöscht"); load();
  };

  // Gruppiere nach Split für Übersicht
  const grouped = SPLIT_OPTIONS.map(sp => ({
    ...sp,
    seasons: items.filter(s => (s.split || "champions") === sp.key)
                  .sort((a, b) => (b.year || 0) - (a.year || 0)),
  }));

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <form onSubmit={save} className="card-surface p-5 md:col-span-1" data-testid="season-form">
        <div className="font-display font-black italic uppercase text-lg">{editingId ? "Saison bearbeiten" : "Neue Saison"}</div>
        <Field label="Name"><input className={TXT} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="season-name" placeholder="Season 4" /></Field>
        <Field label="Spiel">
          <input
            list="season-games"
            className={TXT}
            required
            value={form.game || ""}
            onChange={e => setForm({...form, game: e.target.value})}
            data-testid="season-game"
            placeholder="F1 25"
          />
          <datalist id="season-games">
            <option value="F1 22" />
            <option value="F1 23" />
            <option value="F1 24" />
            <option value="F1 25" />
            <option value="F1 26" />
            <option value="EA SPORTS F1 25" />
            <option value="EA SPORTS F1 26" />
          </datalist>
        </Field>
        <Field label="Split">
          <div className="grid grid-cols-2 gap-2">
            {SPLIT_OPTIONS.map(sp => {
              const active = (form.split || "champions") === sp.key;
              return (
                <button
                  key={sp.key}
                  type="button"
                  onClick={() => setForm({ ...form, split: sp.key })}
                  className={`border px-3 py-2 text-xs font-display font-black italic uppercase tracking-widest transition-colors ${active ? "" : "border-white/10 text-neutral-400 hover:border-white/30"}`}
                  style={active ? { borderColor: sp.color, color: sp.color, background: `${sp.color}1A` } : undefined}
                  data-testid={`season-split-${sp.key}`}
                >
                  {sp.label}
                </button>
              );
            })}
          </div>
        </Field>
        <label className="flex items-center gap-2 mt-3 text-sm text-neutral-300">
          <input type="checkbox" checked={form.is_current} onChange={e => setForm({...form, is_current: e.target.checked})} data-testid="season-is-current" />
          Als aktuelle Saison für diesen Split setzen
        </label>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="season-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Erstellen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptySeason); }} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
      </form>
      <div className="md:col-span-2 space-y-6">
        {grouped.map(g => (
          <div key={g.key} data-testid={`seasons-group-${g.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2 h-6" style={{ background: g.color }} />
              <div className="font-display font-black italic uppercase tracking-widest text-sm" style={{ color: g.color }}>{g.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{g.seasons.length} Saison{g.seasons.length === 1 ? "" : "s"}</div>
            </div>
            <div className="space-y-2">
              {g.seasons.map(s => (
                <div key={s.id} className="border border-white/10 p-4 flex items-center gap-4">
                  <div className="font-display font-black italic text-xl leading-none whitespace-nowrap" style={{ color: g.color }} data-testid={`season-game-display-${s.id}`}>
                    {s.game || "—"}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold flex items-center flex-wrap gap-2">
                      {s.name}
                      {s.is_current && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold" style={{ background: g.color, color: "#000" }}>
                          Aktuell
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => { setEditingId(s.id); setForm({ ...emptySeason, ...s }); }} className="btn-ghost text-xs" data-testid={`season-edit-${s.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(s.id)} className="btn-ghost text-xs text-red-400" data-testid={`season-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {g.seasons.length === 0 && <div className="text-neutral-500 text-xs italic">Noch keine Saisons in diesem Split.</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============= RACE RESULTS ADMIN ============= */
function RaceResultsAdmin() {
  const splits = useSplits();
  const [seasons, setSeasons] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]);
  const [splitKey, setSplitKey] = useState(splits[0]?.key || "champions");
  const [seasonId, setSeasonId] = useState("");
  const [eventId, setEventId] = useState("");
  const [rows, setRows] = useState([]); // unified rows
  const [session, setSession] = useState("race"); // quali | race | sprint
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrPreview, setOcrPreview] = useState(null);
  const ocrFileRef = useRef(null);
  const eventById = Object.fromEntries(events.map(e => [e.id, e]));
  const currentEvent = eventById[eventId] || null;
  const isSprintEvent = !!currentEvent?.is_sprint;

  // Initial load
  useEffect(() => {
    Promise.all([
      api.get("/seasons").then(x => x.data),
      api.get("/drivers").then(x => x.data),
      api.get("/teams").then(x => x.data),
      api.get("/calendar").then(x => x.data),
    ]).then(([s, d, t, e]) => {
      setSeasons(s || []); setDrivers(d || []); setTeams(t || []); setEvents(e || []);
    }).catch(() => {});
  }, []);

  // Auto-select season für Split
  useEffect(() => {
    const inSplit = seasons.filter(s => (s.split || "champions") === splitKey);
    if (!inSplit.length) return;
    if (seasonId && inSplit.some(s => s.id === seasonId)) return;
    const cur = inSplit.find(s => s.is_current) || [...inSplit].sort((a,b) => (b.year||0)-(a.year||0))[0];
    if (cur) Promise.resolve().then(() => setSeasonId(cur.id));
  }, [splitKey, seasons]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first event of season+split
  useEffect(() => {
    const evs = events
      .filter(e => e.split === splitKey && (!seasonId || e.season_id === seasonId))
      .sort((a,b) => (a.date || "").localeCompare(b.date || ""));
    if (!evs.length) { Promise.resolve().then(() => setEventId("")); return; }
    if (eventId && evs.some(e => e.id === eventId)) return;
    Promise.resolve().then(() => setEventId(evs[0].id));
  }, [splitKey, seasonId, events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lade bestehende Ergebnisse für eventId
  useEffect(() => {
    if (!eventId) { Promise.resolve().then(() => setRows([])); return; }
    api.get(`/race-results?calendar_event_id=${eventId}`).then(({ data }) => {
      const splitDrivers = drivers.filter(d => d.split === splitKey && !d.is_rookie && !d.is_reserve_driver);
      const existing = Object.fromEntries((data || []).map(r => [r.driver_id, r]));
      const list = splitDrivers.map(d => existing[d.id] || ({
        driver_id: d.id,
        qualifying_position: null, quali_time: "", quali_gap: "", quali_s1: "", quali_s2: "", quali_s3: "",
        finishing_position: null, race_time: "", race_penalty: "", race_best: "", race_laps: null, race_grid: null, race_stops: null,
        points: 0, fastest_lap: false, dotd: false, dnf: false, penalty_points: 0,
        sprint_position: null, sprint_time: "", sprint_penalty: "", sprint_best: "", sprint_laps: null, sprint_grid: null, sprint_stops: null,
        sprint_points: 0, sprint_fastest_lap: false,
        is_reserve: false, team_id: null,
      }));
      // Reservefahrer ergänzen: gespeicherte Ergebnisse, deren Fahrer NICHT im aktuellen Split sind
      const splitIds = new Set(splitDrivers.map(d => d.id));
      const extras = (data || [])
        .filter(r => !splitIds.has(r.driver_id))
        .map(r => ({ ...r, is_reserve: true }));
      setRows([...list, ...extras]);
    }).catch(() => setRows([]));
  }, [eventId, drivers, splitKey]);

  const update = (driverId, patch) => {
    setRows(rs => rs.map(r => r.driver_id === driverId ? { ...r, ...patch } : r));
  };

  // Tauscht in einer Zeile den zugewiesenen Fahrer (z. B. wenn OCR falsch zugeordnet hat).
  // Blockt, wenn der neue Fahrer bereits eine Zeile hat.
  const replaceDriverInRow = (oldDriverId, newDriverId) => {
    if (!newDriverId || oldDriverId === newDriverId) return;
    let blocked = false;
    setRows(rs => {
      if (rs.some(r => r.driver_id === newDriverId)) { blocked = true; return rs; }
      return rs.map(r => r.driver_id === oldDriverId ? { ...r, driver_id: newDriverId, team_id: null } : r);
    });
    if (blocked) {
      toast.error("Dieser Fahrer hat bereits eine Zeile · zuerst entfernen oder tauschen.");
    } else {
      toast.success("Fahrer-Zuordnung geändert");
    }
  };

  const reloadResults = () => {
    if (!eventId) return;
    api.get(`/race-results?calendar_event_id=${eventId}`).then(({ data }) => {
      const splitDrivers = drivers.filter(d => d.split === splitKey && !d.is_rookie && !d.is_reserve_driver);
      const existing = Object.fromEntries((data || []).map(r => [r.driver_id, r]));
      const list = splitDrivers.map(d => existing[d.id] || ({
        driver_id: d.id,
        qualifying_position: null, quali_time: "", quali_gap: "", quali_s1: "", quali_s2: "", quali_s3: "",
        finishing_position: null, race_time: "", race_penalty: "", race_best: "", race_laps: null, race_grid: null, race_stops: null,
        points: 0, fastest_lap: false, dotd: false, dnf: false, penalty_points: 0,
        sprint_position: null, sprint_time: "", sprint_penalty: "", sprint_best: "", sprint_laps: null, sprint_grid: null, sprint_stops: null,
        sprint_points: 0, sprint_fastest_lap: false,
        is_reserve: false, team_id: null,
      }));
      const splitIds = new Set(splitDrivers.map(d => d.id));
      const extras = (data || []).filter(r => !splitIds.has(r.driver_id)).map(r => ({ ...r, is_reserve: true }));
      setRows([...list, ...extras]);
    }).catch(() => {});
  };

  const deleteSession = async (which) => {
    if (!eventId) return;
    const labelMap = { quali: "Quali", race: "Rennen", sprint: "Sprint", all: "ALLE Ergebnisse" };
    if (!confirm(`${labelMap[which]}-Ergebnisse für dieses Rennen wirklich löschen?`)) return;
    try {
      await api.delete(`/race-results?calendar_event_id=${eventId}&session_type=${which}`);
      toast.success(`${labelMap[which]}-Ergebnisse gelöscht`);
      reloadResults();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const driverById = Object.fromEntries(drivers.map(d => [d.id, d]));
  const teamById = Object.fromEntries(teams.map(t => [t.id, t]));

  const save = async () => {
    if (!eventId) { toast.error("Kein Rennen ausgewählt"); return; }
    setSaving(true);
    try {
      const results = rows.map(r => ({
        driver_id: r.driver_id,
        qualifying_position: r.qualifying_position ? parseInt(r.qualifying_position) : null,
        quali_time: r.quali_time || "",
        quali_gap: r.quali_gap || "",
        quali_s1: r.quali_s1 || "", quali_s2: r.quali_s2 || "", quali_s3: r.quali_s3 || "",
        finishing_position: r.finishing_position ? parseInt(r.finishing_position) : null,
        race_time: r.race_time || "", race_penalty: r.race_penalty || "", race_best: r.race_best || "",
        race_laps: r.race_laps ? parseInt(r.race_laps) : null,
        race_grid: r.race_grid ? parseInt(r.race_grid) : null,
        race_stops: r.race_stops ? parseInt(r.race_stops) : null,
        points: parseFloat(r.points) || 0,
        fastest_lap: !!r.fastest_lap,
        dotd: !!r.dotd,
        dnf: !!r.dnf,
        penalty_points: parseInt(r.penalty_points) || 0,
        sprint_position: r.sprint_position ? parseInt(r.sprint_position) : null,
        sprint_time: r.sprint_time || "", sprint_penalty: r.sprint_penalty || "", sprint_best: r.sprint_best || "",
        sprint_laps: r.sprint_laps ? parseInt(r.sprint_laps) : null,
        sprint_grid: r.sprint_grid ? parseInt(r.sprint_grid) : null,
        sprint_stops: r.sprint_stops ? parseInt(r.sprint_stops) : null,
        sprint_points: parseFloat(r.sprint_points) || 0,
        sprint_fastest_lap: !!r.sprint_fastest_lap,
        is_reserve: !!r.is_reserve,
        team_id: r.team_id || null,
      }));
      await api.post("/race-results/bulk", { calendar_event_id: eventId, results });
      toast.success("Ergebnisse gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  // ----- OCR via Gemini -----
  const resizeToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1600;
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleOcrFile = async (file) => {
    if (!file) return;
    if (!eventId) { toast.error("Bitte zuerst ein Rennen auswählen"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Bitte ein Bild auswählen (PNG/JPG)"); return; }
    setOcrLoading(true);
    try {
      const dataUrl = await resizeToDataUrl(file);
      // 1) Background-Task starten
      const { data: startRes } = await api.post("/race-results/ocr", {
        calendar_event_id: eventId,
        session_type: session,
        image_base64: dataUrl,
      });
      const taskId = startRes.task_id;
      if (!taskId) { throw new Error("Kein task_id vom Server erhalten"); }
      // 2) Pollen bis status == done | error (max. 3 Minuten)
      const started = Date.now();
      let data = null;
      while (Date.now() - started < 180_000) {
        await new Promise(r => setTimeout(r, 2000));
        const { data: statusRes } = await api.get(`/race-results/ocr/${taskId}`);
        if (statusRes.status === "done") { data = statusRes.result; break; }
        if (statusRes.status === "error") { throw new Error(statusRes.error || "OCR fehlgeschlagen"); }
      }
      if (!data) { throw new Error("Timeout: OCR brauchte länger als 3 Minuten"); }
      setOcrPreview(data);
      if (data.fallback_used) {
        toast.warning("Dein Google-API-Key hat das Tageslimit erreicht — OCR wurde via Emergent LLM Key durchgeführt.", { duration: 8000 });
      }
      // Falls neue Ersatzfahrer angelegt wurden: Drivers-State refreshen
      const created = data.created_reserves || [];
      if (created.length > 0) {
        try {
          const { data: ds } = await api.get("/drivers");
          setDrivers(ds || []);
        } catch { /* ignore */ }
        toast.success(`${data.results?.length || 0} Fahrer erkannt (${session.toUpperCase()}) · ${created.length} neue Ersatzfahrer angelegt: ${created.map(c => c.name).join(", ")}`);
      } else {
        toast.success(`${data.results?.length || 0} Fahrer erkannt (${session.toUpperCase()})`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "OCR fehlgeschlagen");
    } finally {
      setOcrLoading(false);
      if (ocrFileRef.current) ocrFileRef.current.value = "";
    }
  };

  const updateOcrRow = (idx, patch) => {
    setOcrPreview(p => ({ ...p, results: p.results.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };

  const removeOcrRow = (idx) => {
    setOcrPreview(p => ({ ...p, results: p.results.filter((_, i) => i !== idx) }));
  };

  const renameAutoCreated = async (idx, driverId, currentName) => {
    const next = prompt("Neuer Name für diesen Ersatzfahrer:", currentName || "");
    if (!next || !next.trim() || next.trim() === currentName) return;
    try {
      await api.put(`/drivers/${driverId}`, { name: next.trim(), is_reserve_driver: true, split: "" });
      updateOcrRow(idx, { driver_name_raw: next.trim() });
      const { data: ds } = await api.get("/drivers");
      setDrivers(ds || []);
      toast.success(`Umbenannt → ${next.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Umbenennen fehlgeschlagen");
    }
  };

  const deleteAutoCreated = async (idx, driverId, name) => {
    if (!confirm(`Ersatzfahrer "${name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/drivers/${driverId}`);
      removeOcrRow(idx);
      const { data: ds } = await api.get("/drivers");
      setDrivers(ds || []);
      toast.success(`"${name}" gelöscht`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const applyOcrToForm = () => {
    if (!ocrPreview) return;
    const stype = ocrPreview.session_type || session;
    const sessionFields = stype === "quali"
      ? ["qualifying_position","quali_time","quali_gap","quali_s1","quali_s2","quali_s3"]
      : stype === "sprint"
      ? ["sprint_position","sprint_time","sprint_penalty","sprint_best","sprint_laps","sprint_grid","sprint_stops","sprint_points","sprint_fastest_lap","dotd","dnf","penalty_points"]
      : ["finishing_position","race_time","race_penalty","race_best","race_laps","race_grid","race_stops","points","fastest_lap","dotd","dnf","penalty_points"];

    const matchedRows = (ocrPreview.results || []).filter(r => r.driver_id);
    const byDriver = Object.fromEntries(matchedRows.map(r => [r.driver_id, r]));
    const mainIds = new Set(ocrPreview.main_split_driver_ids || []);

    setRows(rs => {
      const existingIds = new Set(rs.map(r => r.driver_id));
      // 1) Bestehende Zeilen patchen
      const patched = rs.map(r => {
        const m = byDriver[r.driver_id];
        if (!m) return r;
        const patch = {};
        for (const f of sessionFields) if (f in m) patch[f] = m[f];
        // Auto-Reserve: erkannt aber Fahrer aus anderem Split
        if (m.is_reserve_hint || (m.driver_id && !mainIds.has(m.driver_id))) {
          patch.is_reserve = true;
        }
        return { ...r, ...patch };
      });
      // 2) Reserve-Fahrer ergänzen (Fahrer wurde erkannt, ist aber nicht im aktuellen Split → neue Zeile)
      const extras = matchedRows
        .filter(m => !existingIds.has(m.driver_id))
        .map(m => {
          const base = {
            driver_id: m.driver_id,
            qualifying_position: null, quali_time: "", quali_gap: "", quali_s1: "", quali_s2: "", quali_s3: "",
            finishing_position: null, race_time: "", race_penalty: "", race_best: "", race_laps: null, race_grid: null, race_stops: null,
            points: 0, fastest_lap: false, dotd: false, dnf: false, penalty_points: 0,
            sprint_position: null, sprint_time: "", sprint_penalty: "", sprint_best: "", sprint_laps: null, sprint_grid: null, sprint_stops: null,
            sprint_points: 0, sprint_fastest_lap: false,
            is_reserve: true, team_id: null,
          };
          for (const f of sessionFields) if (f in m) base[f] = m[f];
          return base;
        });
      return [...patched, ...extras];
    });

    setOcrPreview(null);
    const unmatched = (ocrPreview.results || []).filter(r => !r.driver_id).length;
    const reserveCount = matchedRows.filter(m => m.is_reserve_hint || !mainIds.has(m.driver_id)).length;
    let msg = `${stype.toUpperCase()}-Daten übernommen`;
    if (reserveCount > 0) msg += ` · ${reserveCount} Ersatzfahrer markiert`;
    if (unmatched > 0) msg += ` · ${unmatched} Fahrer nicht zugeordnet (übersprungen)`;
    toast.success(msg);
  };

  const eventList = events
    .filter(e => e.split === splitKey && (!seasonId || e.season_id === seasonId))
    .sort((a,b) => (a.date || "").localeCompare(b.date || ""));

  return (
    <div className="space-y-5" data-testid="race-results-admin">
      <div className="border border-white/10 bg-white/[0.02] p-4 grid md:grid-cols-3 gap-3">
        <Field label="Split">
          <select className={TXT} value={splitKey} onChange={e => setSplitKey(e.target.value)} data-testid="results-split">
            {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Saison">
          <select className={TXT} value={seasonId} onChange={e => setSeasonId(e.target.value)} data-testid="results-season">
            <option value="">— Saison wählen —</option>
            {seasons.filter(s => (s.split || "champions") === splitKey).map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.game ? ` · ${s.game}` : ""}{s.is_current ? " · aktuell" : ""}</option>
            ))}
          </select>
        </Field>
        <Field label="Rennen">
          <select className={TXT} value={eventId} onChange={e => setEventId(e.target.value)} data-testid="results-event">
            <option value="">— Rennen wählen —</option>
            {eventList.map(e => (
              <option key={e.id} value={e.id}>R{String(e.round || "?").padStart(2,"0")} · {e.title} · {e.date}</option>
            ))}
          </select>
        </Field>
      </div>

      {eventId && rows.length === 0 && (
        <div className="text-neutral-500 text-sm italic">Keine Fahrer in diesem Split.</div>
      )}

      {/* OCR Upload Block */}
      {eventId && rows.length > 0 && (
        <div className="border border-[#A855F7]/30 bg-[#A855F7]/5 p-4 flex flex-wrap items-center gap-3" data-testid="ocr-upload-block">
          <Sparkles className="w-4 h-4 text-[#A855F7]" />
          <div className="flex-1 min-w-[260px]">
            <div className="font-display font-black italic uppercase tracking-widest text-sm text-[#E9D5FF]">{session.toUpperCase()}-Bild via Gemini auslesen</div>
            <div className="text-xs text-neutral-400 mt-0.5">{session === "quali" ? "Position · Driver · Team · Time · GAP · S1 · S2 · S3" : "Position · Driver · Team · Time · Penalty · Best · Laps · Grid · Stops · Points"}</div>
          </div>
          <input
            ref={ocrFileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={e => handleOcrFile(e.target.files?.[0])}
            className="hidden"
            data-testid="ocr-file-input"
          />
          <button
            type="button"
            onClick={() => ocrFileRef.current?.click()}
            disabled={ocrLoading}
            className="btn-primary chamfer-sm disabled:opacity-50"
            style={{ background: "#A855F7", color: "#fff" }}
            data-testid="ocr-upload-btn"
          >
            <Sparkles className="w-4 h-4" /> {ocrLoading ? "Lese aus…" : "Bild hochladen"}
          </button>
        </div>
      )}

      {/* OCR Preview Modal */}
      {ocrPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="ocr-preview-modal">
          <div className="bg-[#0B0F19] border border-[#A855F7]/40 max-w-6xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0B0F19] border-b border-white/10 p-4 flex items-center justify-between z-10">
              <div>
                <div className="font-display font-black italic uppercase text-lg text-[#E9D5FF]">Gemini-Vorschau · {(ocrPreview.session_type || session).toUpperCase()}</div>
                <div className="text-xs text-neutral-400">Prüfe die ausgelesenen Daten. <span className="text-red-400">Rot</span> = nicht zugeordnet · <span className="text-amber-400">Amber</span> = Ersatzfahrer (anderer Split oder global) · <span className="text-cyan-300">Cyan „Neu angelegt"</span> = automatisch als Ersatzfahrer erstellt.</div>
              </div>
              <button onClick={() => setOcrPreview(null)} className="btn-ghost text-xs" data-testid="ocr-cancel">
                <X className="w-4 h-4" /> Abbrechen
              </button>
            </div>
            <div className="p-4 overflow-x-auto">
              <OcrPreviewTable preview={ocrPreview} updateOcrRow={updateOcrRow} renameAutoCreated={renameAutoCreated} deleteAutoCreated={deleteAutoCreated} />
            </div>
            <div className="sticky bottom-0 bg-[#0B0F19] border-t border-white/10 p-4 flex items-center justify-end gap-3">
              <button onClick={() => setOcrPreview(null)} className="btn-ghost text-xs" data-testid="ocr-discard">Verwerfen</button>
              <button onClick={applyOcrToForm} className="btn-primary chamfer-sm" style={{ background: "#A855F7", color: "#fff" }} data-testid="ocr-apply">
                <Plus className="w-4 h-4" /> In Tabelle übernehmen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session-Tabs */}
      {eventId && rows.length > 0 && (
        <div className="flex flex-wrap gap-1 border border-white/10 p-1 bg-[#0B0F19] w-fit">
          {[["quali","Qualifying"],["race","Rennen"],...(isSprintEvent?[["sprint","Sprint"]]:[])].map(([k,lbl]) => (
            <button key={k} type="button"
              onClick={() => setSession(k)}
              className={`px-4 py-1.5 text-xs uppercase font-bold tracking-widest transition-colors ${session===k ? "bg-[#00E5FF] text-black" : "text-neutral-300 hover:bg-white/5"}`}
              data-testid={`session-tab-${k}`}>
              {lbl}{k==="sprint" && <span className="ml-1 px-1 text-[8px]" style={{background:"#FFCC00",color:"#000"}}>S</span>}
            </button>
          ))}
        </div>
      )}

      {eventId && rows.length > 0 && (
        <div className="border border-white/10 overflow-x-auto">
          <SessionTable session={session} rows={rows} driverById={driverById} teamById={teamById} teamsAll={teams} driversAll={drivers} update={update} replaceDriver={replaceDriverInRow} />
        </div>
      )}

      {eventId && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={save} disabled={saving} className="btn-primary chamfer-sm disabled:opacity-50" data-testid="results-save">
            <Plus className="w-4 h-4" /> {saving ? "Speichern…" : "Alle Ergebnisse speichern"}
          </button>
          <button onClick={() => deleteSession(session)} className="btn-ghost text-xs border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-1.5" data-testid={`results-delete-${session}`}>
            <Trash2 className="w-3.5 h-3.5" /> {session === "quali" ? "Quali" : session === "sprint" ? "Sprint" : "Rennen"}-Ergebnisse löschen
          </button>
          <button onClick={() => deleteSession("all")} className="btn-ghost text-xs border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-1.5" data-testid="results-delete-all">
            <Trash2 className="w-3.5 h-3.5" /> Alle Sessions löschen
          </button>
          <span className="text-xs text-neutral-500 ml-auto">Speichern überschreibt alle Ergebnisse.</span>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mt-3">
    <label className="block text-[10px] uppercase tracking-widest text-neutral-400 mb-1">{label}</label>
    {children}
  </div>
);

/* ============= SESSION TABLE (Quali / Race / Sprint) ============= */
function NumIn({ value, onChange, w="w-14", testId }) {
  return <input type="number" className={`${w} bg-[#0B0F19] border border-white/10 text-center px-1 py-1`} value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={testId} />;
}
function TxtIn({ value, onChange, w="w-20", testId, placeholder, color }) {
  return <input type="text" className={`${w} bg-[#0B0F19] border border-white/10 text-center px-1 py-1`} style={color ? { color, fontWeight: 700 } : undefined} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testId} placeholder={placeholder} />;
}

// Lap-Zeit Parser: "1:23.456", "1:23:45.678", "23.456" → Sekunden
const parseT = (s) => {
  if (!s) return Infinity;
  const parts = String(s).trim().split(":");
  let total = 0;
  for (const p of parts) total = total * 60 + parseFloat(p);
  return isNaN(total) || total <= 0 ? Infinity : total;
};

// Findet pro Feld den kleinsten Zeitwert in einer Zeilenliste
function fastestPerField(rows, session) {
  const fields = session === "quali"
    ? ["quali_time", "quali_s1", "quali_s2", "quali_s3"]
    : session === "sprint"
    ? ["sprint_best"]
    : ["race_best"];
  const result = {};
  for (const f of fields) {
    let best = Infinity;
    for (const r of rows || []) {
      const t = parseT(r?.[f]);
      if (t < best) best = t;
    }
    result[f] = best;
  }
  return result;
}

function SessionTable({ session, rows, driverById, teamById, teamsAll, driversAll, update, replaceDriver }) {
  const posKey = session === "quali" ? "qualifying_position" : session === "sprint" ? "sprint_position" : "finishing_position";
  const sortedRows = [...rows].sort((a, b) => {
    const pa = parseInt(a?.[posKey]); const pb = parseInt(b?.[posKey]);
    const va = Number.isFinite(pa) ? pa : Infinity;
    const vb = Number.isFinite(pb) ? pb : Infinity;
    return va - vb;
  });
  // Schnellste Sektoren/Runden → pink markieren
  const fastest = fastestPerField(rows, session);
  const isFastest = (field, val) => val && parseT(val) !== Infinity && parseT(val) === fastest[field];
  const PINK = "#F472B6"; // tailwind pink-400
  if (session === "quali") {
    return (
      <table className="w-full text-xs" data-testid="results-table-quali">
        <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2 w-14">Pos</th>
            <th className="text-left px-3 py-2 min-w-[180px]">Driver</th>
            <th className="text-left px-3 py-2 min-w-[140px]">Team</th>
            <th className="text-center px-2 py-2">Time</th>
            <th className="text-center px-2 py-2">GAP</th>
            <th className="text-center px-2 py-2">S1</th>
            <th className="text-center px-2 py-2">S2</th>
            <th className="text-center px-2 py-2">S3</th>
            <th className="text-center px-2 py-2 w-10" title="Reservefahrer · Punkte zählen nur für Team">Res</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(r => {
            const d = driverById[r.driver_id] || {};
            const effectiveTeamId = r.team_id || d.team_id;
            const tm = teamById[effectiveTeamId] || {};
            return (
              <tr key={r.driver_id} className={`border-t border-white/5 hover:bg-white/[0.02] ${r.is_reserve ? "bg-amber-500/5" : ""}`} data-testid={`row-quali-${r.driver_id}`}>
                <td className="px-2 py-1.5"><NumIn value={r.qualifying_position} w="w-12" onChange={v => update(r.driver_id,{qualifying_position:v})} testId={`q-pos-${r.driver_id}`}/></td>
                <td className="px-3 py-1.5 font-bold">
                  {d.name} <span className="text-neutral-500">#{d.number}</span>
                  {r.is_reserve && <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400 align-middle">Ersatzfahrer</span>}
                </td>
                <td className="px-3 py-1.5">
                  {r.is_reserve ? (
                    <select
                      value={effectiveTeamId || ""}
                      onChange={e => update(r.driver_id, { team_id: e.target.value || null })}
                      className="bg-[#0B0F19] border border-white/10 px-1.5 py-1 text-xs"
                      style={{ color: tm.color }}
                      data-testid={`reserve-team-quali-${r.driver_id}`}
                      title="Team für diesen Renneinsatz wählen"
                    >
                      <option value="">— Team wählen —</option>
                      {(teamsAll || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  ) : (
                    <span style={{ color: tm.color }}>{tm.name}</span>
                  )}
                </td>
                <td className="px-2 py-1.5"><TxtIn value={r.quali_time} color={isFastest("quali_time", r.quali_time) ? PINK : undefined} onChange={v => update(r.driver_id,{quali_time:v})} placeholder="1:23.456" testId={`q-time-${r.driver_id}`}/></td>
                <td className="px-2 py-1.5"><TxtIn value={r.quali_gap} w="w-16" onChange={v => update(r.driver_id,{quali_gap:v})} placeholder="+0.123" testId={`q-gap-${r.driver_id}`}/></td>
                <td className="px-2 py-1.5"><TxtIn value={r.quali_s1} w="w-16" color={isFastest("quali_s1", r.quali_s1) ? PINK : undefined} onChange={v => update(r.driver_id,{quali_s1:v})} testId={`q-s1-${r.driver_id}`}/></td>
                <td className="px-2 py-1.5"><TxtIn value={r.quali_s2} w="w-16" color={isFastest("quali_s2", r.quali_s2) ? PINK : undefined} onChange={v => update(r.driver_id,{quali_s2:v})} testId={`q-s2-${r.driver_id}`}/></td>
                <td className="px-2 py-1.5"><TxtIn value={r.quali_s3} w="w-16" color={isFastest("quali_s3", r.quali_s3) ? PINK : undefined} onChange={v => update(r.driver_id,{quali_s3:v})} testId={`q-s3-${r.driver_id}`}/></td>
                <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r.is_reserve} onChange={e => update(r.driver_id,{is_reserve: e.target.checked})} data-testid={`reserve-quali-${r.driver_id}`} title="Reservefahrer · zählt nur für Teamwertung"/></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
  // race or sprint
  const isSpr = session === "sprint";
  const k = (n) => isSpr ? `sprint_${n}` : n === "finishing_position" ? "finishing_position" : `race_${n}`;
  const posField = isSpr ? "sprint_position" : "finishing_position";
  const timeField = isSpr ? "sprint_time" : "race_time";
  const penField = isSpr ? "sprint_penalty" : "race_penalty";
  const bestField = isSpr ? "sprint_best" : "race_best";
  const lapsField = isSpr ? "sprint_laps" : "race_laps";
  const gridField = isSpr ? "sprint_grid" : "race_grid";
  const stopsField = isSpr ? "sprint_stops" : "race_stops";
  const ptsField = isSpr ? "sprint_points" : "points";
  const flField = isSpr ? "sprint_fastest_lap" : "fastest_lap";
  return (
    <table className="w-full text-xs" data-testid={`results-table-${session}`}>
      <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
        <tr>
          <th className="text-left px-3 py-2 w-14">Pos</th>
          <th className="text-left px-3 py-2 min-w-[170px]">Driver</th>
          <th className="text-left px-3 py-2 min-w-[140px]">Team</th>
          <th className="text-center px-2 py-2">Time</th>
          <th className="text-center px-2 py-2">Penalty</th>
          <th className="text-center px-2 py-2">Best</th>
          <th className="text-center px-2 py-2 w-12">Laps</th>
          <th className="text-center px-2 py-2 w-12">Grid</th>
          <th className="text-center px-2 py-2 w-12">Stops</th>
          <th className="text-center px-2 py-2 w-14">Points</th>
          <th className="text-center px-2 py-2 w-10">FL</th>
          <th className="text-center px-2 py-2 w-12">DOTD</th>
          <th className="text-center px-2 py-2 w-10">DNF</th>
          <th className="text-center px-2 py-2 w-10">PP</th>
          <th className="text-center px-2 py-2 w-10" title="Reservefahrer · Punkte zählen nur für Team">Res</th>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(r => {
          const d = driverById[r.driver_id] || {};
          const effectiveTeamId = r.team_id || d.team_id;
          const tm = teamById[effectiveTeamId] || {};
          return (
            <tr key={r.driver_id} className={`border-t border-white/5 hover:bg-white/[0.02] ${r.is_reserve ? "bg-amber-500/5" : ""}`} data-testid={`row-${session}-${r.driver_id}`}>
              <td className="px-2 py-1.5"><NumIn value={r[posField]} w="w-12" onChange={v => update(r.driver_id,{[posField]: v ? parseInt(v) : null})}/></td>
              <td className="px-3 py-1.5 font-bold align-top">
                <div className="flex items-center gap-1.5">
                  <select
                    value={r.driver_id}
                    onChange={e => replaceDriver(r.driver_id, e.target.value)}
                    className="bg-[#0B0F19] border border-white/10 px-1.5 py-1 text-xs max-w-[180px]"
                    data-testid={`driver-select-${session}-${r.driver_id}`}
                    title="Zugeordneten Fahrer ändern (z. B. wenn OCR sich vertan hat)"
                  >
                    <option value={r.driver_id}>{d.name || "—"} {d.number ? `#${d.number}` : ""}</option>
                    {(driversAll || [])
                      .filter(x => x.id !== r.driver_id && !rows.some(rr => rr.driver_id === x.id))
                      .sort((a,b) => (a.name || "").localeCompare(b.name || ""))
                      .map(x => <option key={x.id} value={x.id}>{x.name}{x.ingame_name ? ` · "${x.ingame_name}"` : ""} {x.number ? `#${x.number}` : ""}</option>)}
                  </select>
                  {r.is_reserve && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400">Ersatzfahrer</span>}
                </div>
              </td>
              <td className="px-3 py-1.5">
                <select
                  value={effectiveTeamId || ""}
                  onChange={e => update(r.driver_id, { team_id: e.target.value || null })}
                  className="bg-[#0B0F19] border border-white/10 px-1.5 py-1 text-xs"
                  style={{ color: tm.color }}
                  data-testid={`team-${session}-${r.driver_id}`}
                  title={r.is_reserve ? "Reservefahrer · Team für dieses Rennen wählen" : "Team-Zuordnung für dieses Rennen überschreiben (z. B. wenn Fahrer das Team gewechselt hat)"}
                >
                  <option value="">— Standard-Team —</option>
                  {(teamsAll || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </td>
              <td className="px-2 py-1.5"><TxtIn value={r[timeField]} onChange={v => update(r.driver_id,{[timeField]:v})} placeholder="1:23:45.678"/></td>
              <td className="px-2 py-1.5"><TxtIn value={r[penField]} w="w-16" onChange={v => update(r.driver_id,{[penField]:v})} placeholder="+5.0"/></td>
              <td className="px-2 py-1.5"><TxtIn value={r[bestField]} w="w-20" color={isFastest(bestField, r[bestField]) ? PINK : undefined} onChange={v => update(r.driver_id,{[bestField]:v})} placeholder="1:23.456"/></td>
              <td className="px-2 py-1.5"><NumIn value={r[lapsField]} w="w-10" onChange={v => update(r.driver_id,{[lapsField]: v ? parseInt(v):null})}/></td>
              <td className="px-2 py-1.5"><NumIn value={r[gridField]} w="w-10" onChange={v => update(r.driver_id,{[gridField]: v ? parseInt(v):null})}/></td>
              <td className="px-2 py-1.5"><NumIn value={r[stopsField]} w="w-10" onChange={v => update(r.driver_id,{[stopsField]: v ? parseInt(v):null})}/></td>
              <td className="px-2 py-1.5">
                <input type="number" step="0.5" className="w-14 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[ptsField] ?? 0} onChange={e => update(r.driver_id,{[ptsField]: parseFloat(e.target.value) || 0})}/>
              </td>
              <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r[flField]} onChange={e => update(r.driver_id,{[flField]: e.target.checked})}/></td>
              <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r.dotd} onChange={e => update(r.driver_id,{dotd: e.target.checked})}/></td>
              <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r.dnf} onChange={e => update(r.driver_id,{dnf: e.target.checked})}/></td>
              <td className="px-2 py-1.5"><NumIn value={r.penalty_points} w="w-10" onChange={v => update(r.driver_id,{penalty_points: parseInt(v)||0})}/></td>
              <td className="px-2 py-1.5 text-center">
                <input type="checkbox" checked={!!r.is_reserve} onChange={e => update(r.driver_id,{is_reserve: e.target.checked})} data-testid={`reserve-${session}-${r.driver_id}`} title="Reservefahrer · zählt nur für Teamwertung"/>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ============= OCR PREVIEW TABLE ============= */
function OcrPreviewTable({ preview, updateOcrRow, renameAutoCreated, deleteAutoCreated }) {
  const stype = preview.session_type || "race";
  const choices = preview.driver_choices || [];
  const baseRows = preview.results || [];
  const [driverSearch, setDriverSearch] = useState("");
  const posKey = stype === "quali" ? "qualifying_position" : stype === "sprint" ? "sprint_position" : "finishing_position";
  // Mit Original-Index zippen und nach Position sortieren
  const rows = baseRows
    .map((r, originalIdx) => ({ r, originalIdx }))
    .sort((a, b) => {
      const pa = parseInt(a.r?.[posKey]); const pb = parseInt(b.r?.[posKey]);
      const va = Number.isFinite(pa) ? pa : Infinity;
      const vb = Number.isFinite(pb) ? pb : Infinity;
      return va - vb;
    });

  const onDriverChange = (idx, value) => {
    updateOcrRow(idx, { driver_id: value || null, matched: !!value });
  };

  // Fahrer filtern nach Suche (Name, Ingame, Nummer) — Compact-Matching für Sonderzeichen-Toleranz
  const filteredChoices = (() => {
    const q = driverSearch.trim();
    if (!q) return choices;
    const qn = normalizeName(q);
    const qc = normalizeCompact(q);
    return choices.filter(c => {
      const nm = normalizeName(c.name);
      const nc = normalizeCompact(c.name);
      const ig = normalizeName(c.ingame_name || "");
      const ic = normalizeCompact(c.ingame_name || "");
      return (qn && (nm.includes(qn) || ig.includes(qn))) ||
             (qc && (nc.includes(qc) || ic.includes(qc))) ||
             String(c.number || "").includes(q);
    });
  })();

  // Nach Team gruppieren
  const choicesByTeam = (() => {
    const map = new Map();
    filteredChoices.forEach(c => {
      const label = c.team && c.team.trim() ? c.team : "Ohne Team / Global-Reserve";
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(c);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => {
        if (a.startsWith("Ohne Team")) return 1;
        if (b.startsWith("Ohne Team")) return -1;
        return a.localeCompare(b);
      });
  })();

  const renderOptions = (currentId) => (
    <>
      {/* Aktuell gewählter Fahrer immer sichtbar halten, auch wenn Filter greift */}
      {currentId && !filteredChoices.some(c => c.id === currentId) && (() => {
        const cur = choices.find(c => c.id === currentId);
        if (!cur) return null;
        return (
          <option value={cur.id}>
            ★ {cur.name}
            {cur.ingame_name ? ` · "${cur.ingame_name}"` : ""}
            {cur.number ? ` · #${cur.number}` : ""}
            {cur.is_reserve_driver ? " · Reserve" : ""}
          </option>
        );
      })()}
      {choicesByTeam.map(([teamLabel, list]) => (
        <optgroup key={teamLabel} label={teamLabel}>
          {list.map(c => (
            <option key={c.id} value={c.id}>
              {c.is_reserve_driver ? "◆ " : ""}{c.name}
              {c.ingame_name ? ` · "${c.ingame_name}"` : ""}
              {c.number ? ` · #${c.number}` : ""}
              {c.is_rookie ? " · Rookie" : ""}
              {c.is_active === false ? " · Inaktiv" : ""}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );

  const SearchBar = (
    <div className="mb-3 flex items-center gap-3 sticky top-0 z-10 bg-[#0B0F19] py-1">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">Fahrer-Suche</span>
      <input
        type="text"
        placeholder="Name, Ingame-Name oder Nummer — leer = alle"
        value={driverSearch}
        onChange={e => setDriverSearch(e.target.value)}
        className="flex-1 bg-[#0B0F19] border border-white/15 focus:border-[#A855F7]/60 outline-none px-2.5 py-1.5 text-xs text-white placeholder:text-neutral-600"
        data-testid="ocr-driver-search"
      />
      {driverSearch && (
        <button onClick={() => setDriverSearch("")} className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white" data-testid="ocr-driver-search-clear">
          Zurücksetzen
        </button>
      )}
      <span className="text-[10px] text-neutral-500 shrink-0">
        {filteredChoices.length} / {choices.length} Fahrer
      </span>
    </div>
  );

  if (stype === "quali") {
    return (
      <>
        {SearchBar}
        <table className="w-full text-xs" data-testid="ocr-preview-table-quali">
        <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
          <tr>
            <th className="text-left px-2 py-2 w-12">Pos</th>
            <th className="text-left px-2 py-2 min-w-[150px]">Erkannt</th>
            <th className="text-left px-2 py-2 min-w-[200px]">Fahrer zuordnen</th>
            <th className="text-center px-2 py-2">Time</th>
            <th className="text-center px-2 py-2">GAP</th>
            <th className="text-center px-2 py-2">S1</th>
            <th className="text-center px-2 py-2">S2</th>
            <th className="text-center px-2 py-2">S3</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ r, originalIdx: i }) => (
            <tr key={i} className={`border-t border-white/5 ${!r.matched ? "bg-red-500/10" : r.is_reserve_hint ? "bg-amber-500/10" : ""}`} data-testid={`ocr-row-${i}`}>
              <td className="px-2 py-1.5 font-bold">{r.qualifying_position ?? "—"}</td>
              <td className="px-2 py-1.5 text-neutral-300">
                {r.driver_name_raw || "—"}
                {r.auto_created && (
                  <>
                    <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">Neu angelegt</span>
                    <button type="button" onClick={() => renameAutoCreated(i, r.driver_id, r.driver_name_raw)} className="ml-1.5 p-0.5 text-neutral-400 hover:text-cyan-300" title="Umbenennen" data-testid={`ocr-rename-${i}`}><Edit2 className="w-3 h-3 inline" /></button>
                    <button type="button" onClick={() => deleteAutoCreated(i, r.driver_id, r.driver_name_raw)} className="ml-0.5 p-0.5 text-neutral-400 hover:text-red-400" title="Löschen" data-testid={`ocr-delete-${i}`}><Trash2 className="w-3 h-3 inline" /></button>
                  </>
                )}
                {r.is_reserve_hint && !r.auto_created && <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400">Reserve</span>}
              </td>
              <td className="px-2 py-1.5">
                <select className="w-full bg-[#0B0F19] border border-white/10 px-1 py-1" value={r.driver_id || ""} onChange={e => onDriverChange(i, e.target.value)} data-testid={`ocr-driver-${i}`}>
                  <option value="">— nicht zuordnen —</option>
                  {renderOptions(r.driver_id)}
                </select>
              </td>
              <td className="px-2 py-1.5"><input className="w-24 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r.quali_time || ""} onChange={e => updateOcrRow(i, { quali_time: e.target.value })} /></td>
              <td className="px-2 py-1.5"><input className="w-16 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r.quali_gap || ""} onChange={e => updateOcrRow(i, { quali_gap: e.target.value })} /></td>
              <td className="px-2 py-1.5"><input className="w-16 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r.quali_s1 || ""} onChange={e => updateOcrRow(i, { quali_s1: e.target.value })} /></td>
              <td className="px-2 py-1.5"><input className="w-16 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r.quali_s2 || ""} onChange={e => updateOcrRow(i, { quali_s2: e.target.value })} /></td>
              <td className="px-2 py-1.5"><input className="w-16 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r.quali_s3 || ""} onChange={e => updateOcrRow(i, { quali_s3: e.target.value })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      </>
    );
  }

  const isSpr = stype === "sprint";
  const posField = isSpr ? "sprint_position" : "finishing_position";
  const timeField = isSpr ? "sprint_time" : "race_time";
  const penField = isSpr ? "sprint_penalty" : "race_penalty";
  const bestField = isSpr ? "sprint_best" : "race_best";
  const lapsField = isSpr ? "sprint_laps" : "race_laps";
  const gridField = isSpr ? "sprint_grid" : "race_grid";
  const stopsField = isSpr ? "sprint_stops" : "race_stops";
  const ptsField = isSpr ? "sprint_points" : "points";
  const flField = isSpr ? "sprint_fastest_lap" : "fastest_lap";

  return (
    <>
      {SearchBar}
      <table className="w-full text-xs" data-testid={`ocr-preview-table-${stype}`}>
      <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
        <tr>
          <th className="text-left px-2 py-2 w-12">Pos</th>
          <th className="text-left px-2 py-2 min-w-[140px]">Erkannt</th>
          <th className="text-left px-2 py-2 min-w-[200px]">Fahrer zuordnen</th>
          <th className="text-center px-2 py-2">Time</th>
          <th className="text-center px-2 py-2">Pen</th>
          <th className="text-center px-2 py-2">Best</th>
          <th className="text-center px-2 py-2 w-12">Laps</th>
          <th className="text-center px-2 py-2 w-12">Grid</th>
          <th className="text-center px-2 py-2 w-12">Stops</th>
          <th className="text-center px-2 py-2 w-14">Pts</th>
          <th className="text-center px-2 py-2 w-10">FL</th>
          <th className="text-center px-2 py-2 w-12">DOTD</th>
          <th className="text-center px-2 py-2 w-10">DNF</th>
          <th className="text-center px-2 py-2 w-10">PP</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ r, originalIdx: i }) => (
          <tr key={i} className={`border-t border-white/5 ${!r.matched ? "bg-red-500/10" : r.is_reserve_hint ? "bg-amber-500/10" : ""}`} data-testid={`ocr-row-${i}`}>
            <td className="px-2 py-1.5 font-bold">{r[posField] ?? "—"}</td>
            <td className="px-2 py-1.5 text-neutral-300">
              {r.driver_name_raw || "—"}
              {r.auto_created && (
                <>
                  <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">Neu angelegt</span>
                  <button type="button" onClick={() => renameAutoCreated(i, r.driver_id, r.driver_name_raw)} className="ml-1.5 p-0.5 text-neutral-400 hover:text-cyan-300" title="Umbenennen" data-testid={`ocr-rename-${i}`}><Edit2 className="w-3 h-3 inline" /></button>
                  <button type="button" onClick={() => deleteAutoCreated(i, r.driver_id, r.driver_name_raw)} className="ml-0.5 p-0.5 text-neutral-400 hover:text-red-400" title="Löschen" data-testid={`ocr-delete-${i}`}><Trash2 className="w-3 h-3 inline" /></button>
                </>
              )}
              {r.is_reserve_hint && !r.auto_created && <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400">Reserve</span>}
            </td>
            <td className="px-2 py-1.5">
              <select className="w-full bg-[#0B0F19] border border-white/10 px-1 py-1" value={r.driver_id || ""} onChange={e => onDriverChange(i, e.target.value)} data-testid={`ocr-driver-${i}`}>
                <option value="">— nicht zuordnen —</option>
                {renderOptions(r.driver_id)}
              </select>
            </td>
            <td className="px-2 py-1.5"><input className="w-24 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[timeField] || ""} onChange={e => updateOcrRow(i, { [timeField]: e.target.value })} /></td>
            <td className="px-2 py-1.5"><input className="w-16 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[penField] || ""} onChange={e => updateOcrRow(i, { [penField]: e.target.value })} /></td>
            <td className="px-2 py-1.5"><input className="w-20 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[bestField] || ""} onChange={e => updateOcrRow(i, { [bestField]: e.target.value })} /></td>
            <td className="px-2 py-1.5"><input type="number" className="w-10 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[lapsField] ?? ""} onChange={e => updateOcrRow(i, { [lapsField]: e.target.value ? parseInt(e.target.value) : null })} /></td>
            <td className="px-2 py-1.5"><input type="number" className="w-10 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[gridField] ?? ""} onChange={e => updateOcrRow(i, { [gridField]: e.target.value ? parseInt(e.target.value) : null })} /></td>
            <td className="px-2 py-1.5"><input type="number" className="w-10 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[stopsField] ?? ""} onChange={e => updateOcrRow(i, { [stopsField]: e.target.value ? parseInt(e.target.value) : null })} /></td>
            <td className="px-2 py-1.5"><input type="number" step="0.5" className="w-14 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r[ptsField] ?? 0} onChange={e => updateOcrRow(i, { [ptsField]: parseFloat(e.target.value) || 0 })} /></td>
            <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r[flField]} onChange={e => updateOcrRow(i, { [flField]: e.target.checked })} /></td>
            <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r.dotd} onChange={e => updateOcrRow(i, { dotd: e.target.checked })} /></td>
            <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r.dnf} onChange={e => updateOcrRow(i, { dnf: e.target.checked })} /></td>
            <td className="px-2 py-1.5"><input type="number" className="w-10 bg-[#0B0F19] border border-white/10 text-center px-1 py-1" value={r.penalty_points ?? 0} onChange={e => updateOcrRow(i, { penalty_points: parseInt(e.target.value) || 0 })} /></td>
          </tr>
        ))}
      </tbody>
    </table>
    </>
  );
}

/* ============= SPLITS ADMIN ============= */
const emptySplit = { key: "", name: "", color: "#00E5FF", sort_order: 0 };

function SplitsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptySplit);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/splits").then(({ data }) => {
    setItems(data || []);
    invalidateSplits(data);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        key: (form.key || "").trim().toLowerCase().replace(/\s+/g, "-"),
        name: form.name.trim(),
        color: form.color || "#00E5FF",
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (!payload.key) { toast.error('Bitte einen Key vergeben (z. B. "pro", "rookie")'); return; }
      if (!payload.name) { toast.error("Bitte einen Anzeigenamen vergeben"); return; }
      if (editingId) { await api.put(`/splits/${editingId}`, payload); toast.success("Split aktualisiert"); }
      else { await api.post("/splits", payload); toast.success("Split erstellt"); }
      setForm(emptySplit); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!confirm("Split wirklich löschen?")) return;
    try {
      await api.delete(`/splits/${id}`);
      toast.success("Split gelöscht");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="splits-admin">
      <form onSubmit={save} className="card-surface p-5 md:col-span-1" data-testid="split-form">
        <div className="font-display font-black italic uppercase text-lg">{editingId ? "Split bearbeiten" : "Neuer Split"}</div>
        <p className="text-xs text-neutral-500 mt-1">Splits wie z. B. &bdquo;Pro&ldquo;, &bdquo;Semi-Pro&ldquo;, &bdquo;Rookie&ldquo; — wird in Tabs, Karten und Wertungen verwendet.</p>
        <Field label="Anzeige-Name">
          <input className={TXT} required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="split-name" placeholder="Formula Champions" />
        </Field>
        <Field label="Key (Slug, intern, klein, ohne Leerzeichen)">
          <input className={TXT} required value={form.key} onChange={e => setForm({...form, key: e.target.value})} data-testid="split-key" placeholder="champions" />
        </Field>
        <Field label="Farbe">
          <div className="flex items-center gap-2">
            <input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="w-12 h-9 bg-transparent border border-white/10 cursor-pointer" data-testid="split-color-picker" />
            <input className={TXT} value={form.color} onChange={e => setForm({...form, color: e.target.value})} data-testid="split-color-hex" placeholder="#00E5FF" />
          </div>
        </Field>
        <Field label="Sortier-Reihenfolge (klein = weiter vorne)">
          <input type="number" className={TXT} value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} data-testid="split-order" />
        </Field>
        <div className="flex gap-2 mt-4">
          <button type="submit" className="btn-primary chamfer-sm" data-testid="split-save"><Plus className="w-4 h-4" />{editingId ? "Speichern" : "Erstellen"}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(emptySplit); }} className="btn-ghost text-xs">Abbrechen</button>}
        </div>
      </form>
      <div className="md:col-span-2 space-y-2">
        {items.map(s => (
          <div key={s.id} className="border border-white/10 p-4 flex items-center gap-4" data-testid={`split-row-${s.key}`}>
            <span className="inline-block w-3 h-12" style={{ background: s.color }} />
            <div className="flex-1">
              <div className="font-display font-black italic uppercase text-lg leading-none" style={{ color: s.color }}>{s.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                Key: <span className="text-neutral-300">{s.key}</span> · Reihenfolge: {s.sort_order ?? 0}
              </div>
            </div>
            <button onClick={() => { setEditingId(s.id); setForm({ ...emptySplit, ...s }); }} className="btn-ghost text-xs" data-testid={`split-edit-${s.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
            <button onClick={() => del(s.id)} className="btn-ghost text-xs text-red-400" data-testid={`split-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-neutral-500 text-sm">Keine Splits.</div>}
      </div>
    </div>
  );
}

/* ============= TIERS / WERTUNGS-STUFEN ============= */
const TIER_ORDER = ["bronze", "silver", "gold", "whitegold", "rookie", "champion"];
const TIER_DEFAULT_LABELS = {
  bronze: "Bronze", silver: "Silber", gold: "Gold", whitegold: "White Gold", rookie: "Rookie", champion: "Champion",
};
const TIER_TINT = {
  bronze: "#CD7F32", silver: "#C0C0C0", gold: "#D4AF37", whitegold: "#FFE9A8", rookie: "#94A3B8", champion: "#A855F7",
};
const TIER_HINTS = {
  rookie: 'Wird bei Fahrern mit Toggle „Neuer Fahrer" verwendet (Karte zeigt „??").',
  champion: 'Wird bei Fahrern mit Toggle „Champion" verwendet (Krone-Badge oben links).',
};

function tiersReducer(state, action) {
  if (action.type === "set") return action.tiers;
  if (action.type === "patch") {
    return state.map((t) => (t.key === action.key ? { ...t, ...action.patch } : t));
  }
  return state;
}

function TiersAdmin() {
  const [tiers, dispatch] = useReducer(tiersReducer, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tier-config");
      const ordered = TIER_ORDER.map((k) => {
        const found = (data.tiers || []).find((t) => t.key === k);
        return found || { key: k, label: TIER_DEFAULT_LABELS[k], min_ovr: 0, background_url: "", accent_color: "" };
      });
      dispatch({ type: "set", tiers: ordered });
    } catch (e) {
      toast.error("Tier-Konfiguration konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateTier = (key, patch) => dispatch({ type: "patch", key, patch });

  const save = async () => {
    // Sortierung & Plausibilität: min_ovr für bronze..whitegold aufsteigend
    const ovrTiers = tiers.filter((t) => !["rookie", "champion"].includes(t.key));
    for (let i = 1; i < ovrTiers.length; i++) {
      if ((ovrTiers[i].min_ovr || 0) <= (ovrTiers[i - 1].min_ovr || 0)) {
        toast.error(`„${ovrTiers[i].label}" muss eine höhere Mindest-OVR als „${ovrTiers[i - 1].label}" haben.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { tiers: tiers.map((t) => ({
        key: t.key,
        label: t.label || TIER_DEFAULT_LABELS[t.key],
        min_ovr: parseInt(t.min_ovr) || 0,
        background_url: t.background_url || "",
        accent_color: t.accent_color || "",
      })) };
      const { data } = await api.put("/tier-config", payload);
      invalidateTiers(data.tiers);
      toast.success("Wertungs-Stufen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-neutral-400 text-sm">Lade…</div>;

  // Zeige max_ovr Anzeige: nächste Stufe -1, letzte Stufe = ∞
  const ovrTiers = tiers.filter((t) => !["rookie", "champion"].includes(t.key));
  const rangeFor = (key) => {
    if (key === "rookie" || key === "champion") return "—";
    const idx = ovrTiers.findIndex((t) => t.key === key);
    const min = ovrTiers[idx]?.min_ovr ?? 0;
    const next = ovrTiers[idx + 1];
    return next ? `${min} – ${(next.min_ovr || 0) - 1}` : `${min} +`;
  };

  return (
    <div className="space-y-5" data-testid="tiers-admin">
      <div className="border border-white/10 bg-white/[0.02] p-4">
        <div className="font-display font-black italic uppercase text-lg">Wertungs-Stufen</div>
        <p className="text-sm text-neutral-400 mt-1">
          Lege fest, ab welcher Gesamtwertung (OVR) eine Stufe greift und welcher Hintergrund auf der Fahrerkarte angezeigt wird.
          Rookie-Karten (&bdquo;??&ldquo;) nutzen immer den Rookie-Hintergrund unabhängig von der OVR.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {tiers.map((t) => {
          const isSpecial = t.key === "rookie" || t.key === "champion";
          const tint = (t.accent_color && t.accent_color.match(/^#/)) ? t.accent_color : TIER_TINT[t.key];
          return (
          <div key={t.key} className="border border-white/10 p-4 bg-[#0B0F19]" data-testid={`tier-row-${t.key}`}>
            <div className="flex items-center gap-3">
              <span className="inline-block w-3 h-8" style={{ background: tint }} />
              <div className="flex-1">
                <div className="font-display font-black italic uppercase text-base leading-none" style={{ color: tint }}>
                  {t.label}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                  Bereich: {rangeFor(t.key)}
                </div>
              </div>
            </div>

            {TIER_HINTS[t.key] && (
              <div className="text-[11px] text-neutral-400 mt-2 border-l-2 pl-2" style={{ borderColor: `${tint}80` }}>
                {TIER_HINTS[t.key]}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mt-3">
              <Field label="Anzeige-Name">
                <input className={TXT} value={t.label || ""} onChange={(e) => updateTier(t.key, { label: e.target.value })} data-testid={`tier-label-${t.key}`} />
              </Field>
              <Field label={isSpecial ? `Mind. OVR (für ${t.label} irrelevant)` : "Mind. OVR"}>
                <input
                  type="number"
                  className={TXT}
                  value={t.min_ovr ?? 0}
                  disabled={isSpecial}
                  onChange={(e) => updateTier(t.key, { min_ovr: parseInt(e.target.value) || 0 })}
                  data-testid={`tier-min-${t.key}`}
                />
              </Field>
            </div>

            <Field label="Akzentfarbe (HEX · leer = Standard)">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={t.accent_color || TIER_TINT[t.key]}
                  onChange={(e) => updateTier(t.key, { accent_color: e.target.value })}
                  className="w-12 h-9 bg-transparent border border-white/10 cursor-pointer"
                  data-testid={`tier-accent-color-${t.key}`}
                />
                <input
                  className={TXT}
                  placeholder="#A855F7"
                  value={t.accent_color || ""}
                  onChange={(e) => updateTier(t.key, { accent_color: e.target.value })}
                  data-testid={`tier-accent-hex-${t.key}`}
                />
                {t.accent_color && (
                  <button type="button" onClick={() => updateTier(t.key, { accent_color: "" })} className="btn-ghost text-[10px]" data-testid={`tier-accent-reset-${t.key}`}>Reset</button>
                )}
              </div>
            </Field>

            <Field label="Hintergrund-Bild (PNG/JPG · ~2:3, wird zugeschnitten)">
              <ImageUploader
                value={t.background_url || ""}
                onChange={(v) => updateTier(t.key, { background_url: v })}
                testIdPrefix={`tier-bg-${t.key}`}
                targetW={600}
                targetH={900}
                cropFrom="center"
                folder="teams"
                hint="600×900 · Upload zu Cloudinary"
                previewSize="w-20 h-28"
              />
            </Field>
          </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button onClick={save} disabled={saving} className="btn-primary chamfer-sm disabled:opacity-50" data-testid="tiers-save">
          <Plus className="w-4 h-4" /> {saving ? "Speichern…" : "Stufen speichern"}
        </button>
        <button onClick={load} className="btn-ghost text-xs" data-testid="tiers-reload">Verwerfen / Neu laden</button>
      </div>
    </div>
  );
}


/* ============= SITE IDENTITY (im Sonstiges-Tab oben) ============= */
function SiteIdentityAdmin() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data || {}));
  }, []);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        site_title: s.site_title || "",
        favicon_url: s.favicon_url || "",
        logo_url: s.logo_url || "",
        theme_color: s.theme_color || "#00E5FF",
        meta_description: s.meta_description || "",
        og_image: s.og_image || "",
        maintenance_mode: !!s.maintenance_mode,
        maintenance_message: s.maintenance_message || "",
        maintenance_start_at: s.maintenance_start_at || "",
        maintenance_end_at: s.maintenance_end_at || "",
        prelaunch_mode: !!s.prelaunch_mode,
        prelaunch_eyebrow: s.prelaunch_eyebrow ?? "",
        prelaunch_title: s.prelaunch_title || "",
        prelaunch_message: s.prelaunch_message || "",
        prelaunch_target: s.prelaunch_target || "",
        social_twitch_url: s.social_twitch_url || "",
        social_discord_url: s.social_discord_url || "",
        social_youtube_url: s.social_youtube_url || "",
        social_twitter_url: s.social_twitter_url || "",
        social_instagram_url: s.social_instagram_url || "",
        countdown_enabled: !!s.countdown_enabled,
        countdown_title: s.countdown_title || "",
        countdown_target: s.countdown_target || "",
        countdown_url: s.countdown_url || "",
        ga_measurement_id: (s.ga_measurement_id || "").trim(),
        cookie_banner_script_url: (s.cookie_banner_script_url || "").trim(),
      });
      toast.success("Site-Einstellungen gespeichert");
      // Neuladen, damit Layout/HomePage frische Werte sehen
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  if (!s) return <div className="text-neutral-500 text-sm">Lädt…</div>;

  // Hilfsfunktion: ISO ↔ datetime-local
  const dtToLocal = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d)) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };
  const localToIso = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d) ? "" : d.toISOString();
  };

  return (
    <div className="space-y-6">
      {/* SITE-IDENTITÄT */}
      <div className="card-surface p-5" data-testid="site-identity">
        <div className="font-display font-black italic uppercase text-lg">Site-Identität</div>
        <p className="text-xs text-neutral-500 mt-1 mb-4">Wirkt sich auf Browser-Tab, Header-Logo und Header-Farbe aus.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Site-Titel (Browser-Tab)">
            <input className={TXT} value={s.site_title || ""} onChange={e => set("site_title", e.target.value)} placeholder="Sector Four · Soest GP" data-testid="site-title" />
          </Field>
          <Field label="Theme-Farbe (Mobile Browser-Adressleiste)">
            <div className="flex items-center gap-2">
              <input type="color" value={s.theme_color || "#00E5FF"} onChange={e => set("theme_color", e.target.value)} className="h-9 w-12 bg-transparent border border-white/10 cursor-pointer" data-testid="site-theme-color" />
              <input className={TXT} value={s.theme_color || "#00E5FF"} onChange={e => set("theme_color", e.target.value)} placeholder="#00E5FF" />
            </div>
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <Field label="Favicon (Browser-Tab-Icon, quadratisch)">
            <ImageUploader
              value={s.favicon_url || ""}
              onChange={v => set("favicon_url", v)}
              folder="site"
              targetW={256} targetH={256} cropFrom="center"
              hint="Empfohlen: 1:1 · wird auf 256×256 zugeschnitten"
              previewSize="w-14 h-14"
              testIdPrefix="site-favicon"
            />
          </Field>
          <Field label="Logo (ersetzt /brand/sector-four.png)">
            <ImageUploader
              value={s.logo_url || ""}
              onChange={v => set("logo_url", v)}
              folder="site"
              targetW={800} targetH={300} cropFrom="center"
              hint="Empfohlen: ~ 8:3 · transparenter Hintergrund"
              previewSize="w-32 h-12"
              testIdPrefix="site-logo"
            />
          </Field>
        </div>
      </div>

      {/* SEO / SOCIAL */}
      <div className="card-surface p-5" data-testid="site-seo">
        <div className="font-display font-black italic uppercase text-lg">SEO &amp; Social Sharing</div>
        <p className="text-xs text-neutral-500 mt-1 mb-4">Wird in Google-Suche und Link-Vorschauen (WhatsApp, Discord, X) angezeigt.</p>
        <Field label="Meta-Description (Kurz, max ~160 Zeichen)">
          <textarea className={TXT} rows={2} maxLength={250} value={s.meta_description || ""} onChange={e => set("meta_description", e.target.value)} placeholder="Sector Four — das Racing Journal der Soest Gaming League." data-testid="site-meta-desc" />
        </Field>
        <Field label="Open-Graph-Image (Vorschau beim Teilen, 1200×630)">
          <ImageUploader
            value={s.og_image || ""}
            onChange={v => set("og_image", v)}
            folder="site"
            targetW={1200} targetH={630} cropFrom="center"
            hint="Empfohlen: 1.91:1 · 1200×630"
            previewSize="w-32 h-[68px]"
            testIdPrefix="site-og"
          />
        </Field>
        <Field label="Google Analytics 4 Measurement-ID (optional)">
          <input
            className={TXT}
            type="text"
            value={s.ga_measurement_id || ""}
            onChange={e => set("ga_measurement_id", e.target.value)}
            placeholder="G-XXXXXXXXXX"
            data-testid="site-ga-id"
          />
          <p className="text-[11px] text-neutral-500 mt-1">
            Wenn gesetzt, wird das gtag.js-Script automatisch auf allen Seiten geladen. IP-Anonymisierung ist aktiv. Leerlassen = kein Tracking. Findest du in GA unter Verwaltung → Datenstreams → Web-Stream → Mess-ID.
          </p>
        </Field>
        <Field label="Cookie-Consent-Banner Script-URL (optional, z. B. CCM19)">
          <input
            className={TXT}
            type="url"
            value={s.cookie_banner_script_url || ""}
            onChange={e => set("cookie_banner_script_url", e.target.value)}
            placeholder="https://cloud.ccm19.de/app.js?apiKey=...&domain=..."
            data-testid="site-cookie-banner-url"
          />
          <p className="text-[11px] text-neutral-500 mt-1">
            Komplette Script-URL deines Consent-Tools eintragen (CCM19, Cookiebot, etc.). Wird automatisch auf allen Seiten mit <code>referrerpolicy="origin"</code> geladen. Leerlassen = kein Banner.
          </p>
        </Field>
      </div>

      {/* SOCIAL LINKS */}
      <div className="card-surface p-5" data-testid="site-social">
        <div className="font-display font-black italic uppercase text-lg">Social-Media-Links</div>
        <p className="text-xs text-neutral-500 mt-1 mb-4">Werden im Wartungs- und Pre-Launch-Footer als kleine Icons angezeigt. Leer = ausgeblendet.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Twitch URL">
            <input className={TXT} type="url" value={s.social_twitch_url || ""} onChange={e => set("social_twitch_url", e.target.value)} placeholder="https://twitch.tv/..." data-testid="site-social-twitch" />
          </Field>
          <Field label="Discord URL">
            <input className={TXT} type="url" value={s.social_discord_url || ""} onChange={e => set("social_discord_url", e.target.value)} placeholder="https://discord.gg/..." data-testid="site-social-discord" />
          </Field>
          <Field label="YouTube URL">
            <input className={TXT} type="url" value={s.social_youtube_url || ""} onChange={e => set("social_youtube_url", e.target.value)} placeholder="https://youtube.com/..." data-testid="site-social-youtube" />
          </Field>
          <Field label="X / Twitter URL">
            <input className={TXT} type="url" value={s.social_twitter_url || ""} onChange={e => set("social_twitter_url", e.target.value)} placeholder="https://x.com/..." data-testid="site-social-twitter" />
          </Field>
          <Field label="Instagram URL">
            <input className={TXT} type="url" value={s.social_instagram_url || ""} onChange={e => set("social_instagram_url", e.target.value)} placeholder="https://instagram.com/..." data-testid="site-social-instagram" />
          </Field>
        </div>
      </div>


      {/* COUNTDOWN */}
      <div className="card-surface p-5" data-testid="site-countdown">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display font-black italic uppercase text-lg">Countdown auf der Startseite</div>
            <p className="text-xs text-neutral-500 mt-1">Großer Countdown ganz oben — z. B. „Saison 6 startet in".</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-xs uppercase tracking-widest">
            <input type="checkbox" className="w-4 h-4 accent-cyan-400" checked={!!s.countdown_enabled} onChange={e => set("countdown_enabled", e.target.checked)} data-testid="site-countdown-enabled" />
            <span className={s.countdown_enabled ? "text-cyan-300" : "text-neutral-400"}>Aktiv</span>
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Titel">
            <input className={TXT} value={s.countdown_title || ""} onChange={e => set("countdown_title", e.target.value)} placeholder="Saison 6 startet in" data-testid="site-countdown-title" />
          </Field>
          <Field label="Ziel-Zeitpunkt">
            <input className={TXT} type="datetime-local" value={dtToLocal(s.countdown_target)} onChange={e => set("countdown_target", localToIso(e.target.value))} data-testid="site-countdown-target" />
          </Field>
        </div>
        <Field label="Optionaler Klick-Link (z. B. /kalender)">
          <input className={TXT} value={s.countdown_url || ""} onChange={e => set("countdown_url", e.target.value)} placeholder="/kalender" data-testid="site-countdown-url" />
        </Field>
      </div>

      {/* PRE-LAUNCH */}
      <div className="card-surface p-5" data-testid="site-prelaunch">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display font-black italic uppercase text-lg">Pre-Launch / Veröffentlichungs-Countdown</div>
            <p className="text-xs text-neutral-500 mt-1 max-w-xl">
              Sperrt die gesamte Site und zeigt einen Countdown bis zum Launch. <strong className="text-cyan-300">Wird nach Ablauf der Ziel-Zeit automatisch deaktiviert</strong> — kein manueller Eingriff nötig. Impressum + Datenschutz bleiben weiterhin erreichbar.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-xs uppercase tracking-widest">
            <input type="checkbox" className="w-4 h-4 accent-cyan-400" checked={!!s.prelaunch_mode} onChange={e => set("prelaunch_mode", e.target.checked)} data-testid="site-prelaunch-enabled" />
            <span className={s.prelaunch_mode ? "text-cyan-300" : "text-neutral-400"}>Aktiv</span>
          </label>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <Field label="Eyebrow (klein über dem Titel, leer = ausblenden)">
            <input className={TXT} value={s.prelaunch_eyebrow ?? ""} onChange={e => set("prelaunch_eyebrow", e.target.value)} placeholder="Pre-Launch" data-testid="site-prelaunch-eyebrow" />
          </Field>
          <Field label="Titel (leer = ausblenden)">
            <input className={TXT} value={s.prelaunch_title || ""} onChange={e => set("prelaunch_title", e.target.value)} placeholder="Sector Four geht live" data-testid="site-prelaunch-title" />
          </Field>
          <Field label="Veröffentlichungs-Zeitpunkt">
            <input className={TXT} type="datetime-local" value={dtToLocal(s.prelaunch_target)} onChange={e => set("prelaunch_target", localToIso(e.target.value))} data-testid="site-prelaunch-target" />
          </Field>
        </div>
        <Field label="Nachricht (Markdown)">
          <textarea className={TXT + " font-mono text-xs"} rows={4} value={s.prelaunch_message || ""} onChange={e => set("prelaunch_message", e.target.value)} placeholder={"# Die Liga öffnet ihre Tore\n\nWir starten am Sonntag um 20:00 Uhr."} data-testid="site-prelaunch-message" />
        </Field>
        {s.prelaunch_mode && s.prelaunch_target && (() => {
          const target = new Date(s.prelaunch_target);
          if (isNaN(target)) return null;
          const now = new Date();
          const active = now < target;
          return (
            <div className={`text-[11px] uppercase tracking-widest px-3 py-1.5 border inline-block mt-2 ${active ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10" : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"}`} data-testid="site-prelaunch-status">
              {active ? `Aktiv bis ${target.toLocaleString("de-DE")}` : `Ziel erreicht — Site wieder offen seit ${target.toLocaleString("de-DE")}`}
            </div>
          );
        })()}
      </div>

      {/* MAINTENANCE */}
      <div className="card-surface p-5" data-testid="site-maintenance">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display font-black italic uppercase text-lg">Wartungsmodus</div>
            <p className="text-xs text-neutral-500 mt-1">
              Aktiv: alle Besucher sehen statt der Seite eine Wartungs-Nachricht. <strong className="text-amber-300">Eingeloggte Admins sehen weiterhin alles</strong> — der <code>/admin</code> Bereich ist immer erreichbar.
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-xs uppercase tracking-widest">
            <input type="checkbox" className="w-4 h-4 accent-amber-400" checked={!!s.maintenance_mode} onChange={e => set("maintenance_mode", e.target.checked)} data-testid="site-maintenance-enabled" />
            <span className={s.maintenance_mode ? "text-amber-300" : "text-neutral-400"}>Sofort-Aktiv</span>
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Geplanter Start (optional)">
            <input className={TXT} type="datetime-local" value={dtToLocal(s.maintenance_start_at)} onChange={e => set("maintenance_start_at", localToIso(e.target.value))} data-testid="site-maintenance-start" />
          </Field>
          <Field label="Geplantes Ende (optional)">
            <input className={TXT} type="datetime-local" value={dtToLocal(s.maintenance_end_at)} onChange={e => set("maintenance_end_at", localToIso(e.target.value))} data-testid="site-maintenance-end" />
          </Field>
        </div>
        <p className="text-[11px] text-neutral-500 -mt-2">
          Wenn beide Zeiten gesetzt sind, schaltet die Site automatisch im Zeitfenster auf Wartung. Beide leer + Sofort-Toggle aus = Wartung aus.
        </p>
        {(() => {
          const start = s.maintenance_start_at ? new Date(s.maintenance_start_at) : null;
          const end = s.maintenance_end_at ? new Date(s.maintenance_end_at) : null;
          if (!start && !end && !s.maintenance_mode) return null;
          const now = new Date();
          let label = "";
          let color = "neutral";
          if (s.maintenance_mode) { label = "Sofort-Modus: jetzt aktiv"; color = "amber"; }
          else if (start && end && now >= start && now < end) { label = `Plan-Modus: jetzt aktiv bis ${end.toLocaleString("de-DE")}`; color = "amber"; }
          else if (start && now < start) { label = `Plan-Modus: startet ${start.toLocaleString("de-DE")}`; color = "cyan"; }
          else if (end && now >= end) { label = `Plan-Modus: abgelaufen (${end.toLocaleString("de-DE")})`; color = "neutral"; }
          if (!label) return null;
          const palette = color === "amber"
            ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
            : color === "cyan"
            ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10"
            : "border-white/15 text-neutral-300";
          return <div className={`text-[11px] uppercase tracking-widest px-3 py-1.5 border inline-block mt-2 ${palette}`} data-testid="site-maintenance-status">{label}</div>;
        })()}
        <Field label="Wartungs-Nachricht (Markdown)">
          <textarea className={TXT + " font-mono text-xs"} rows={5} value={s.maintenance_message || ""} onChange={e => set("maintenance_message", e.target.value)} placeholder={"# Wir basteln gerade\n\nDie Seite ist gleich wieder da."} data-testid="site-maintenance-message" />
        </Field>
      </div>

      <div className="flex items-center justify-end">
        <button type="button" onClick={save} disabled={busy} className="btn-primary chamfer-sm disabled:opacity-40" data-testid="site-identity-save">
          {busy ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </div>
  );
}



/* ============= SETTINGS (Sonstiges) ============= */
function CardRevealAdminBlock() {
  const [drivers, setDrivers] = useState([]);
  const [s, setS] = useState({ cards_hidden: false, card_reveal_enabled: false, card_reveal_pool: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setS({
        cards_hidden: !!data?.cards_hidden,
        card_reveal_enabled: !!data?.card_reveal_enabled,
        card_reveal_pool: Array.isArray(data?.card_reveal_pool) ? data.card_reveal_pool : [],
      });
    }).catch(() => {});
    api.get("/drivers").then(({ data }) => setDrivers((data || []).filter(d => !d.is_reserve_driver))).catch(() => {});
  }, []);

  const togglePool = (id) => {
    setS(prev => ({
      ...prev,
      card_reveal_pool: prev.card_reveal_pool.includes(id)
        ? prev.card_reveal_pool.filter(x => x !== id)
        : [...prev.card_reveal_pool, id],
    }));
  };
  const selectAll = (driverList) => {
    setS(prev => ({ ...prev, card_reveal_pool: Array.from(new Set([...prev.card_reveal_pool, ...driverList.map(d => d.id)])) }));
  };
  const clearAll = () => setS(prev => ({ ...prev, card_reveal_pool: [] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Reveal-Einstellungen gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const splits = Array.from(new Set(drivers.map(d => d.split || "champions")));

  return (
    <div className="border border-cyan-400/30 p-5 bg-cyan-500/[0.04]" data-testid="card-reveal-admin">
      <div className="eyebrow text-cyan-300">Karten-Reveal · Launch-Event</div>
      <div className="text-base font-bold mt-1">Pack-Opening & verdeckte Karten</div>
      <p className="text-xs text-neutral-400 mt-1 max-w-2xl">
        Steuere die Reveal-Phase für deinen Launch. Aktiviere „Karten verdecken", damit die Fahrerseite Karten-Rückseiten zeigt.
        Schalte die <code>/karten-reveal</code> Seite frei und wähle, welche Karten im Pack-Pool sein dürfen.
      </p>

      <label className="flex items-center gap-3 mt-5 cursor-pointer" data-testid="reveal-cards-hidden">
        <input
          type="checkbox"
          checked={s.cards_hidden}
          onChange={(e) => setS({ ...s, cards_hidden: e.target.checked })}
        />
        <span>
          <div className="font-bold text-sm">Alle Karten verdecken</div>
          <div className="text-[11px] text-neutral-500">Die Fahrerübersicht (<code>/fahrer</code>) zeigt nur Karten-Rückseiten mit Fragezeichen.</div>
        </span>
      </label>

      <label className="flex items-center gap-3 mt-4 cursor-pointer" data-testid="reveal-enabled-toggle">
        <input
          type="checkbox"
          checked={s.card_reveal_enabled}
          onChange={(e) => setS({ ...s, card_reveal_enabled: e.target.checked })}
        />
        <span>
          <div className="font-bold text-sm">Karten-Reveal-Seite freischalten</div>
          <div className="text-[11px] text-neutral-500">
            Die Seite <code>/karten-reveal</code> wird öffentlich erreichbar. Besucher können Packs öffnen und Karten aus dem Pool unten aufdecken.
          </div>
        </span>
      </label>

      <div className="mt-6 border-t border-white/10 pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="font-bold text-sm">Karten-Pool ({s.card_reveal_pool.length} ausgewählt)</div>
            <div className="text-[11px] text-neutral-500">Nur diese Karten erscheinen im Pack-Opening.</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => selectAll(drivers)} className="text-[11px] uppercase tracking-widest text-cyan-300 hover:text-white" data-testid="reveal-pool-all">
              Alle wählen
            </button>
            <span className="text-neutral-700">·</span>
            <button onClick={clearAll} className="text-[11px] uppercase tracking-widest text-neutral-400 hover:text-white" data-testid="reveal-pool-none">
              Leeren
            </button>
          </div>
        </div>
        {splits.map(split => {
          const list = drivers.filter(d => (d.split || "champions") === split);
          if (!list.length) return null;
          return (
            <div key={split} className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow text-neutral-500">{split}</div>
                <button onClick={() => selectAll(list)} className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white">
                  Split wählen
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {list.map(d => {
                  const sel = s.card_reveal_pool.includes(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => togglePool(d.id)}
                      className="text-left px-3 py-2 text-xs border transition-colors"
                      style={{
                        borderColor: sel ? "#22D3EE" : "rgba(255,255,255,0.08)",
                        background: sel ? "rgba(34,211,238,0.10)" : "transparent",
                        color: sel ? "#fff" : "#9CA3AF",
                      }}
                      data-testid={`reveal-pool-driver-${d.id}`}
                    >
                      <div className="font-bold">{d.name}</div>
                      <div className="text-[10px] text-neutral-500">#{d.number || "-"} · OVR {d.ovr || "?"}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 bg-cyan-400 text-black font-bold uppercase tracking-widest text-xs hover:bg-cyan-300 disabled:opacity-40"
          data-testid="reveal-save"
        >
          {saving ? "Speichert …" : "Speichern"}
        </button>
        <a
          href="/karten-reveal"
          target="_blank"
          rel="noreferrer"
          className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white"
        >
          → Reveal-Seite öffnen
        </a>
      </div>
    </div>
  );
}

function SettingsAdmin() {
  const [watchUrl, setWatchUrl] = useState("");
  const [deltaBig, setDeltaBig] = useState(5);
  const [inactiveLogoUrl, setInactiveLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setWatchUrl(data?.watch_live_url || "");
      setDeltaBig(parseInt(data?.delta_big_threshold ?? 5));
      setInactiveLogoUrl(data?.inactive_logo_url || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { watch_live_url: watchUrl.trim(), delta_big_threshold: parseInt(deltaBig) || 5, inactive_logo_url: inactiveLogoUrl || "" });
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const migrateImages = async () => {
    if (!confirm("Alle alten Base64-Bilder (Fahrer/Teams/Tiers) jetzt nach Cloudinary migrieren? Das kann ein paar Sekunden dauern.")) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const { data } = await api.post("/cloudinary/migrate");
      setMigrationResult(data);
      toast.success(`${data.total_migrated} Bilder migriert · ${data.total_failed} Fehler`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration fehlgeschlagen");
    } finally {
      setMigrating(false);
    }
  };

  const syncImages = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post("/cloudinary/sync");
      setSyncResult(data);
      toast.success(`${data.total_updated} Bilder aktualisiert (${data.total_matched} gematched)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-admin">
      <SiteIdentityAdmin />
      <div className="max-w-2xl card-surface p-6">
      <div className="font-display font-black italic uppercase text-lg">Sonstige Einstellungen</div>
      <p className="text-xs text-neutral-500 mt-1">Globale Konfigurationen für die öffentliche Seite.</p>

      <Field label="Watch Live · Link (öffnet sich in neuem Fenster)">
        <input
          className={TXT}
          type="url"
          placeholder="https://twitch.tv/dein_kanal"
          value={watchUrl}
          onChange={e => setWatchUrl(e.target.value)}
          data-testid="settings-watch-url"
        />
      </Field>
      <p className="text-[11px] text-neutral-500 mt-1">Wird im Rennkalender bei geplanten (noch nicht gefahrenen) Rennen als „Watch Live"-Button angezeigt.</p>

      <Field label="Schwelle für Doppelpfeil ⏫/⏬ auf der Fahrerkarte">
        <input
          className={TXT + " max-w-[120px]"}
          type="number"
          min="1"
          value={deltaBig}
          onChange={e => setDeltaBig(e.target.value)}
          data-testid="settings-delta-big"
        />
      </Field>
      <p className="text-[11px] text-neutral-500 mt-1">Ab diesem Stat-Unterschied wird statt einfacher ▲/▼ ein Doppelpfeil ⏫/⏬ neben dem Wert angezeigt.</p>

      <Field label="Inaktiv-Logo (Fahrerkarten zeigen dieses Bild statt Teamlogo bei inaktiven Fahrern)">
        <ImageUploader
          value={inactiveLogoUrl}
          onChange={setInactiveLogoUrl}
          folder="site"
          publicId="inactive-logo"
          testIdPrefix="settings-inactive-logo"
          hint="PNG / JPG · 256×256 · transparent empfohlen"
        />
      </Field>
      <p className="text-[11px] text-neutral-500 mt-1">Empfohlen: Quadratisches Logo mit transparentem Hintergrund (PNG/SVG), ca. 256×256 px. Wenn leer, zeigt die Karte einen Fallback-Badge mit „Inaktiv".</p>

      <div className="flex items-center gap-2 mt-5">
        <button onClick={save} disabled={saving} className="btn-primary chamfer-sm disabled:opacity-50" data-testid="settings-save">
          <Plus className="w-4 h-4" /> {saving ? "Speichern…" : "Speichern"}
        </button>
      </div>

      <div className="mt-8 pt-5 border-t border-white/10">
        <div className="font-display font-black italic uppercase text-base">Cloudinary · Daten-Migration</div>
        <p className="text-xs text-neutral-500 mt-1 max-w-md">
          Migriert alle alten Base64-Bilder (Fahrerfotos, Team-Badges, Tier-Hintergründe) aus der Datenbank nach Cloudinary.
          Ersetzt die <code>data:image/…</code>-URLs durch echte CDN-Links. Einmaliger Vorgang.
        </p>
        <button onClick={migrateImages} disabled={migrating} className="btn-ghost text-xs border border-[#A855F7]/40 text-[#A855F7] hover:bg-[#A855F7]/10 mt-3 disabled:opacity-50" data-testid="settings-migrate">
          {migrating ? "Migriere…" : "Jetzt nach Cloudinary migrieren"}
        </button>
        {migrationResult && (
          <div className="mt-3 text-xs text-neutral-400 border border-white/10 p-3" data-testid="migration-result">
            <div className="font-bold text-white">Ergebnis · {migrationResult.total_migrated} migriert · {migrationResult.total_failed} Fehler</div>
            {migrationResult.details?.map(d => (
              <div key={d.collection + d.field} className="mt-1">
                {d.collection}.{d.field}: gescannt {d.scanned}, migriert {d.migrated}, fehler {d.failed}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 pt-5 border-t border-white/10">
        <div className="font-display font-black italic uppercase text-base">Cloudinary · ID-Sync</div>
        <p className="text-xs text-neutral-500 mt-1 max-w-md">
          Bulk-Workflow: Lade Fahrerbilder im Cloudinary-Dashboard direkt in den Ordner <code>drivers/</code> hoch, jedes Bild als Dateiname die Fahrer-ID (z. B. <code>abc123…</code>).
          Klick danach hier — alle gematchten Bilder werden automatisch den Fahrern zugewiesen.
          Genauso für Teams unter <code>teams/&lt;team_id&gt;</code>.
        </p>
        <button onClick={syncImages} disabled={syncing} className="btn-ghost text-xs border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 mt-3 disabled:opacity-50" data-testid="settings-sync">
          {syncing ? "Synchronisiere…" : "Jetzt aus Cloudinary synchronisieren"}
        </button>
        {syncResult && (
          <div className="mt-3 text-xs text-neutral-400 border border-white/10 p-3" data-testid="sync-result">
            <div className="font-bold text-white">Ergebnis · {syncResult.total_updated} aktualisiert · {syncResult.total_matched} gefunden</div>
            {syncResult.details?.map(d => (
              <div key={d.collection + d.field} className="mt-1">
                {d.collection}.{d.field}: gefunden {d.matched}, aktualisiert {d.updated}, nicht gefunden {d.not_found}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 pt-5 border-t border-white/10">
        <div className="font-display font-black italic uppercase text-base">Daten · IDs neu nummerieren</div>
        <p className="text-xs text-neutral-500 mt-1 max-w-md">
          Vergibt allen Fahrern und Teams einfache fortlaufende IDs (1, 2, 3, …) und aktualisiert automatisch alle FK-Referenzen. Vorhandene Cloudinary-Bilder verlieren ggf. ihre Zuordnung — danach Bilder nach neuer ID umbenennen + „Aus Cloudinary synchronisieren".
        </p>
        <button onClick={async () => {
          if (!confirm("Alle IDs jetzt neu vergeben? Dieser Schritt ist nicht reversibel ohne Backup.")) return;
          try {
            const { data } = await api.post("/admin/renumber");
            toast.success(`Teams: ${data.teams.renumbered}/${data.teams.total} · Drivers: ${data.drivers.renumbered}/${data.drivers.total}`);
            setTimeout(() => window.location.reload(), 1500);
          } catch (e) {
            toast.error(e.response?.data?.detail || "Renumber fehlgeschlagen");
          }
        }} className="btn-ghost text-xs border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 mt-3" data-testid="settings-renumber">
          IDs jetzt neu vergeben (1, 2, 3, …)
        </button>
      </div>
      </div>
    </div>
  );
}
/* ============= FOOTER ADMIN ============= */
function MarkdownField({ label, value, onChange, rows = 10, placeholder, testId, required = false }) {
  const [tab, setTab] = useState("edit"); // edit | preview
  const [mediaOpen, setMediaOpen] = useState(false);
  const textareaRef = useRef(null);

  const insertAtCursor = (text) => {
    const el = textareaRef.current;
    const cur = value || "";
    if (!el) { onChange(cur + text); return; }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + text + cur.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Selektion mit Prefix/Suffix umschließen — wenn keine Selektion, defaultText einfügen.
  const wrapSelection = (prefix, suffix, defaultText = "Text") => {
    const el = textareaRef.current;
    const cur = value || "";
    if (!el) { onChange(cur + prefix + defaultText + suffix); return; }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const selected = cur.slice(start, end) || defaultText;
    const inserted = `${prefix}${selected}${suffix}`;
    const next = cur.slice(0, start) + inserted + cur.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
    });
  };

  // Aktuelle Zeile mit prefix vorbelegen (für H2, Listen).
  const prependLine = (prefix) => {
    const el = textareaRef.current;
    const cur = value || "";
    if (!el) { onChange(cur + "\n" + prefix); return; }
    const start = el.selectionStart ?? cur.length;
    const lineStart = cur.lastIndexOf("\n", start - 1) + 1;
    const next = cur.slice(0, lineStart) + prefix + cur.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + prefix.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const insertLink = () => {
    const el = textareaRef.current;
    const cur = value || "";
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? cur.length;
    const selected = cur.slice(start, end);
    const url = prompt("URL eingeben (https://...)", "https://");
    if (!url) return;
    const label = selected || "Link";
    const inserted = `[${label}](${url})`;
    const next = cur.slice(0, start) + inserted + cur.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + inserted.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  const onMediaSelect = (url) => {
    if (!url) return;
    // Sinnvoller alt-Text aus dem letzten URL-Segment ableiten
    const name = (url.split("/").pop() || "Bild").replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ");
    insertAtCursor(`\n\n![${name}](${url})\n\n`);
  };

  return (
    <Field label={label}>
      <div className="flex items-center gap-1 mb-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => setTab("edit")}
          className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${tab === "edit" ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-white/15 text-neutral-400 hover:bg-white/5"}`}
          data-testid={`${testId}-tab-edit`}
        >Bearbeiten</button>
        <button
          type="button"
          onClick={() => setTab("preview")}
          className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${tab === "preview" ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-white/15 text-neutral-400 hover:bg-white/5"}`}
          data-testid={`${testId}-tab-preview`}
        >Vorschau</button>
        {tab === "edit" && (
          <div className="flex items-center gap-1 ml-2 border-l border-white/10 pl-2">
            <button type="button" onClick={() => prependLine("## ")} title="Überschrift 2"
              className="text-neutral-300 hover:text-cyan-300 hover:bg-white/5 p-1.5 border border-transparent hover:border-white/10"
              data-testid={`${testId}-fmt-h2`}><Heading2 className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => wrapSelection("**", "**", "fett")} title="Fett"
              className="text-neutral-300 hover:text-cyan-300 hover:bg-white/5 p-1.5 border border-transparent hover:border-white/10"
              data-testid={`${testId}-fmt-bold`}><Bold className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => wrapSelection("*", "*", "kursiv")} title="Kursiv"
              className="text-neutral-300 hover:text-cyan-300 hover:bg-white/5 p-1.5 border border-transparent hover:border-white/10"
              data-testid={`${testId}-fmt-italic`}><Italic className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={insertLink} title="Link einfügen"
              className="text-neutral-300 hover:text-cyan-300 hover:bg-white/5 p-1.5 border border-transparent hover:border-white/10"
              data-testid={`${testId}-fmt-link`}><LinkIcon className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => prependLine("- ")} title="Listenpunkt"
              className="text-neutral-300 hover:text-cyan-300 hover:bg-white/5 p-1.5 border border-transparent hover:border-white/10"
              data-testid={`${testId}-fmt-list`}><List className="w-3.5 h-3.5" /></button>
            <button
              type="button"
              onClick={() => setMediaOpen(true)}
              className="text-[10px] uppercase tracking-widest px-2 py-1 border border-white/15 text-neutral-300 hover:bg-white/5 inline-flex items-center gap-1 ml-1"
              data-testid={`${testId}-insert-image`}
              title="Bild aus Mediathek auswählen und an Cursor-Position einfügen"
            >
              <Images className="w-3 h-3" /> Bild einfügen
            </button>
          </div>
        )}
      </div>
      {tab === "edit" ? (
        <textarea
          ref={textareaRef}
          className={TXT + " font-mono text-xs"}
          rows={rows}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
        />
      ) : (
        <div className="bg-[#0B0F19] border border-white/10 px-3 py-2 min-h-[200px]" data-testid={`${testId}-preview`}>
          {value ? <Markdown>{value}</Markdown> : <div className="text-xs text-neutral-500 italic">Noch nichts geschrieben.</div>}
        </div>
      )}
      <MediaLibrary
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={onMediaSelect}
      />
    </Field>
  );
}

function FooterAdmin() {
  const [tagline, setTagline] = useState("");
  const [ligaText, setLigaText] = useState("");
  const [impressum, setImpressum] = useState("");
  const [datenschutz, setDatenschutz] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setTagline(data?.footer_tagline || "");
      setLigaText(data?.footer_liga_text || "");
      setImpressum(data?.impressum || "");
      setDatenschutz(data?.datenschutz || "");
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings", {
        footer_tagline: tagline,
        footer_liga_text: ligaText,
        impressum,
        datenschutz,
      });
      toast.success("Footer-Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setBusy(false); }
  };

  if (!loaded) return <div className="text-neutral-500 text-sm">Lädt…</div>;

  return (
    <form onSubmit={save} className="grid lg:grid-cols-2 gap-6" data-testid="footer-admin">
      <div className="card-surface p-5">
        <div className="font-display font-black italic uppercase text-lg mb-1">Footer-Texte</div>
        <p className="text-[11px] text-neutral-500 mb-4">Diese Texte erscheinen direkt im Footer auf jeder Seite.</p>
        <Field label="Tagline (unter dem Logo)">
          <textarea
            className={TXT}
            rows={3}
            value={tagline}
            onChange={e => setTagline(e.target.value)}
            placeholder="Das Racing Journal der Soest Gaming League ..."
            data-testid="footer-admin-tagline"
          />
        </Field>
        <Field label='Liga-Box (rechter Footer-Kasten)'>
          <textarea
            className={TXT}
            rows={3}
            value={ligaText}
            onChange={e => setLigaText(e.target.value)}
            placeholder="Soest Gaming League · Fiktive F1 Sim-Racing-Liga · Saison 2026"
            data-testid="footer-admin-liga"
          />
        </Field>
      </div>

      <div className="card-surface p-5">
        <div className="font-display font-black italic uppercase text-lg mb-1">Rechtliche Pflichtseiten</div>
        <p className="text-[11px] text-neutral-500 mb-3">
          Die Inhalte werden auf <code>/impressum</code> bzw. <code>/datenschutz</code> gerendert.
          Hinweis: rechtliche Beratung übernimmt diese Anwendung nicht — bitte einen aktuellen Impressums- und
          Datenschutz-Generator (z. B. e-recht24.de) als Grundlage nutzen.
          <br />
          <span className="text-cyan-300">Tipp:</span> Es funktioniert sowohl <strong>Markdown</strong> als auch <strong>reines HTML</strong> (z. B. fertiger Generator-Output mit <code>&lt;h2&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;a&gt;</code>). Beides kann auch gemischt werden.
        </p>
        <details className="mb-3">
          <summary className="text-[10px] uppercase tracking-widest text-neutral-500 cursor-pointer hover:text-neutral-300">Markdown-Kurzreferenz</summary>
          <div className="text-[11px] text-neutral-400 mt-2 grid grid-cols-2 gap-x-4 gap-y-1 font-mono">
            <span># Überschrift 1</span><span>## Überschrift 2</span>
            <span>**fett** · *kursiv*</span><span>[Link](https://…)</span>
            <span>- Listenpunkt</span><span>1. Nummeriert</span>
            <span>--- (Trennlinie)</span><span>&gt; Zitat</span>
          </div>
        </details>
        <MarkdownField
          label="Impressum"
          value={impressum}
          onChange={setImpressum}
          rows={10}
          placeholder={"# Impressum\n\n**Angaben gemäß § 5 TMG**\n\nMax Mustermann  \nMusterstr. 1  \n12345 Musterstadt\n\n## Kontakt\n\nE-Mail: info@example.de"}
          testId="footer-admin-impressum"
        />
        <MarkdownField
          label="Datenschutz"
          value={datenschutz}
          onChange={setDatenschutz}
          rows={10}
          placeholder={"# Datenschutzerklärung\n\n## 1. Verantwortlicher\n\nMax Mustermann  \nMusterstr. 1  \n12345 Musterstadt\n\n## 2. Erhebung von Daten\n\n..."}
          testId="footer-admin-datenschutz"
        />
      </div>

      <div className="lg:col-span-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-xs text-neutral-500">
          <a href="/impressum" target="_blank" rel="noreferrer" className="hover:text-cyan-300">Vorschau Impressum →</a>
          <a href="/datenschutz" target="_blank" rel="noreferrer" className="hover:text-cyan-300">Vorschau Datenschutz →</a>
        </div>
        <button type="submit" disabled={busy} className="btn-primary chamfer-sm disabled:opacity-40" data-testid="footer-admin-save">
          {busy ? "Speichert…" : "Speichern"}
        </button>
      </div>
    </form>
  );
}



/* ============= STATS UPDATER ============= */
const STAT_KEYS = ["pac", "kon", "rac", "cle", "qua", "erg", "zuv"];
const CSV_NUMERIC_KEYS = ["ovr", ...STAT_KEYS, "market_value"];

const normalizeName = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9 ]/g, " ")
  .replace(/\s+/g, " ")
  .trim();

// Kompakt-Normalisierung: alle Whitespaces & Sonderzeichen komplett weg.
// "Danger Anger" ↔ "DangerAnger" ↔ "danger.anger_99" → "dangeranger99"
const normalizeCompact = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]/g, "");

const parseCsv = (text) => {
  const lines = text.replace(/\r/g, "").split("\n").filter(l => l.trim().length);
  if (!lines.length) return { header: [], rows: [] };
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQ = false; }
        else { cur += c; }
      } else if (c === ",") { out.push(cur); cur = ""; }
      else if (c === '"' && cur === "") { inQ = true; }
      else { cur += c; }
    }
    out.push(cur);
    return out.map(x => x.trim());
  };
  const header = parseLine(lines[0]).map(h => h.toLowerCase());
  const rows = lines.slice(1).map(line => {
    const cols = parseLine(line);
    const obj = {};
    header.forEach((h, i) => { obj[h] = cols[i] ?? ""; });
    return obj;
  });
  return { header, rows };
};

const matchDriverByName = (rawName, drivers) => {
  const norm = normalizeName(rawName);
  const compact = normalizeCompact(rawName);
  if (!norm && !compact) return { matches: [] };
  // Helper: alle Ingame-Namen eines Fahrers (primary + aliases) sammeln
  const ingameNamesOf = (d) => {
    const list = [];
    if (d.ingame_name) list.push(d.ingame_name);
    if (Array.isArray(d.ingame_aliases)) {
      d.ingame_aliases.forEach(a => { if (a && String(a).trim()) list.push(a); });
    }
    return list;
  };
  const anyIngameMatches = (d, testFn) => ingameNamesOf(d).some(n => testFn(n));
  // 1) Priorität: Ingame-Name exakt (normal ODER kompakt — Sonderzeichen/Leerzeichen egal)
  const ingameExact = drivers.filter(d =>
    anyIngameMatches(d, n => normalizeName(n) === norm || normalizeCompact(n) === compact)
  );
  if (ingameExact.length === 1) return { matched: ingameExact[0], matches: ingameExact, kind: "ingame-exact" };
  if (ingameExact.length > 1) return { matches: ingameExact, ambiguous: true };
  // 2) Klarname exakt (normal ODER kompakt)
  const exact = drivers.filter(d => normalizeName(d.name) === norm || normalizeCompact(d.name) === compact);
  if (exact.length === 1) return { matched: exact[0], matches: exact, kind: "exact" };
  if (exact.length > 1) return { matches: exact, ambiguous: true };
  // 3) Ingame-Name startsWith / contains (auf beiden Normalisierungen, inkl. Aliases)
  const ingameSoft = drivers.filter(d => anyIngameMatches(d, n => {
    const dn = normalizeName(n);
    const dc = normalizeCompact(n);
    return (dn && (dn.startsWith(norm) || norm.startsWith(dn) || dn.includes(norm) || norm.includes(dn))) ||
           (dc && compact && (dc.startsWith(compact) || compact.startsWith(dc) || dc.includes(compact) || compact.includes(dc)));
  }));
  if (ingameSoft.length === 1) return { matched: ingameSoft[0], matches: ingameSoft, kind: "ingame-soft" };
  if (ingameSoft.length > 1) return { matches: ingameSoft, ambiguous: true };
  // 4) Klarname startsWith / contains (beide Varianten)
  const starts = drivers.filter(d => {
    const dn = normalizeName(d.name);
    const dc = normalizeCompact(d.name);
    return (dn.startsWith(norm) || norm.startsWith(dn)) ||
           (dc && compact && (dc.startsWith(compact) || compact.startsWith(dc)));
  });
  if (starts.length === 1) return { matched: starts[0], matches: starts, kind: "starts" };
  const contains = drivers.filter(d => {
    const dn = normalizeName(d.name);
    const dc = normalizeCompact(d.name);
    return (dn.includes(norm) || norm.includes(dn)) ||
           (dc && compact && (dc.includes(compact) || compact.includes(dc)));
  });
  if (contains.length === 1) return { matched: contains[0], matches: contains, kind: "contains" };
  if (contains.length > 1) return { matches: contains, ambiguous: true };
  return { matches: [] };
};

function StatsUpdaterAdmin() {
  const splits = useSplits();
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filterSplit, setFilterSplit] = useState(splits[0]?.key || "champions");
  const [seasonName, setSeasonName] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [edits, setEdits] = useState({}); // driver_id -> { stats:{}, ovr, market_value }
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, label: "" });
  const [csvPreview, setCsvPreview] = useState(null); // { rows: [...] }
  const [driverSearchQuery, setDriverSearchQuery] = useState("");
  const [walkoutReveals, setWalkoutReveals] = useState(null);
  const csvFileRef = useRef(null);
  const captureContainerRef = useRef(null);

  const load = async () => {
    const [d, t] = await Promise.all([api.get("/drivers"), api.get("/teams")]);
    setDrivers(d.data);
    setTeams(t.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => { setFilterSplit(s => s || splits[0]?.key || "champions"); }, [splits.length]);

  const teamById = Object.fromEntries(teams.map(t => [t.id, t]));
  const visibleDrivers = drivers
    .filter(d => !d.is_reserve_driver && (d.split || "champions") === filterSplit)
    .slice()
    .sort((a, b) => (b.ovr || 0) - (a.ovr || 0));

  // ALLE Fahrer split-übergreifend — für die manuelle Zuordnung im CSV-Modal.
  // Inkl. Reserve + Inaktive, gruppiert nach Team + optional nach Name gefiltert.
  const allSelectableDrivers = drivers
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const filteredSelectableDrivers = (() => {
    const q = driverSearchQuery.trim();
    if (!q) return allSelectableDrivers;
    const qn = normalizeName(q);
    const qc = normalizeCompact(q);
    return allSelectableDrivers.filter(d => {
      const name = normalizeName(d.name);
      const nameC = normalizeCompact(d.name);
      const ingame = normalizeName(d.ingame_name || "");
      const ingameC = normalizeCompact(d.ingame_name || "");
      return (qn && (name.includes(qn) || ingame.includes(qn))) ||
             (qc && (nameC.includes(qc) || ingameC.includes(qc))) ||
             String(d.number || "").includes(q);
    });
  })();

  // Nach Team gruppieren (Team-Name alphabetisch, "Ohne Team" ans Ende).
  const driversByTeam = (() => {
    const map = new Map();
    filteredSelectableDrivers.forEach(d => {
      const team = teamById[d.team_id];
      const label = team?.name || "Ohne Team";
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(d);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => {
        if (a === "Ohne Team") return 1;
        if (b === "Ohne Team") return -1;
        return a.localeCompare(b);
      });
  })();

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === visibleDrivers.length) setSelected(new Set());
    else setSelected(new Set(visibleDrivers.map(d => d.id)));
  };

  const editVal = (driverId, key, value) => {
    setEdits(prev => {
      const cur = prev[driverId] || { stats: {} };
      if (STAT_KEYS.includes(key)) {
        return { ...prev, [driverId]: { ...cur, stats: { ...(cur.stats || {}), [key]: value } } };
      }
      return { ...prev, [driverId]: { ...cur, [key]: value } };
    });
  };

  const valFor = (d, key) => {
    const e = edits[d.id];
    if (STAT_KEYS.includes(key)) {
      const v = e?.stats?.[key];
      if (v !== undefined && v !== "") return v;
      return d.stats?.[key] ?? 80;
    }
    const v = e?.[key];
    if (v !== undefined && v !== "") return v;
    return d[key] ?? (key === "market_value" ? 0 : 80);
  };

  // Aktuelle Karte des Fahrers als PNG-base64 rendern (1:1 wie sichtbar, NUR Front)
  const captureCard = async (driver) => {
    const node = document.createElement("div");
    // WICHTIG: nicht via "left: -9999px" verstecken, sondern via opacity & pointer-events,
    // damit html2canvas die Layout-Maße korrekt berechnen kann.
    node.style.cssText = "position:absolute;top:0;left:0;width:288px;height:420px;background:#000;opacity:0;pointer-events:none;z-index:-1;";
    captureContainerRef.current.appendChild(node);
    const { createRoot } = await import("react-dom/client");
    const root = createRoot(node);
    return new Promise((resolve) => {
      root.render(
        <DriverCard driver={driver} team={teamById[driver.team_id]} teamsById={teamById} testId="capture-card" staticFront={true} />
      );
      // Warte 500ms für Hintergrundbilder, Schriften und Tier-Lookups
      setTimeout(async () => {
        try {
          const cardWrapper = node.querySelector("[data-testid='capture-card']");
          if (!cardWrapper) { resolve(null); return; }
          // Maße erzwingen, damit html2canvas garantiert 288×420 nimmt
          cardWrapper.style.width = "288px";
          cardWrapper.style.height = "420px";
          cardWrapper.style.maxWidth = "288px";
          cardWrapper.style.maxHeight = "420px";
          cardWrapper.style.minHeight = "420px";
          cardWrapper.style.overflow = "hidden";
          const rect = cardWrapper.getBoundingClientRect();
          console.log("[captureCard] rect:", rect.width, "x", rect.height);
          const canvas = await html2canvas(cardWrapper, {
            backgroundColor: "#000",
            useCORS: true,
            scale: 2,
            width: 288,
            height: 420,
            windowWidth: 288,
            windowHeight: 420,
            onclone: (_clonedDoc, _clonedEl) => {
              // backdrop-filter wird von html2canvas nicht unterstützt → entfernen
              _clonedEl.querySelectorAll("*").forEach(el => {
                if (el instanceof HTMLElement) {
                  el.style.backdropFilter = "none";
                  el.style.webkitBackdropFilter = "none";
                }
              });
              // BUGFIX: html2canvas berechnet `bottom: 0` der bottom-panel falsch
              // (offset-parent-Höhe wird in manchen Kontexten als < 420 ermittelt).
              // Wir konvertieren explizit `bottom: 0` -> `top: <berechnet>` mit Hilfe des
              // ORIGINAL-DOMs (das die korrekte Höhe kennt).
              try {
                const originalNode = document.querySelector("[data-testid='capture-card']");
                const origPanels = originalNode ? originalNode.querySelectorAll(".absolute") : [];
                const clonePanels = _clonedEl.querySelectorAll(".absolute");
                for (let i = 0; i < clonePanels.length && i < origPanels.length; i++) {
                  const orig = origPanels[i];
                  const cln = clonePanels[i];
                  const cs = window.getComputedStyle(orig);
                  if (cs.position === "absolute" && cs.bottom === "0px" && cs.top === "auto") {
                    const rect = orig.getBoundingClientRect();
                    const parentRect = orig.offsetParent?.getBoundingClientRect();
                    if (parentRect) {
                      const topPx = rect.top - parentRect.top;
                      cln.style.top = topPx + "px";
                      cln.style.bottom = "auto";
                    }
                  }
                }
              } catch (e) {
                console.warn("bottom-fix failed:", e);
              }
            },
          });
          const dataUrl = canvas.toDataURL("image/png", 0.92);
          resolve(dataUrl);
        } catch (e) {
          console.warn("captureCard failed:", e);
          resolve(null);
        } finally {
          try { root.unmount(); } catch (_e) { /* noop */ }
          node.remove();
        }
      }, 500);
    });
  };

  const handleCsvFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const { header, rows } = parseCsv(text);
      if (!rows.length) { toast.error("CSV ist leer"); return; }
      const missing = ["name"].filter(h => !header.includes(h));
      if (missing.length) { toast.error(`Spalte „${missing.join(", ")}" fehlt im Header`); return; }
      const previewRows = rows.map((r, idx) => {
        const matchRes = matchDriverByName(r.name, visibleDrivers);
        const driverId = (!matchRes.ambiguous && matchRes.matched) ? matchRes.matched.id : null;
        return {
          idx,
          src: r,
          driver_id: driverId,
          matches: matchRes.matches.map(m => m.id),
          ambiguous: !!matchRes.ambiguous,
          kind: matchRes.kind || (driverId ? "matched" : (matchRes.matches.length ? "ambiguous" : "unmatched")),
        };
      });
      setCsvPreview({ header, rows: previewRows });
      if (csvFileRef.current) csvFileRef.current.value = "";
    } catch (e) {
      toast.error("CSV konnte nicht gelesen werden: " + (e.message || ""));
    }
  };

  const updateCsvRow = (idx, patch) => {
    setCsvPreview(p => ({ ...p, rows: p.rows.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };
  const removeCsvRow = (idx) => {
    setCsvPreview(p => ({ ...p, rows: p.rows.filter((_, i) => i !== idx) }));
  };

  const applyCsvPreview = () => {
    if (!csvPreview) return;
    const matched = csvPreview.rows.filter(r => r.driver_id);
    if (!matched.length) { toast.error("Keine zugeordneten Fahrer"); return; }
    const newSelected = new Set(selected);
    const newEdits = { ...edits };
    for (const r of matched) {
      newSelected.add(r.driver_id);
      const e = newEdits[r.driver_id] || { stats: {} };
      const stats = { ...(e.stats || {}) };
      for (const k of STAT_KEYS) {
        if (r.src[k] !== undefined && r.src[k] !== "") {
          const n = parseInt(r.src[k]);
          if (Number.isFinite(n)) stats[k] = n;
        }
      }
      const ovrVal = r.src.ovr !== undefined && r.src.ovr !== "" ? parseInt(r.src.ovr) : undefined;
      const mvVal = r.src.market_value !== undefined && r.src.market_value !== "" ? parseInt(r.src.market_value) : undefined;
      newEdits[r.driver_id] = {
        ...e,
        stats,
        ...(Number.isFinite(ovrVal) ? { ovr: ovrVal } : {}),
        ...(Number.isFinite(mvVal) ? { market_value: mvVal } : {}),
      };
    }
    setSelected(newSelected);
    setEdits(newEdits);
    setCsvPreview(null);
    const skipped = csvPreview.rows.length - matched.length;
    toast.success(`${matched.length} Fahrer in Tabelle übernommen${skipped ? ` · ${skipped} übersprungen` : ""}`);
  };

  const submit = async () => {
    if (!selected.size) { toast.error("Keine Fahrer ausgewählt"); return; }
    const ids = Array.from(selected);
    setSaving(true);
    setProgress({ done: 0, total: ids.length, label: "Karten rendern …" });
    try {
      const updates = [];
      for (let i = 0; i < ids.length; i++) {
        const d = drivers.find(x => x.id === ids[i]);
        if (!d) continue;
        setProgress({ done: i, total: ids.length, label: `Karte von ${d.name} archivieren …` });
        const img = await captureCard(d);
        const newStats = {};
        for (const k of STAT_KEYS) newStats[k] = parseInt(valFor(d, k)) || 0;
        updates.push({
          driver_id: d.id,
          season_name: seasonName || "",
          stats: newStats,
          ovr: parseInt(valFor(d, "ovr")) || 0,
          market_value: parseInt(valFor(d, "market_value")) || 0,
          card_image_b64: img,
        });
      }
      setProgress({ done: ids.length, total: ids.length, label: "An Server senden …" });
      const { data } = await api.post("/drivers/bulk-update-stats", { updates });
      toast.success(`${data.updated} Fahrer aktualisiert · ${data.archived} Karten archiviert`);
      setEdits({});
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update fehlgeschlagen");
    } finally {
      setSaving(false);
      setProgress({ done: 0, total: 0, label: "" });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <div className="eyebrow">Split-Filter</div>
          <select value={filterSplit} onChange={e => setFilterSplit(e.target.value)} className={TXT + " w-48"} data-testid="stats-split-filter">
            {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <div className="eyebrow">Saison (für Karten-Archiv)</div>
          <input value={seasonName} onChange={e => setSeasonName(e.target.value)} placeholder="z. B. Saison 4 · Mitte" className={TXT + " w-64"} data-testid="stats-season-name" />
        </div>
        <div>
          <div className="eyebrow">CSV-Import</div>
          <input ref={csvFileRef} type="file" accept=".csv,text/csv" onChange={e => handleCsvFile(e.target.files?.[0])} className="hidden" data-testid="stats-csv-file" />
          <button type="button" onClick={() => csvFileRef.current?.click()} className="btn-ghost text-xs chamfer-sm" data-testid="stats-csv-btn">
            CSV hochladen
          </button>
          <div className="text-[10px] text-neutral-500 mt-1">Spalten: name,ovr,pac,kon,rac,cle,qua,erg,zuv,market_value</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="text-xs text-neutral-400 mr-2">{selected.size} / {visibleDrivers.length} ausgewählt</div>
          <button
            type="button"
            onClick={() => {
              // Demo: pick 5 active drivers and stage fake deltas across all tiers
              const pool = visibleDrivers.slice(0, 5);
              if (!pool.length) { toast.error("Keine Fahrer im aktiven Split."); return; }
              const fakeDeltas = [+1, +3, +6, -2, +12]; // standard / upgrade / walkout / downgrade / ULTRA
              const reveals = pool.map((d, i) => {
                const newOvr = Math.max(50, Math.min(99, (d.ovr || 80) + fakeDeltas[i % fakeDeltas.length]));
                return {
                  driver: d,
                  team: teamById[d.team_id] || null,
                  prev: d,
                  oldOvr: d.ovr || 80,
                  newOvr,
                };
              });
              setWalkoutReveals(reveals);
            }}
            className="btn-ghost text-xs chamfer-sm"
            data-testid="stats-walkout-demo"
            title="Zeigt die Reveal-Animation mit Beispiel-Deltas in allen Stufen"
          >
            🎬 Walkout-Demo
          </button>
          <button onClick={toggleAll} className="btn-ghost text-xs" data-testid="stats-toggle-all">
            {selected.size === visibleDrivers.length && visibleDrivers.length > 0 ? "Keine" : "Alle"}
          </button>
          <button onClick={submit} disabled={saving || !selected.size} className="btn-primary chamfer-sm text-xs disabled:opacity-40" data-testid="stats-submit">
            {saving ? `${progress.done}/${progress.total} · ${progress.label}` : "Übernehmen + Karte archivieren"}
          </button>
        </div>
      </div>

      {walkoutReveals && (
        <WalkoutReveal
          reveals={walkoutReveals}
          onClose={() => setWalkoutReveals(null)}
          autoMs={0}
        />
      )}

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-xs" data-testid="stats-table">
          <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
            <tr>
              <th className="w-10 px-2 py-2 text-center">
                <input type="checkbox" checked={visibleDrivers.length > 0 && selected.size === visibleDrivers.length} onChange={toggleAll} data-testid="stats-select-all" />
              </th>
              <th className="text-left px-3 py-2 min-w-[170px]">Fahrer</th>
              <th className="text-left px-3 py-2 min-w-[120px]">Team</th>
              <th className="text-left px-3 py-2 min-w-[150px]">Letztes Karten-Archiv</th>
              {STAT_KEYS.map(k => <th key={k} className="px-2 py-2 text-center w-14">{k.toUpperCase()}</th>)}
              <th className="px-2 py-2 text-center w-16">OVR</th>
              <th className="px-2 py-2 text-center w-32">Marktwert (€)</th>
            </tr>
          </thead>
          <tbody>
            {visibleDrivers.map(d => {
              const tm = teamById[d.team_id] || {};
              const isSel = selected.has(d.id);
              return (
                <tr key={d.id} className={`border-t border-white/5 hover:bg-white/[0.02] ${isSel ? "bg-cyan-500/5" : ""}`} data-testid={`stats-row-${d.id}`}>
                  <td className="px-2 py-1.5 text-center">
                    <input type="checkbox" checked={isSel} onChange={() => toggle(d.id)} data-testid={`stats-select-${d.id}`} />
                  </td>
                  <td className="px-3 py-1.5 font-bold">
                    {d.name} <span className="text-neutral-500">#{d.number || "-"}</span>
                    {d.ingame_name && <div className="text-[10px] text-neutral-500 font-normal italic">„{d.ingame_name}"</div>}
                  </td>
                  <td className="px-3 py-1.5" style={{ color: tm.color }}>{tm.name || "—"}</td>
                  <td className="px-3 py-1.5 text-[11px]">
                    {(() => {
                      const arch = d.cards_archive || [];
                      if (!arch.length) return <span className="text-neutral-600">—</span>;
                      const last = arch[arch.length - 1];
                      const date = last.captured_at ? new Date(last.captured_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
                      return (
                        <span className="text-neutral-300" title={`Archiviert am ${date}`}>
                          {last.season_name || <em className="text-neutral-500">ohne Saison</em>}
                          {date && <span className="text-neutral-500 ml-1.5">· {date}</span>}
                        </span>
                      );
                    })()}
                  </td>
                  {STAT_KEYS.map(k => (
                    <td key={k} className="px-1 py-1 text-center">
                      <input type="number" value={valFor(d, k)} onChange={e => editVal(d.id, k, e.target.value)}
                        className="w-12 bg-[#0B0F19] border border-white/10 px-1.5 py-1 text-center text-xs" data-testid={`stats-${k}-${d.id}`} />
                    </td>
                  ))}
                  <td className="px-1 py-1 text-center">
                    <input type="number" value={valFor(d, "ovr")} onChange={e => editVal(d.id, "ovr", e.target.value)}
                      className="w-14 bg-[#0B0F19] border border-white/10 px-1.5 py-1 text-center text-xs font-bold" data-testid={`stats-ovr-${d.id}`} />
                  </td>
                  <td className="px-1 py-1 text-center">
                    <input type="number" value={valFor(d, "market_value")} onChange={e => editVal(d.id, "market_value", e.target.value)}
                      className="w-28 bg-[#0B0F19] border border-white/10 px-1.5 py-1 text-center text-xs" data-testid={`stats-mv-${d.id}`} placeholder="2500000" />
                  </td>
                </tr>
              );
            })}
            {visibleDrivers.length === 0 && (
              <tr><td colSpan={4 + STAT_KEYS.length + 2} className="px-4 py-6 text-center text-neutral-500">Keine Fahrer in diesem Split.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-neutral-500 mt-3">
        Beim Klick auf <strong>Übernehmen</strong> wird für jeden ausgewählten Fahrer die <em>aktuelle</em> Karte (Vorderseite) als PNG nach Cloudinary in den Ordner <code>driver-cards/</code> hochgeladen und im Backend mit Saison-Info verlinkt. Anschließend werden die neuen Werte gespeichert; auf der Fahrerkarte erscheinen Δ-Pfeile, die die Veränderung zur letzten Karte anzeigen.
      </p>

      {/* unsichtbarer Container für html2canvas-Rendering — muss sichtbares Layout haben! */}
      <div ref={captureContainerRef} style={{ position: "absolute", left: 0, top: 0, width: 288, height: 420, overflow: "hidden", opacity: 0, pointerEvents: "none", zIndex: -1 }} aria-hidden />

      {/* CSV Vorschau-Modal */}
      {csvPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="csv-preview-modal">
          <div className="bg-[#0B0F19] border border-[#00E5FF]/40 max-w-6xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0B0F19] border-b border-white/10 p-4 flex items-center justify-between z-10">
              <div>
                <div className="font-display font-black italic uppercase text-lg text-[#A5F3FC]">CSV-Vorschau · {csvPreview.rows.length} Zeilen</div>
                <div className="text-xs text-neutral-400">
                  <span className="text-emerald-400">Grün</span> = automatisch zugeordnet ·
                  <span className="text-amber-400 ml-1">Amber</span> = mehrere Treffer, bitte manuell wählen ·
                  <span className="text-red-400 ml-1">Rot</span> = nicht zugeordnet (Dropdown nutzen oder Zeile löschen)
                </div>
              </div>
              <button onClick={() => setCsvPreview(null)} className="btn-ghost text-xs" data-testid="csv-preview-cancel"><X className="w-4 h-4" /> Abbrechen</button>
            </div>
            <div className="sticky top-[76px] bg-[#0B0F19] border-b border-white/10 px-4 py-2 z-10 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Fahrer-Suche</span>
              <input
                type="text"
                placeholder="Name, Ingame-Name oder Nummer — leer = alle"
                value={driverSearchQuery}
                onChange={e => setDriverSearchQuery(e.target.value)}
                className="flex-1 bg-[#0B0F19] border border-white/15 focus:border-[#00E5FF]/60 outline-none px-2.5 py-1.5 text-xs text-white placeholder:text-neutral-600"
                data-testid="csv-driver-search"
              />
              {driverSearchQuery && (
                <button onClick={() => setDriverSearchQuery("")} className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white" data-testid="csv-driver-search-clear">
                  Zurücksetzen
                </button>
              )}
              <span className="text-[10px] text-neutral-500">
                {filteredSelectableDrivers.length} / {allSelectableDrivers.length} Fahrer
              </span>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-2 py-2">CSV-Name</th>
                    <th className="text-left px-2 py-2 min-w-[200px]">Zuordnen zu</th>
                    {CSV_NUMERIC_KEYS.map(k => <th key={k} className="px-1 py-2 text-center">{k === "market_value" ? "€ MV" : k.toUpperCase()}</th>)}
                    <th className="px-1 py-2 text-center">·</th>
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.rows.map((r) => {
                    const statusColor = !r.driver_id ? (r.ambiguous ? "border-amber-500/40 bg-amber-500/5" : "border-red-500/40 bg-red-500/5") : "border-emerald-500/30 bg-emerald-500/5";
                    return (
                      <tr key={r.idx} className={`border-t border-white/5 ${statusColor}`} data-testid={`csv-row-${r.idx}`}>
                        <td className="px-2 py-1.5 font-bold">{r.src.name || <em className="text-neutral-500">leer</em>}</td>
                        <td className="px-2 py-1.5">
                          <select
                            value={r.driver_id || ""}
                            onChange={e => updateCsvRow(r.idx, { driver_id: e.target.value || null, ambiguous: false })}
                            className="w-full bg-[#0B0F19] border border-white/10 px-2 py-1 text-xs"
                            data-testid={`csv-driver-select-${r.idx}`}
                          >
                            <option value="">— nicht zuordnen —</option>
                            {/* Aktuell zugeordneter Fahrer immer sichtbar halten, auch wenn Suche greift */}
                            {r.driver_id && !filteredSelectableDrivers.some(d => d.id === r.driver_id) && (() => {
                              const cur = allSelectableDrivers.find(d => d.id === r.driver_id);
                              if (!cur) return null;
                              return (
                                <option value={cur.id}>
                                  ★ {cur.name}
                                  {cur.ingame_name ? ` · "${cur.ingame_name}"` : ""}
                                  {" "}(#{cur.number || "-"} · {(teamById[cur.team_id]||{}).name || "-"})
                                </option>
                              );
                            })()}
                            {driversByTeam.map(([teamLabel, list]) => (
                              <optgroup key={teamLabel} label={teamLabel}>
                                {list.map(d => (
                                  <option key={d.id} value={d.id}>
                                    {d.name}
                                    {d.ingame_name ? ` · "${d.ingame_name}"` : ""}
                                    {" "}(#{d.number || "-"}
                                    {d.split ? ` · ${d.split === "elite" ? "Elite" : d.split === "champions" ? "Champions" : d.split}` : ""}
                                    {d.is_reserve_driver ? " · Reserve" : ""}
                                    {d.is_active === false ? " · Inaktiv" : ""})
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </td>
                        {CSV_NUMERIC_KEYS.map(k => (
                          <td key={k} className="px-1 py-1 text-center text-neutral-300">{r.src[k] || <span className="text-neutral-600">—</span>}</td>
                        ))}
                        <td className="px-1 py-1 text-center">
                          <button onClick={() => removeCsvRow(r.idx)} className="text-red-400 hover:text-red-300" title="Zeile entfernen" data-testid={`csv-row-remove-${r.idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="sticky bottom-0 bg-[#0B0F19] border-t border-white/10 p-4 flex items-center justify-end gap-3">
              <div className="text-xs text-neutral-400 mr-auto">
                {csvPreview.rows.filter(r => r.driver_id).length} / {csvPreview.rows.length} zugeordnet
              </div>
              <button onClick={() => setCsvPreview(null)} className="btn-ghost text-xs" data-testid="csv-preview-discard">Verwerfen</button>
              <button onClick={applyCsvPreview} className="btn-primary chamfer-sm text-xs" data-testid="csv-preview-apply">
                In Tabelle übernehmen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ============= STANDINGS OVERRIDE ADMIN ============= */
const DRIVER_NUM_FIELDS = [
  { k: "points", label: "Punkte", step: "0.5", auto: r => r.points },
  { k: "wins", label: "P1", step: "1", auto: r => r.wins },
  { k: "p2", label: "P2", step: "1", auto: r => r.p2 },
  { k: "p3", label: "P3", step: "1", auto: r => r.p3 },
  { k: "top5", label: "Top 5", step: "1", auto: r => r.top5 },
  { k: "top10", label: "Top 10", step: "1", auto: r => r.top10 },
  { k: "best_pos", label: "Best P", step: "1", auto: r => r.best_pos },
  { k: "best_q", label: "Best Q", step: "1", auto: r => r.best_q },
  { k: "poles", label: "PP", step: "1", auto: r => r.poles },
  { k: "fastest_laps", label: "FL", step: "1", auto: r => r.fastest_laps },
  { k: "dotd", label: "DOTD", step: "1", auto: r => r.dotd },
  { k: "penalty_points", label: "STR", step: "1", auto: r => r.penalty_points },
  { k: "dnfs", label: "DNF", step: "1", auto: r => r.dnfs },
  { k: "races", label: "Rennen", step: "1", auto: r => r.races },
];

/* ============= WERTUNGS-OVERRIDE ============= */
// Top-Level Komponenten, damit der Input-Focus nicht bei jedem Eltern-Render verloren geht.
const OverrideNumCell = React.memo(function OverrideNumCell({ value, autoVal, step, onChange, testId }) {
  const has = value !== undefined && value !== null && value !== "";
  return (
    <td className="px-1 py-1 text-center">
      <input
        type="number"
        step={step}
        value={value ?? ""}
        placeholder={autoVal ?? "—"}
        onChange={onChange}
        className={`w-14 bg-[#0B0F19] border px-1 py-0.5 text-center text-[11px] ${has ? "border-amber-500/60 text-amber-300" : "border-white/10 text-neutral-400"}`}
        data-testid={testId}
        title={`Auto: ${autoVal ?? "—"}`}
      />
    </td>
  );
});

const OverrideRaceCell = React.memo(function OverrideRaceCell({ value, autoVal, label, onChange, testId }) {
  const has = value !== undefined && value !== null && value !== "";
  return (
    <td className="px-1 py-1 text-center">
      <input
        type="number"
        step="0.5"
        value={value ?? ""}
        placeholder={autoVal > 0 ? String(autoVal) : "·"}
        onChange={onChange}
        className={`w-12 bg-[#0B0F19] border px-1 py-0.5 text-center text-[11px] ${has ? "border-amber-500/60 text-amber-300" : "border-white/10 text-neutral-500"}`}
        data-testid={testId}
        title={`${label} · Auto: ${autoVal || 0}`}
      />
    </td>
  );
});

function StandingsOverrideAdmin() {
  const splits = useSplits();
  const [splitKey, setSplitKey] = useState(splits[0]?.key || "champions");
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [driverStandings, setDriverStandings] = useState([]);
  const [teamStandings, setTeamStandings] = useState([]);
  const [overrides, setOverrides] = useState({}); // "<scope>:<id>" -> { points?, wins?, ..., race_points?: {eid: val} }
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSplitKey(s => s || splits[0]?.key || "champions"); }, [splits.length]);

  useEffect(() => {
    if (!splitKey) return;
    api.get("/seasons", { params: { split: splitKey } }).then(({ data }) => {
      const list = data || [];
      setSeasons(list);
      // Wenn aktuelle seasonId nicht (mehr) zu diesem Split gehört → neu wählen
      const stillValid = seasonId && list.some(s => s.id === seasonId);
      if (!stillValid && list.length) {
        const cur = list.find(s => s.is_current);
        const fallback = [...list].sort((a, b) => (b.year || 0) - (a.year || 0))[0];
        setSeasonId((cur || fallback).id);
      } else if (!list.length) {
        setSeasonId("");
      }
    }).catch(() => {});
  }, [splitKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    if (!splitKey || !seasonId) return;
    try {
      const [drv, con, ovs] = await Promise.all([
        api.get("/standings/drivers", { params: { split: splitKey, season_id: seasonId } }),
        api.get("/standings/constructors", { params: { split: splitKey, season_id: seasonId, include_all: true } }),
        api.get("/standings/overrides", { params: { split: splitKey, season_id: seasonId } }),
      ]);
      setDriverStandings(drv.data || []);
      setTeamStandings(con.data || []);
      const ovMap = {};
      (ovs.data || []).forEach(o => { ovMap[`${o.scope}:${o.entity_id}`] = (o.fields || (o.points != null ? { points: o.points } : {})); });
      setOverrides(ovMap);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    }
  };
  useEffect(() => { load(); }, [splitKey, seasonId]);

  const setField = (scope, id, key, val) => {
    setOverrides(prev => {
      const k = `${scope}:${id}`;
      const cur = { ...(prev[k] || {}) };
      if (val === "" || val === null || val === undefined) {
        delete cur[key];
      } else {
        cur[key] = val;
      }
      return { ...prev, [k]: cur };
    });
  };
  const setRacePoint = (scope, id, eventId, val) => {
    setOverrides(prev => {
      const k = `${scope}:${id}`;
      const cur = { ...(prev[k] || {}) };
      const rp = { ...(cur.race_points || {}) };
      if (val === "" || val === null || val === undefined) {
        delete rp[eventId];
      } else {
        rp[eventId] = val;
      }
      if (Object.keys(rp).length) cur.race_points = rp; else delete cur.race_points;
      return { ...prev, [k]: cur };
    });
  };
  const clearRow = (scope, id) => {
    setOverrides(prev => ({ ...prev, [`${scope}:${id}`]: {} }));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(overrides).map(([k, fields]) => {
        const [scope, entity_id] = k.split(":");
        const cleaned = {};
        for (const [fk, fv] of Object.entries(fields || {})) {
          if (fk === "race_points") {
            const rp = {};
            for (const [eid, val] of Object.entries(fv || {})) {
              if (val !== "" && val !== null && val !== undefined) {
                const n = parseFloat(val);
                if (Number.isFinite(n)) rp[eid] = n;
              }
            }
            if (Object.keys(rp).length) cleaned.race_points = rp;
          } else if (fv !== "" && fv !== null && fv !== undefined) {
            const n = parseFloat(fv);
            if (Number.isFinite(n)) cleaned[fk] = n;
          }
        }
        return { season_id: seasonId, split: splitKey, scope, entity_id, fields: cleaned };
      });
      await api.post("/standings/overrides", { overrides: payload });
      toast.success("Wertungs-Overrides gespeichert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  // Stabile Inline-Render-Helper (verwenden Top-Level OverrideNumCell/OverrideRaceCell)
  const renderRaceCells = (scope, id, rps) => {
    const ov = overrides[`${scope}:${id}`] || {};
    const rpOv = ov.race_points || {};
    return (rps || []).map(rc => (
      <OverrideRaceCell
        key={rc.event_id}
        value={rpOv[rc.event_id]}
        autoVal={rc.points}
        label={rc.track || rc.short_code}
        onChange={e => setRacePoint(scope, id, rc.event_id, e.target.value)}
        testId={`override-${scope}-${id}-r-${rc.event_id}`}
      />
    ));
  };
  const renderNumCell = (scope, id, fkey, autoVal, step = "1") => {
    const ov = overrides[`${scope}:${id}`] || {};
    return (
      <OverrideNumCell
        key={fkey}
        value={ov[fkey]}
        autoVal={autoVal}
        step={step}
        onChange={e => setField(scope, id, fkey, e.target.value)}
        testId={`override-${scope}-${id}-${fkey}`}
      />
    );
  };

  const raceCols = driverStandings[0]?.race_points || teamStandings[0]?.race_points || [];

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <div className="eyebrow">Split</div>
          <select value={splitKey} onChange={e => setSplitKey(e.target.value)} className={TXT + " w-48"} data-testid="override-split">
            {splits.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <div className="eyebrow">Saison</div>
          <select value={seasonId} onChange={e => setSeasonId(e.target.value)} className={TXT + " w-64"} data-testid="override-season">
            {seasons.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{s.year ? ` · ${s.year}` : ""}{s.game ? ` · ${s.game}` : ""}{s.is_current ? " · aktuell" : ""}
              </option>
            ))}
          </select>
        </div>
        <button onClick={submit} disabled={saving} className="btn-primary chamfer-sm text-xs ml-auto disabled:opacity-40" data-testid="override-save">
          {saving ? "Speichern …" : "Overrides speichern"}
        </button>
      </div>
      <p className="text-[11px] text-neutral-500 mb-3">
        Leeres Feld = Auto-Wert aus den Rennergebnissen. Amber = manueller Override aktiv. Pos/INT/GAP werden automatisch aus den Punkten neu berechnet.
      </p>

      {/* FAHRER */}
      <h3 className="font-display font-black italic uppercase text-sm tracking-widest text-[#A5F3FC] mb-2">Fahrer</h3>
      <div className="border border-white/10 overflow-x-auto mb-8">
        <table className="text-[11px] min-w-max" data-testid="override-drivers-table">
          <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
            <tr>
              <th className="text-left px-2 py-2 sticky left-0 bg-[#11151E] z-10 min-w-[140px]">Fahrer</th>
              <th className="text-left px-2 py-2 min-w-[110px]">Team</th>
              {raceCols.map(rc => <th key={rc.event_id} className="px-1 py-2 text-center w-12" title={rc.track}>{rc.short_code || rc.country_code}</th>)}
              {DRIVER_NUM_FIELDS.map(f => <th key={f.k} className="px-1 py-2 text-center w-14">{f.label}</th>)}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {driverStandings.map(r => (
              <tr key={r.driver_id} className="border-t border-white/5" data-testid={`override-row-driver-${r.driver_id}`}>
                <td className="px-2 py-1 sticky left-0 bg-[#0B0F19] font-bold whitespace-nowrap">{r.driver_name} <span className="text-neutral-500 text-[10px]">#{r.number || "-"}</span></td>
                <td className="px-2 py-1 whitespace-nowrap" style={{ color: r.team_color }}>{r.team_name}</td>
                {renderRaceCells("driver", r.driver_id, r.race_points)}
                {DRIVER_NUM_FIELDS.map(f => renderNumCell("driver", r.driver_id, f.k, f.auto(r), f.step))}
                <td className="px-2 py-1">
                  {Object.keys(overrides[`driver:${r.driver_id}`] || {}).length > 0 && (
                    <button onClick={() => clearRow("driver", r.driver_id)} className="text-red-400 text-[10px] uppercase" data-testid={`override-clear-driver-${r.driver_id}`}>X</button>
                  )}
                </td>
              </tr>
            ))}
            {!driverStandings.length && <tr><td colSpan={3 + raceCols.length + DRIVER_NUM_FIELDS.length} className="px-4 py-6 text-center text-neutral-500">Keine Fahrer.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* KONSTRUKTEURE */}
      <h3 className="font-display font-black italic uppercase text-sm tracking-widest text-[#A5F3FC] mb-2">Konstrukteure</h3>
      <div className="border border-white/10 overflow-x-auto">
        <table className="text-[11px] min-w-max" data-testid="override-teams-table">
          <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
            <tr>
              <th className="text-left px-2 py-2 sticky left-0 bg-[#11151E] z-10 min-w-[140px]">Team</th>
              {raceCols.map(rc => <th key={rc.event_id} className="px-1 py-2 text-center w-12" title={rc.track}>{rc.short_code || rc.country_code}</th>)}
              {DRIVER_NUM_FIELDS.map(f => <th key={f.k} className="px-1 py-2 text-center w-14">{f.label}</th>)}
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {teamStandings.map(r => (
              <tr key={r.team_id} className="border-t border-white/5" data-testid={`override-row-team-${r.team_id}`}>
                <td className="px-2 py-1 sticky left-0 bg-[#0B0F19] font-bold whitespace-nowrap" style={{ color: r.color }}>{r.team_name} <span className="text-neutral-500 text-[10px]">{r.short_name}</span></td>
                {renderRaceCells("team", r.team_id, r.race_points)}
                {DRIVER_NUM_FIELDS.map(f => renderNumCell("team", r.team_id, f.k, f.auto(r), f.step))}
                <td className="px-2 py-1">
                  {Object.keys(overrides[`team:${r.team_id}`] || {}).length > 0 && (
                    <button onClick={() => clearRow("team", r.team_id)} className="text-red-400 text-[10px] uppercase" data-testid={`override-clear-team-${r.team_id}`}>X</button>
                  )}
                </td>
              </tr>
            ))}
            {!teamStandings.length && <tr><td colSpan={2 + raceCols.length + DRIVER_NUM_FIELDS.length} className="px-4 py-6 text-center text-neutral-500">Keine Teams.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ============= SAISONS ABSCHLIEßEN ============= */
function SeasonLockAdmin() {
  const [seasons, setSeasons] = useState([]);
  const [busy, setBusy] = useState(null);
  const splits = useSplits();
  const splitName = (k) => splits.find(s => s.key === k)?.name || k;

  const load = () => api.get("/seasons").then(({ data }) => {
    const sorted = [...(data || [])].sort((a, b) => {
      if ((a.split || "") !== (b.split || "")) return (a.split || "").localeCompare(b.split || "");
      return (b.year || 0) - (a.year || 0);
    });
    setSeasons(sorted);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const onClose = async (s) => {
    if (!window.confirm(
      `Saison „${s.name}${s.year ? ` · ${s.year}` : ""}${s.game ? ` · ${s.game}` : ""}" abschließen?\n\n` +
      `Dabei wird das AKTUELLE Team jedes Fahrers in jedem Rennen dieser Saison fest geschrieben. ` +
      `Spätere Team-Wechsel ändern die Anzeige in dieser Saison NICHT mehr.\n\n` +
      `Reservefahrer-Zuordnungen bleiben unverändert.`
    )) return;
    setBusy(s.id);
    try {
      const { data } = await api.post(`/seasons/${s.id}/close`);
      toast.success(`Saison eingefroren · ${data.snapped} Einträge aktualisiert, ${data.skipped} übersprungen`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Schließen fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  const onReopen = async (s) => {
    if (!window.confirm(
      `Saison „${s.name}" wieder öffnen?\n\n` +
      `Das eingefrorene Team wird aus den Rennergebnissen entfernt — Standings & Rennergebnisse zeigen wieder das aktuelle Team jedes Fahrers.\n\n` +
      `(Reservefahrer-Zuordnungen bleiben erhalten.)`
    )) return;
    setBusy(s.id);
    try {
      const { data } = await api.post(`/seasons/${s.id}/reopen`);
      toast.success(`Saison wieder geöffnet · ${data.cleared} Einträge zurückgesetzt`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Öffnen fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div data-testid="season-lock-admin">
      <div className="border border-amber-500/30 bg-amber-500/5 p-4 mb-6 text-[12px] leading-relaxed">
        <div className="font-display font-black italic uppercase text-amber-300 mb-1">Saison einfrieren</div>
        <p className="text-amber-100/80">
          Wenn ein Fahrer am Saisonende das Team wechselt, würden Standings und Rennergebnisse
          alter Saisons sonst rückwirkend das neue Team anzeigen. Durch das Abschließen wird die
          Team-Zuordnung jedes Fahrers in dieser Saison fest geschrieben.
        </p>
        <p className="text-amber-100/60 mt-1">
          Die <span className="font-bold">Ewige Tabelle</span> zeigt unabhängig davon immer das aktuelle Team eines Fahrers.
        </p>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="season-lock-table">
          <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Saison</th>
              <th className="text-left px-3 py-2">Split</th>
              <th className="text-left px-3 py-2">Jahr · Game</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Geschlossen am</th>
              <th className="text-right px-3 py-2 w-56">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {seasons.map(s => (
              <tr key={s.id} className="border-t border-white/5" data-testid={`season-lock-row-${s.id}`}>
                <td className="px-3 py-2 font-bold">
                  {s.name}
                  {s.is_current && <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#00E5FF]/40 text-[#00E5FF]">aktuell</span>}
                </td>
                <td className="px-3 py-2 text-neutral-300">{splitName(s.split)}</td>
                <td className="px-3 py-2 text-neutral-400">{s.year || "—"}{s.game ? ` · ${s.game}` : ""}</td>
                <td className="px-3 py-2">
                  {s.closed ? (
                    <span className="inline-flex items-center gap-1 text-amber-300 font-bold text-[11px] uppercase tracking-widest" data-testid={`season-lock-status-${s.id}`}>
                      <span aria-hidden>🔒</span> Geschlossen
                    </span>
                  ) : (
                    <span className="text-emerald-400 font-bold text-[11px] uppercase tracking-widest" data-testid={`season-lock-status-${s.id}`}>Offen</span>
                  )}
                </td>
                <td className="px-3 py-2 text-neutral-500 text-[11px]">{fmtDate(s.closed_at)}</td>
                <td className="px-3 py-2 text-right">
                  {s.closed ? (
                    <button
                      onClick={() => onReopen(s)}
                      disabled={busy === s.id}
                      className="border border-white/20 hover:border-white/60 px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40"
                      data-testid={`season-reopen-${s.id}`}
                    >
                      {busy === s.id ? "…" : "Wieder öffnen"}
                    </button>
                  ) : (
                    <button
                      onClick={() => onClose(s)}
                      disabled={busy === s.id}
                      className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-3 py-1.5 text-[11px] uppercase tracking-widest chamfer-sm disabled:opacity-40"
                      data-testid={`season-close-${s.id}`}
                    >
                      {busy === s.id ? "…" : "Abschließen"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!seasons.length && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-neutral-500">Keine Saisons.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ============= BACKUP / RESTORE ============= */
function BackupAdmin() {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const fileRef = useRef(null);

  const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/export");
      const data = res.data;
      const filename = `sector-four-backup-${new Date().toISOString().slice(0, 10)}.json`;
      downloadJSON(data, filename);
      toast.success("Backup heruntergeladen");
    } catch (e) {
      toast.error(`Export fehlgeschlagen: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleFilePick = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed?.collections || typeof parsed.collections !== "object") {
        toast.error("Ungültige Backup-Datei (keine 'collections' gefunden)");
        return;
      }
      const summary = Object.entries(parsed.collections).map(([k, v]) => ({
        name: k,
        count: Array.isArray(v) ? v.length : 0,
      }));
      setPendingFile(parsed);
      setPendingPreview({
        exported_at: parsed?._meta?.exported_at,
        db_name: parsed?._meta?.db_name,
        summary,
        file_name: file.name,
      });
      setConfirmText("");
    } catch (e) {
      toast.error(`Datei konnte nicht gelesen werden: ${e.message}`);
    }
  };

  const handleImport = async () => {
    if (!pendingFile) return;
    if (confirmText !== "IMPORT") {
      toast.error("Bitte 'IMPORT' eingeben, um zu bestätigen.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/admin/import", { ...pendingFile, mode: "replace" });
      setReport(res.data);
      toast.success("Import erfolgreich");
      setPendingFile(null);
      setPendingPreview(null);
      setConfirmText("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(`Import fehlgeschlagen: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-backup">
      <div>
        <h2 className="text-2xl font-display font-black italic uppercase tracking-tight">Backup &amp; Restore</h2>
        <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
          Exportiere die komplette Datenbank (Teams, Fahrer, Saisons, Rennen, News, Roadmap, Settings …) als JSON-Datei und spiele sie in einer anderen Umgebung wieder ein. Ideal, um Produktions-Daten in die Vorschau zu spiegeln.
          Admin-User werden bewusst <strong>nicht</strong> mitexportiert, damit Logins pro Umgebung unabhängig bleiben.
        </p>
      </div>

      {/* EXPORT */}
      <div className="border border-white/10 p-5 bg-white/[0.02]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow text-[#00E5FF]">Export</div>
            <div className="text-base font-bold mt-1">Datenbank als JSON herunterladen</div>
            <div className="text-xs text-neutral-500 mt-1">Erzeugt eine vollständige Snapshot-Datei zum lokalen Speichern oder zum Import in einer anderen Umgebung.</div>
          </div>
          <button
            onClick={handleExport}
            disabled={busy}
            className="px-5 py-2 bg-[#00E5FF] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#00E5FF]/80 disabled:opacity-50"
            data-testid="backup-export-btn"
          >
            {busy ? "Lädt …" : "Backup herunterladen"}
          </button>
        </div>
      </div>

      {/* IMPORT */}
      <div className="border border-amber-400/30 p-5 bg-amber-500/[0.04]">
        <div className="eyebrow text-amber-300">Restore (Import)</div>
        <div className="text-base font-bold mt-1">Backup-Datei einspielen</div>
        <div className="text-xs text-neutral-400 mt-1 max-w-2xl">
          <strong className="text-amber-300">Achtung:</strong> Beim Import werden die betroffenen Collections in dieser Datenbank <strong>komplett ersetzt</strong>. Bestehende Inhalte (in dieser Umgebung) gehen verloren. Lege vorher idealerweise ein Backup dieser Umgebung an.
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={(e) => handleFilePick(e.target.files?.[0])}
            className="text-xs file:mr-3 file:px-4 file:py-2 file:border-0 file:bg-white/10 file:text-white file:uppercase file:font-bold file:tracking-widest file:text-[10px] file:cursor-pointer"
            data-testid="backup-file-input"
          />
        </div>

        {pendingPreview && (
          <div className="mt-5 border border-white/10 bg-black/30 p-4" data-testid="backup-preview">
            <div className="text-xs text-neutral-400">
              Datei: <span className="text-white font-mono">{pendingPreview.file_name}</span>
            </div>
            {pendingPreview.exported_at && (
              <div className="text-xs text-neutral-400 mt-0.5">
                Exportiert am: <span className="text-white">{pendingPreview.exported_at}</span>
                {pendingPreview.db_name && <span className="text-neutral-500"> · DB: {pendingPreview.db_name}</span>}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {pendingPreview.summary.map((c) => (
                <div key={c.name} className="flex items-center justify-between border border-white/5 px-2 py-1">
                  <span className="text-neutral-400">{c.name}</span>
                  <span className="font-bold text-white">{c.count}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 border-t border-white/10 pt-4">
              <label className="text-xs uppercase tracking-widest text-amber-300">
                Tippe <span className="font-mono text-white">IMPORT</span> zum Bestätigen
              </label>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className={`${TXT} mt-2 max-w-[200px]`}
                placeholder="IMPORT"
                data-testid="backup-confirm-input"
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={handleImport}
                  disabled={busy || confirmText !== "IMPORT"}
                  className="px-5 py-2 bg-amber-400 text-black font-bold uppercase tracking-widest text-xs hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="backup-import-btn"
                >
                  {busy ? "Importiere …" : "Jetzt importieren (überschreibt)"}
                </button>
                <button
                  onClick={() => { setPendingFile(null); setPendingPreview(null); setConfirmText(""); if (fileRef.current) fileRef.current.value = ""; }}
                  disabled={busy}
                  className="px-4 py-2 border border-white/20 text-neutral-300 hover:text-white text-xs uppercase tracking-widest"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {report && (
          <div className="mt-5 border border-emerald-400/30 bg-emerald-500/[0.06] p-4 text-xs" data-testid="backup-report">
            <div className="text-emerald-300 uppercase tracking-widest font-bold">Import abgeschlossen</div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(report.applied || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border border-white/5 px-2 py-1">
                  <span className="text-neutral-400">{k}</span>
                  <span className="font-bold text-white">{v}</span>
                </div>
              ))}
            </div>
            {report.skipped?.length > 0 && (
              <div className="text-amber-300 mt-2">Übersprungen: {report.skipped.join(", ")}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
